import React, { useState, useEffect, useRef } from 'react';
import { View, Text, ImageBackground, TextInput, TouchableOpacity, ActivityIndicator, ScrollView, Picker, Image, Animated, Platform, StyleSheet, Modal, Switch } from 'react-native';
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  deleteDoc,
  getDoc,
  setDoc,
  onSnapshot,
  limit,
  serverTimestamp,
  merge,
  arrayUnion,
  writeBatch,
  orderBy,
  getCountFromServer,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import Papa from 'papaparse';
import { Ionicons } from '@expo/vector-icons';
import { globalStyles } from '../styles/globalStyles';
import { BREAKPOINTS } from '../styles/breakpoints';
import { colors } from '../styles/theme';
import { useAdminTheme } from '../hooks/useAdminTheme';
import { AdminThemeProvider } from '../contexts/AdminThemeContext';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';

import { findLiveNationVenuePhoto } from '../api/venuePhotoFetcher';
import { migrateUserIds } from '../scripts/migrateUserIds';
import { useUser } from '../components/LoginPersistence';
import Constants from 'expo-constants';
import { generateVenueData } from '../scripts/generateVenueData';
import venueData from '../../assets/venues/venueData.json';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { extraGradeDisplayFields } from '../utils/extraGradeLines';
import { csvRejectReason, mapVenueCsvHeader } from '../utils/csvFileAccept';
import { getResourceCategoryById, getResourceCategoryLabel, RESOURCE_CATEGORIES, getResourceCategoryIconSize } from '../constants/resourceCategories';
import {
  fetchResourceCategoryCatalog,
  MERCH_RESOURCE_CATEGORY,
  saveResourceCategory,
} from '../utils/resourceCategoryCatalog';
import { creditUserFundEarn, listAllMutualFunds, upsertMutualFund, setMutualFundActive } from '../utils/userFundsService';
import { useCreditEconomy } from '../contexts/CreditEconomyContext';
import { formatFundsCents } from '../constants/userFunds';
import {
  LIVE_COMMUNITY_HIDDEN_STATUS,
  LIVE_COMMUNITY_SOURCE,
  LIVE_TECH_SPEC_FIELDS,
  isLiveCommunityTechSpecReport,
  matchesVenueReportKind,
} from '../utils/liveTechSpecs';
import * as DocumentPicker from 'expo-document-picker';
// Import db directly from firebase config
import { db } from '../config/firebase';
import { useNavigation } from '@react-navigation/native';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { getDeployTarget, DEV_MODE } from '../config/environment';

// Import components
import { AdminSidebar, ADMIN_SIDEBAR_WIDTH, ADMIN_SIDEBAR_COLLAPSED_WIDTH } from '../components/admin/AdminSidebar';
import { AdminPageShell } from '../components/admin/AdminPageShell';
import { AdminDashboard } from '../components/admin/AdminDashboard';
import { AdminFeatureFlags } from '../components/admin/AdminFeatureFlags';
import { AdminMobileFeatureFlags } from '../components/admin/AdminMobileFeatureFlags';
import AdminCreditEconomy from '../components/admin/AdminCreditEconomy';
import { AdminSectionHeader } from '../components/admin/AdminSectionHeader';
import { useVenues } from '../components/VenueContext';
import { UniqueVenueCard } from '../components/UniqueVenueCard';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { useAnimation } from '../hooks/useAnimation';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { NotificationPopup } from '../components/NotificationPopup';
import { getMockVenueData, createVenueCSV } from '../api/venueScraper';
import { Footer } from '../components/Footer';
import { CommunityDirectoryManagement } from '../components/CommunityDirectoryManagement';
import { scrapeVenueData } from '../api/venueScraper';
import { processVenueData } from '../api/venueScraper';
import { NewVenues } from '../components/admin/NewVenues';
import { AdminUsersList } from '../components/admin/AdminUsersList';
import { canArchiveEmailExtras, pickKeeperAndExtras, usersSharingEmail } from '../utils/duplicateUserAccounts';
import { setWebPlaceholderColor, getAdminSearchPlaceholderColor } from '../utils/setWebPlaceholderColor';
import { AdminPoiCoverageMap } from '../components/admin/AdminPoiCoverageMap';
import { AdminLocationCategories } from '../components/admin/AdminLocationCategories';
import { AdminInterests } from '../components/admin/AdminInterests';
import { AdminFeatureNotify } from '../components/admin/AdminFeatureNotify';
import { AdminChangeLogs } from '../components/admin/AdminChangeLogs';
import AdminMutualFunds from '../components/AdminMutualFunds';
import {
  USA_POI_TILES,
  USA_POI_TILE_SET_ID,
  USA_POI_TILE_COUNT,
  isUsaV1CoverageDoc,
} from '../data/usaPoiTiles';
import { findRelevantManuals } from '../utils/findRelevantManuals';

// Import map embed utilities
import { isValidMapEmbed, normalizeMapEmbed, attachEmbedCoordinatesToVenuePayload, resolveEmbedCoordinatesForSave } from '../utils/mapEmbedUtils';

// Import VenuesList component
import VenuesList from '../components/admin/VenuesList';

// Import VenueCard component
import VenueCard from '../components/admin/VenueCard';

// Add to the imports at the top
import { useSystemStyles } from '../hooks/useSystemStyles';
import StyleManagement from '../components/admin/StyleManagement';
import { AdminProvider } from '../contexts/AdminContext';

const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:3001/api'
  : 'https://'; // Replace with your production API URL

const getLetterGrade = (grade) => {
  if (!grade) return 'N/A';
  
  // If it's already a letter grade
  if (typeof grade === 'string') {
    const validGrade = grade.toUpperCase();
    if (['A', 'B', 'C', 'D', 'F'].includes(validGrade)) {
      return validGrade;
    }
  }
  
  // If it's a numeric grade
  if (typeof grade === 'number' || !isNaN(Number(grade))) {
    const numericGrade = Number(grade);
    if (numericGrade >= 4.5) return 'A';
    if (numericGrade >= 3.5) return 'B';
    if (numericGrade >= 2.5) return 'C';
    if (numericGrade >= 1.5) return 'D';
    if (numericGrade >= 1) return 'F';
  }
  
  return 'N/A';
};

const readImageDimensions = (file) => new Promise((resolve) => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    resolve({ width: null, height: null });
    return;
  }
  const uri = file?.uri || (file instanceof Blob && typeof URL !== 'undefined' ? URL.createObjectURL(file) : null);
  if (!uri) {
    resolve({ width: null, height: null });
    return;
  }
  const img = new window.Image();
  img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
  img.onerror = () => resolve({ width: null, height: null });
  img.src = uri;
});

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'approved':
      return '#2ecc71';
    case 'live':
      return '#4e7de0';
    case 'hidden':
      return '#95a5a6';
    case 'rejected':
      return '#e74c3c';
    case 'pending':
    default:
      return '#f1c40f';
  }
};

// Add new section to reportOptions array
const reportOptions = [
  {
    title: 'User Reports',
    value: 'user'
  },
  {
    title: 'Venue Reports',
    value: 'venue'
  },
  {
    title: 'New Venues',
    value: 'venue_submission'
  },
  {
    title: 'Resource Submissions',
    value: 'resource_submission'
  },
  {
    title: 'Venue Document Uploads',
    value: 'venue_document_submission'
  },
  {
    title: 'Venue Archive Requests',
    value: 'venue_archive_request'
  },
  {
    title: 'Site Management',
    value: 'site_management'
  },
  {
    title: 'Email Testing',
    value: 'email_testing'
  }
];

// Add this near the top with other utility functions
const calculateAverageGrade = (ratings) => {
  const gradePoints = {
    'A': 5,
    'B': 4,
    'C': 3,
    'D': 2,
    'F': 1
  };

  const validRatings = Object.values(ratings).filter(rating => {
    if (typeof rating === 'string' && ['A', 'B', 'C', 'D', 'F'].includes(rating.toUpperCase())) {
      return true;
    }
    if (typeof rating === 'number' || !isNaN(Number(rating))) {
      const num = Number(rating);
      return num >= 1 && num <= 5;
    }
    return false;
  });

  if (validRatings.length === 0) return null;

  const sum = validRatings.reduce((acc, rating) => {
    if (typeof rating === 'string') {
      return acc + gradePoints[rating.toUpperCase()];
    }
    return acc + Number(rating);
  }, 0);

  console.log('sum:', sum);

  const average = sum / validRatings.length;
  console.log('average: from sum / validRatings.length', average);

  // Convert numeric average back to letter grade
  if (average >= 4.5) return 'A';
  if (average >= 3.5) return 'B';
  if (average >= 2.5) return 'C';
  if (average >= 1.5) return 'D';
  return 'F';
};

// Add this after the existing USER_TYPES constant
const USER_TYPE_COLORS = {
  member: '#0bb5df',        // Standard verification blue
  venue_employee: '#2ecc71', // Green
  venue_owner: colors.purpleReplacement, // Updated to use purpleReplacement
  artist: '#e67e22',        // Orange
  touring_crew: '#3498db',   // Blue
  music_industry: '#1abc9c', // Turquoise
  admin: '#e74c3c',         // Red
  user: '#95a5a6'           // Gray
};

// Add after the API_BASE_URL constant and before getLetterGrade function

const USER_TYPES = {
  ADMIN: {
    label: 'Admin',
    value: 'admin',
    color: 'pink'
  },
  MEMBER: {
    label: 'Member',
    value: 'member',
    color: '#0bb5df'
  },
  EMPLOYEE: {
    label: 'Employee',
    value: 'employee',
    color: '#2ecc71' // Green
  },
  VENUE_OWNER: {
    label: 'Venue Owner',
    value: 'venue_owner',
    color: colors.purpleReplacement // Updated to use purpleReplacement
  },
  ARTIST: {
    label: 'Artist',
    value: 'artist',
    color: '#e67e22' // Orange
  },
  TOURING: {  // Changed from BAND_MEMBER
    label: 'Touring Crew',
    value: 'touring crew',  // Changed from band_member
    color: '#3498db' // Changed to blue
  },
  MUSIC_INDUSTRY: {
    label: 'Music Industry Employee',
    value: 'music industry',
    color: '#1abc9c' // Turquoise
  },
  LIVE_PERFORMER: {
    label: 'Live Performer',
    value: 'live performer',
    color: '#FF6B6B' // Coral Red
  },
  VENUE_EMPLOYEE: {
    label: 'Venue Employee',
    value: 'venue employee',
    color: '#4ECDC4' // Caribbean Green
  },
  BACKLINE_TECH: {
    label: 'Backline Tech',
    value: 'backline tech',
    color: '#45B7D1' // Ocean Blue
  },
  BOOKING_AGENT: {
    label: 'Booking Agent',
    value: 'booking agent',
    color: '#96CEB4' // Sage Green
  },
  PROMOTER: {
    label: 'Promoter',
    value: 'promoter',
    color: '#D4A5A5' // Dusty Rose
  },
  VENUE_MANAGER: {
    label: 'Venue Manager',
    value: 'venue manager',
    color: '#9B59B6' // Amethyst Purple
  },
  TOUR_MANAGER: {
    label: 'Tour Manager',
    value: 'tour manager',
    color: '#F1C40F' // Sunflower Yellow
  },
  LD: {
    label: 'LD',
    value: 'ld',
    color: '#E056FD' // Bright Purple
  },
  FOH: {
    label: 'FOH',
    value: 'foh',
    color: '#20B2AA' // Light Sea Green
  }
};

// Add these utility functions at the top with other utility functions
const calculateGradesByUserType = (reports) => {
  const gradesByType = {
    artist: [],
    employee: [],
    touring_crew: [],
    other: []
  };

  reports.forEach(report => {
    const ratings = [
      report.safetyRating,
      report.equipmentRating,
      report.staffRating,
      report.workingConditionsRating,
      report.breakTimeRating,
      report.overtimeRating,
      report.schedulingRating,
      report.audioStaff,
      report.backstage,
      report.hospitality,
      report.lightingStaff,
      report.audioEquipment,
      report.lightingEquipment,
      report.workplaceCleaniness,
      report.overallSafety,
      report.payComparison,
      report.venueManagement,
      report.venueStaff,
      report.cityWalkability,
      report.musicalEquipmentGrade,
      report.stageSafetyGrade,
      report.venuePromotion,
      report.pay,
      // report.productionManager
    ].filter(rating => rating && ['A', 'B', 'C', 'D', 'F'].includes(rating.toUpperCase()));

    if (ratings.length === 0) return;

    // Convert array to object with numbered keys
    const ratingsObject = ratings.reduce((obj, rating, index) => {
      obj[`rating${index + 1}`] = rating;
      return obj;
    }, {});

    const averageGrade = calculateAverageGrade(ratingsObject);

    switch(report.userType?.toLowerCase()) {
      case 'artist':
        gradesByType.artist.push(averageGrade);
        break;
      case 'employee':
        gradesByType.employee.push(averageGrade);
        break;
      case 'venue_owner':
      case 'music_industry':
        gradesByType.employee.push(averageGrade);
        break;
      case 'touring':
        gradesByType.touring_crew.push(averageGrade);
        break;
      default:
        gradesByType.other.push(averageGrade);
    }
  });

  return {
    artistGrade: gradesByType.artist.length > 0 
      ? calculateAverageGrade(gradesByType.artist.reduce((obj, rating, index) => {
          obj[`rating${index + 1}`] = rating;
          return obj;
        }, {}))
      : null,
    employeeGrade: gradesByType.employee.length > 0 
      ? calculateAverageGrade(gradesByType.employee.reduce((obj, rating, index) => {
          obj[`rating${index + 1}`] = rating;
          return obj;
        }, {}))
      : null,
    crewGrade: gradesByType.touring_crew.length > 0 
      ? calculateAverageGrade(gradesByType.touring_crew.reduce((obj, rating, index) => {
          obj[`rating${index + 1}`] = rating;
          return obj;
        }, {}))
      : null,
    otherGrade: gradesByType.other.length > 0 
      ? calculateAverageGrade(gradesByType.other.reduce((obj, rating, index) => {
          obj[`rating${index + 1}`] = rating;
          return obj;
        }, {}))
      : null
  };
};

const calculateOverallGrade = (grades) => {
  const gradePoints = {
    'A': 5,
    'B': 4,
    'C': 3,
    'D': 2,
    'F': 1
  };
  const validGrades = Object.values(grades).filter(grade => grade !== null);
  if (validGrades.length === 0) return null;
  return validGrades.reduce((acc, grade) => acc + gradePoints[grade], 0) / validGrades.length;
};

const waitMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ADMIN_REPORTS_SEARCH_ID = 'admin-reports-search';
const ADMIN_COMMUNITY_SEARCH_ID = 'admin-community-search';

const REPORT_SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name_asc', label: 'Venue A–Z' },
  { value: 'name_desc', label: 'Venue Z–A' },
  { value: 'submitter_asc', label: 'Submitter A–Z' },
];

const RESOURCE_SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name_asc', label: 'Resource A–Z' },
  { value: 'name_desc', label: 'Resource Z–A' },
  { value: 'submitter_asc', label: 'Submitter A–Z' },
];

function loadReportColumnCount(reportType) {
  if (typeof window === 'undefined') return 2;
  const saved = parseInt(localStorage.getItem(`vrc-admin-reports-columns-${reportType}`), 10);
  return [1, 2, 3].includes(saved) ? saved : 2;
}

function getReportTimestamp(report) {
  const ts = report?.createdAt;
  if (!ts) return 0;
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  const parsed = new Date(ts).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getReportSortName(report, reportType) {
  if (reportType === 'resource_submission') {
    return report.resourceName || report.venueName || '';
  }
  return report.venueName || report.name || '';
}

function getReportSubmitter(report) {
  return report.userEmail || report.userName || '';
}

function sortReportsList(reports, sortBy, reportType) {
  const sorted = [...reports];
  switch (sortBy) {
    case 'oldest':
      sorted.sort((a, b) => getReportTimestamp(a) - getReportTimestamp(b));
      break;
    case 'name_asc':
      sorted.sort((a, b) => getReportSortName(a, reportType).localeCompare(getReportSortName(b, reportType)));
      break;
    case 'name_desc':
      sorted.sort((a, b) => getReportSortName(b, reportType).localeCompare(getReportSortName(a, reportType)));
      break;
    case 'submitter_asc':
      sorted.sort((a, b) => getReportSubmitter(a).localeCompare(getReportSubmitter(b)));
      break;
    case 'newest':
    default:
      sorted.sort((a, b) => getReportTimestamp(b) - getReportTimestamp(a));
      break;
  }
  return sorted;
}

/** Firestore poiSeedCoverage doc means this tile+category finished (ok or cached). */
function isPoiSeedCoverageComplete(snap) {
  if (!snap.exists()) return false;
  const d = snap.data() || {};
  if (d.importStatus === 'failed') return false;
  if (d.importStatus === 'ok' || d.importStatus === 'cached') return true;
  return Number(d.importedAtMs) > 0;
}

export function Admin() {
  const { colors: adminColors, styles: adminStyles, domSelectStyle, isDarkMode: adminIsDarkMode } = useAdminTheme();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [activeUserSubTab, setActiveUserSubTab] = useState('usersList'); // 'usersList' or 'communityDirectory'
  const navigation = useNavigation();
  const [reportType, setReportType] = useState('venue');
  const [reportStatus, setReportStatus] = useState('pending');
  const adminSidebarWidth = Platform.OS === 'web'
    ? ADMIN_SIDEBAR_COLLAPSED_WIDTH
    : (sidebarCollapsed ? ADMIN_SIDEBAR_COLLAPSED_WIDTH : ADMIN_SIDEBAR_WIDTH);

  // POI admin controls (batch import + basic cache stats)
  const POI_TYPE_OPTIONS = [
    { key: 'rv_parking', label: 'RV Parking' },
    { key: 'rest_stop', label: 'Rest Stops' },
    { key: 'hotel', label: 'Hotels' },
    { key: 'airport', label: 'Airports' },
    { key: 'train_station', label: 'Train Stations' },
    { key: 'bus_station', label: 'Local Transit' },
    { key: 'border_checkpoint', label: 'Border Checkpoints' },
    { key: 'gas_station', label: 'Gas Stations' },
    { key: 'clinic', label: 'Walk-in Clinics' },
    { key: 'spa', label: 'Spas' },
    { key: 'massage_parlor', label: 'Massage' },
    { key: 'gym', label: 'Gyms' },
    { key: 'rock_climbing_gym', label: 'Rock Climbing' },
    { key: 'park', label: 'Parks' },
    { key: 'fishing_destination', label: 'Fishing' },
    { key: 'golf_course', label: 'Golf Courses' },
    { key: 'museum', label: 'Museums' },
    { key: 'barbershop', label: 'Barbershops' },
  ];

  const ALL_POI_CATEGORY_KEYS = POI_TYPE_OPTIONS.map((o) => o.key);

  /** USA batch: sweep every category × tile with one gap between each callable (min 5s in this mode). */
  const POI_BATCH_ALL_INCOMPLETE_KEY = '__all_incomplete__';
  const POI_BATCH_CATEGORY_DROPDOWN_OPTIONS = [
    { key: POI_BATCH_ALL_INCOMPLETE_KEY, label: 'All incomplete (all categories × tiles)' },
    ...POI_TYPE_OPTIONS,
  ];

  const [poiSelectedTypeKeys, setPoiSelectedTypeKeys] = useState([]);
  const [poiTypeModeTab, setPoiTypeModeTab] = useState('travel'); // travel | health | parks
  const [poiImporting, setPoiImporting] = useState(false);
  const [poiImportStatusText, setPoiImportStatusText] = useState('');
  const [poiPrelimImporting, setPoiPrelimImporting] = useState(false);
  const [poiPrelimStatusText, setPoiPrelimStatusText] = useState('');
  const [poiBaking, setPoiBaking] = useState(false);
  const [poiBakeDeleting, setPoiBakeDeleting] = useState(false);
  const [poiBakeStatusText, setPoiBakeStatusText] = useState('');
  const [poiBakeMaxPerCategory, setPoiBakeMaxPerCategory] = useState('15000');
  const [poiPrelimMaxRegions, setPoiPrelimMaxRegions] = useState(10);
  /** Single category for USA tile batch import (one Overpass query shape per tile). */
  const [poiBatchCategoryKey, setPoiBatchCategoryKey] = useState('hotel');
  const [poiBatchCategoryDropdownOpen, setPoiBatchCategoryDropdownOpen] = useState(false);
  const [poiBatchSkipCompleted, setPoiBatchSkipCompleted] = useState(true);
  const [poiBatchForceRerun, setPoiBatchForceRerun] = useState(false);
  const [poiBatchSleepMs, setPoiBatchSleepMs] = useState(5000);
  const [poiBatchProgress, setPoiBatchProgress] = useState({ current: 0, total: 0, label: '' });
  const [poiSeedCoverageByCategory, setPoiSeedCoverageByCategory] = useState({});
  /** Raw Firestore rows for map (same collection as aggregate stats). */
  const [poiSeedCoverageDocs, setPoiSeedCoverageDocs] = useState([]);
  const [poiCoverageMapCategory, setPoiCoverageMapCategory] = useState('all');
  const poiBatchAbortRef = useRef(false);
  const [poiCoverageStats, setPoiCoverageStats] = useState({
    totalRegions: 0,
    freshRegions: 0,
    lastUpdated: null,
  });
  /** Total documents in Firestore `pois` (global count; not USA-only). */
  const [poisCollectionTotalCount, setPoisCollectionTotalCount] = useState(null);
  const [poisCollectionCountError, setPoisCollectionCountError] = useState(false);

  const POI_TYPE_GROUPS = {
    travel: ['rv_parking', 'rest_stop', 'hotel', 'airport', 'train_station', 'bus_station'],
    health: ['clinic', 'spa', 'massage_parlor', 'gym', 'rock_climbing_gym'],
    parks: ['fishing_destination', 'park', 'golf_course'],
  };

  const POI_TYPE_META_BY_KEY = POI_TYPE_OPTIONS.reduce((acc, opt) => {
    acc[opt.key] = opt;
    return acc;
  }, {});
  const { venues, refreshVenues } = useVenues();
  const { user, setUser, setDevModeUser } = useUser();
  const { flags: featureFlags } = useFeatureFlags();
  const creditEconomy = useCreditEconomy();
  const [loading, setLoading] = useState(false);
  const [photoFetchProgress, setPhotoFetchProgress] = useState(0);
  const [isFetchingPhotos, setIsFetchingPhotos] = useState(false);
  const [totalVenues, setTotalVenues] = useState(0);
  const [processedVenues, setProcessedVenues] = useState(0);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [venuePhotos, setVenuePhotos] = useState([]);
  const [venueStats, setVenueStats] = useState({
    total: 0,
    withPhotos: 0,
    withoutPhotos: 0,
    notFound: 0,
    venuesNeedingPhotos: []
  });
  const [testMode, setTestMode] = useState(false);
  const [testVenueCount, setTestVenueCount] = useState(5);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [venueSearchQuery, setVenueSearchQuery] = useState('');
  const [userStats, setUserStats] = useState({
    total: 0,
    active: 0,
    suspended: 0,
    admins: 0,
    users: 0,
    organizationAccounts: 0,
    personalAccounts: 0
  });
  const [userIdMismatches, setUserIdMismatches] = useState({});
  const [devModeEmail, setDevModeEmail] = useState('tjhawk27@gmail.com');
  const isDev = process.env.NODE_ENV === 'development';
  const [uploadType, setUploadType] = useState('background');
  const [reports, setReports] = useState([]);
  const [usersData, setUsersData] = useState({});
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingReport, setRejectingReport] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [showUploadReviewModal, setShowUploadReviewModal] = useState(false);
  const [pendingVenueUpdates, setPendingVenueUpdates] = useState([]);
  const [isApprovingReport, setIsApprovingReport] = useState(false);
  const [approvingReportId, setApprovingReportId] = useState(null);
  const [isRejectingReport, setIsRejectingReport] = useState(false);
  const [isUploadingReports, setIsUploadingReports] = useState(false);
  const [isFinishingUpload, setIsFinishingUpload] = useState(false);
  const [editingResourceSubmission, setEditingResourceSubmission] = useState(null);
  const [editResourceCategory, setEditResourceCategory] = useState('');
  const [editResourceNewCategory, setEditResourceNewCategory] = useState('');
  const [resourceCategoryCatalog, setResourceCategoryCatalog] = useState([
    ...RESOURCE_CATEGORIES,
    MERCH_RESOURCE_CATEGORY,
  ]);
  const [editResourcePhoto, setEditResourcePhoto] = useState({
    file: null,
    preview: null,
    width: null,
    height: null,
    removed: false,
  });
  const [isSavingResourceEdit, setIsSavingResourceEdit] = useState(false);
  
  // Add new state for site management
  const [selectedEmail, setSelectedEmail] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('registration');
  const [testResult, setTestResult] = useState(null);
  const [isDocCollapsed, setIsDocCollapsed] = useState(true);
  const [maintenanceForm, setMaintenanceForm] = useState({
    password: '',
    duration: '1',
    message: '',
    scheduledStart: new Date().toISOString().slice(0, 16),
  });

  // Add new state for maintenance list
  const [maintenanceList, setMaintenanceList] = useState([]);

  // Community moderation states
  const [communityMessages, setCommunityMessages] = useState([]);
  const [loadingCommunityMessages, setLoadingCommunityMessages] = useState(false);
  const [communitySearchQuery, setCommunitySearchQuery] = useState('');
  const [selectedVenueFilter, setSelectedVenueFilter] = useState('all');
  


  const fadeIn = useAnimation(0, 1, 700);
  const slideUp = useAnimation(40, 0, 700);

  const [verifierNames, setVerifierNames] = useState({});
  const [notification, setNotification] = useState(null);

  // Add new state for selected user
  const [selectedUserId, setSelectedUserId] = useState('');

  const VENUES_PER_PAGE = 60;

  // Add new state for venue submissions
  const [newVenueSubmissions, setNewVenueSubmissions] = useState([]);

  // Add new state for user type filter

  // Add these state declarations near the top with other state variables
  const [reportSearchQuery, setReportSearchQuery] = useState('');
  const [showTechSpecsOnly, setShowTechSpecsOnly] = useState(false);
  const [reportSortBy, setReportSortBy] = useState('newest');
  const [reportColumnCount, setReportColumnCount] = useState(() => loadReportColumnCount('venue'));
  const reportSearchPlaceholderColor = getAdminSearchPlaceholderColor(adminIsDarkMode);

  useEffect(() => {
    fetchResourceCategoryCatalog(db)
      .then(setResourceCategoryCatalog)
      .catch((error) => console.error('Error loading resource categories in admin:', error));
  }, []);

  useEffect(() => {
    setReportColumnCount(loadReportColumnCount(reportType));
    setReportSortBy('newest');
  }, [reportType]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(`vrc-admin-reports-columns-${reportType}`, String(reportColumnCount));
    }
  }, [reportColumnCount, reportType]);

  useEffect(() => {
    setWebPlaceholderColor(ADMIN_REPORTS_SEARCH_ID, reportSearchPlaceholderColor);
    setWebPlaceholderColor(ADMIN_COMMUNITY_SEARCH_ID, reportSearchPlaceholderColor);
  }, [reportSearchPlaceholderColor]);

  useEffect(() => {
    if (!DEV_MODE && activeTab === 'site_dev_tools') {
      setActiveTab('dashboard');
    }
  }, [activeTab]);

  // Inside Admin function, add a new state variable for import mode
  const [importMode, setImportMode] = useState('add'); // 'add' or 'overwrite'
  const [isImportingMerch, setIsImportingMerch] = useState(false);
  const [merchImportStatus, setMerchImportStatus] = useState('');
  const [merchImportProgress, setMerchImportProgress] = useState(0);
  // Add state for the overwrite password
  const [overwritePassword, setOverwritePassword] = useState('');
  const [showOverwritePasswordPrompt, setShowOverwritePasswordPrompt] = useState(false);

  const SITE_SECTION_LABELS = {
    analytics: { title: 'Analytics', subtitle: 'Site-wide usage stats and system information.' },
    'feature-flags': { title: 'Feature Flags', subtitle: 'Toggle feature visibility for staging and production.' },
    'credit-economy': { title: 'Credit Economy', subtitle: 'Conversion rates and Credits granted per approval type.' },
    email: { title: 'Email System', subtitle: 'Test transactional email templates and delivery.' },
    maintenance: { title: 'Maintenance', subtitle: 'Schedule or enable site maintenance mode.' },
    manuals: { title: 'Manuals', subtitle: 'Manage equipment manual links and metadata.' },
    database: { title: 'Database', subtitle: 'Import, export, and maintain database records.' },
    'dev-tools': { title: 'Dev Tools', subtitle: 'Development and testing utilities.' },
  };

  const SITE_MERGED_PAGES = {
    site_operations: {
      title: 'Operations',
      subtitle: 'Analytics, feature flags, email, and maintenance controls.',
      sections: ['analytics', 'feature-flags', 'credit-economy', 'email', 'maintenance'],
    },
    site_data: {
      title: 'Data & Imports',
      subtitle: 'Database tools, equipment manuals, and API integrations.',
      sections: ['database', 'manuals'],
    },
    site_locations: {
      title: 'Locations',
      subtitle: 'POI coverage and location category management.',
    },
    site_dev_tools: {
      title: 'Dev Tools',
      subtitle: 'Development and testing utilities.',
      sections: ['dev-tools'],
    },
  };

  const persistFeatureFlag = async (key, value) => {
    try {
      const auth = getAuth();
      await setDoc(doc(db, 'system', 'featureFlags'), {
        [key]: value,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null,
      }, { merge: true });
      setNotification({ message: 'Feature flag saved', type: 'success' });
    } catch (e) {
      console.error(e);
      setNotification({ message: e.message || 'Failed to save flag', type: 'error' });
    }
  };

  const persistTourPlannerFlag = async (key, value) => {
    try {
      const auth = getAuth();
      await setDoc(doc(db, 'system', 'featureFlags'), {
        [key]: value,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null,
      }, { merge: true });
      setNotification({ message: 'Feature flag saved', type: 'success' });
    } catch (e) {
      console.error(e);
      setNotification({ message: e.message || 'Failed to save flag', type: 'error' });
    }
  };
  
  // Manuals editor state
  const [editableManuals, setEditableManuals] = useState({});
  const [manualsSearchQuery, setManualsSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [editingManual, setEditingManual] = useState(null);
  const [addingNewManual, setAddingNewManual] = useState(false);
  const [newManualForm, setNewManualForm] = useState({
    id: '',
    searchWord: '',
    name: '',
    url: '',
    category: 'audio',
    software: { name: '', url: '' }
  });

  // Inside Admin function, add these new state variables
  const [importStartTime, setImportStartTime] = useState(null);
  const [importEndTime, setImportEndTime] = useState(null);
  const [importReport, setImportReport] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
    calculateVenueStats();
    loadNewVenueSubmissions();
    loadManualsDatabase();
    loadCommunityMessages();
    
    // Prepare venuePhotos array for display
    const venuesWithPhotos = venues.filter(venue => venue.photos && venue.photos.length > 0);
    setVenuePhotos(venuesWithPhotos.map(venue => ({
      id: venue.id,
      name: venue.name,
      photoCount: venue.photos?.length || 0
    })));
  }, [venues]);

  // Load the manuals database from Firebase
  const loadManualsDatabase = async () => {
    try {
      const db = getFirestore();
      const manualsSnapshot = await getDocs(collection(db, 'equipment_manuals'));
      const manualsData = {};
      
      manualsSnapshot.forEach((doc) => {
        manualsData[doc.id] = doc.data();
      });

      // If no manuals in database, populate with default data
      if (Object.keys(manualsData).length === 0) {
        console.log('No manuals found in database, populating with default data...');
        const { EQUIPMENT_MANUALS } = await import('../utils/findRelevantManuals.js');
        const batch = writeBatch(db);
        
        Object.entries(EQUIPMENT_MANUALS).forEach(([keyword, manual]) => {
          const docRef = doc(db, 'equipment_manuals', keyword);
          batch.set(docRef, manual);
          manualsData[keyword] = manual;
        });
        
        await batch.commit();
        console.log('Default manuals populated to database');
      }

      setEditableManuals(manualsData);
    } catch (error) {
      console.error('Error loading manuals database:', error);
      // Fallback to static data if database fails
      try {
        const { EQUIPMENT_MANUALS } = await import('../utils/findRelevantManuals.js');
        setEditableManuals(EQUIPMENT_MANUALS || {});
      } catch (fallbackError) {
        console.error('Error loading fallback manuals:', fallbackError);
        setEditableManuals({});
      }
    }
  };

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    console.log('Admin component mounted, calling loadReports');
    if (activeTab === 'venue_reports' || activeTab === 'resource_submissions' || activeTab === 'venue_spec_flags' || activeTab === 'venue_interest_tags') {
      loadReports();
    }
  }, [reportType, reportStatus, activeTab]); // Add activeTab as a dependency

  useEffect(() => {
    const fetchUsersData = async () => {
      if (!reports || reports.length === 0) return;

      const db = getFirestore();
      const usersRef = collection(db, 'users');
      const userEmails = reports
        .map(report => report.userEmail)
        .filter(Boolean);

      const newUsersData = { ...usersData };

      for (const email of userEmails) {
        if (!newUsersData[email]) {
          const q = query(usersRef, where('email', '==', email));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            newUsersData[email] = {
              ...userData,
              id: querySnapshot.docs[0].id
            };
          }
        }
      }

      setUsersData(newUsersData);
    };

    fetchUsersData();
  }, [reports]);

  // Add this effect near the other useEffect hooks
  useEffect(() => {
    if (activeTab === 'users') {
      console.log('Users tab active, fetching users and checking mismatches...');
      fetchUsers();
      fetchOrganizations(); // Ensure organizations are loaded for user cards
    }
  }, [activeTab]);

  useEffect(() => {
    const db = getFirestore();
    const unsubscribe = onSnapshot(collection(db, 'system', 'maintenance', 'schedules'), (snapshot) => {
      const schedules = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMaintenanceList(schedules);
    });

    return () => unsubscribe();
  }, []);

  // Fetch organizations
  const fetchOrganizations = async () => {
    try {
      const db = getFirestore();
      const orgsSnapshot = await getDocs(collection(db, 'organizations'));
      const orgsData = orgsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrganizations(orgsData);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  // Update the fetchUsers function to handle the fetch error better
  const fetchUsers = async () => {
    try {
      console.log('Starting fetchUsers...');
      const db = getFirestore();
      const auth = getAuth();

      // Fetch users from Firestore
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Fetched users from Firestore:', usersData);

      // Set users first so they show up even if mismatch check fails
      setUsers(usersData);
      calculateUserStats(usersData);

      // Check for mismatches using Cloud Function
      try {
        if (!auth.currentUser) {
          console.log('No user logged in, skipping mismatch check');
          return;
        }

        const idToken = await auth.currentUser.getIdToken(true);

        // Use the correct URL based on environment
        const baseUrl = process.env.NODE_ENV === 'development'
          ? 'http://127.0.0.1:5002/venue-report-card/us-central1'
          : 'https://us-central1-venue-report-card.cloudfunctions.net';

        const response = await fetch(`${baseUrl}/checkUserIdMismatches`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
            'Accept': 'application/json'
          },
          credentials: 'same-origin', // Change from 'include' to 'same-origin'
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('Raw mismatch check result:', result);
        const { mismatches } = result.data;
        console.log('Found mismatches:', mismatches);
        
        if (mismatches && Object.keys(mismatches).length > 0) {
          console.log('Setting mismatches in state:', mismatches);
          setUserIdMismatches(mismatches);
        } else {
          console.log('No mismatches found or mismatches is empty');
          setUserIdMismatches({});
        }
      } catch (error) {
        console.error('Error checking mismatches:', error);
        // Don't let mismatch check failure prevent showing users
        setUserIdMismatches({});
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Error fetching users: ' + error.message);
    }
  };

  const calculateVenueStats = () => {
    const stats = {
      total: venues.length,
      withPhotos: 0,
      withoutPhotos: 0,
      notFound: 0,
      venuesNeedingPhotos: []
    };

    venues.forEach(venue => {
      const hasPhotos = 
        (venue.backgroundImage) || // Check background image
        (venue.photos && venue.photos.length > 0) || // Check photos array
        (venue.venue_media && typeof venue.venue_media === 'string' && 
          JSON.parse(venue.venue_media).photos?.length > 0); // Check venue_media

      if (hasPhotos) {
        stats.withPhotos++;
      } else {
        stats.withoutPhotos++;
        stats.venuesNeedingPhotos.push(venue);
      }

      if (venue.notFoundOnLiveNation) {
        stats.notFound++;
      }
    });

    setVenueStats(stats);
  };

  const calculateUserStats = (usersData) => {
    const organizationAccounts = usersData.filter(u => u.accountType === 'organization').length;
    const personalAccounts = usersData.length - organizationAccounts;
    
    setUserStats({
      total: usersData.length,
      active: usersData.filter(u => u.status !== 'suspended').length,
      suspended: usersData.filter(u => u.status === 'suspended').length,
      admins: usersData.filter(u => u.userType === 'admin').length,
      users: usersData.filter(u => u.userType === 'user').length,
      organizationAccounts: organizationAccounts,
      personalAccounts: personalAccounts
    });
  };

  // Add this helper function to check for duplicates more thoroughly
  const findExistingVenue = (venue, existingVenues) => {
    // First try exact match
    const normalizedName = normalizeVenueName(venue.name);
    const normalizedState = venue.state?.toUpperCase().trim();
    const normalizedCity = venue.city ? normalizeVenueName(venue.city) : '';
    
    const primaryKey = `${normalizedName}_${normalizedState}`;
    const cityKey = normalizedCity ? `${normalizedName}_${normalizedCity}_${normalizedState}` : null;
    
    let existingVenue = existingVenues.get(primaryKey) || (cityKey ? existingVenues.get(cityKey) : null);
    
    // If no exact match, look for name matches with similar location
    if (!existingVenue) {
      // Try matching just by name and checking if locations are compatible
      const nameMatches = Array.from(existingVenues.entries())
        .filter(([key, venue2]) => {
          const keys = key.split('_');
          const venueName = keys[0];
          return venueName === normalizedName;
        })
        .map(([_, venue2]) => venue2);
      
      if (nameMatches.length > 0) {
        console.log(`Found ${nameMatches.length} name matches for venue: ${venue.name}`);
        
        // Check if any of the name matches has compatible location
        const locationMatch = nameMatches.find(v => {
          // If states match exactly, great
          if (v.data.state?.toUpperCase().trim() === normalizedState) {
            return true;
          }
          
          // If one is "United States" and the other has a state abbreviation, might be compatible
          if ((v.data.state === 'United States' && venue.state !== 'United States') ||
              (venue.state === 'United States' && v.data.state !== 'United States')) {
            return true;
          }
          
          // If city matches and one state is unknown, might be compatible
          if (v.data.city && venue.city && 
              normalizeVenueName(v.data.city) === normalizeVenueName(venue.city) &&
              (v.data.state === 'UNKNOWN' || venue.state === 'UNKNOWN')) {
            return true;
          }
          
          return false;
        });
        
        if (locationMatch) {
          console.log(`Found location compatible match for venue: ${venue.name}`, locationMatch);
          existingVenue = locationMatch;
        }
      }
    }
    
    // If still no match, check for similar venues with fuzzy matching
    if (!existingVenue) {
      for (const [, venue2] of existingVenues) {
        if (areVenuesDuplicate(venue, venue2.data)) {
          existingVenue = venue2;
          break;
        }
      }
    }
    
    return existingVenue;
  };

  // Add this helper function to check for duplicates during import
  const findAndCleanupDuplicates = async (venue, existingVenues, db) => {
    // Find all potential duplicates
    const duplicates = [];
    for (const [, existingVenue] of existingVenues) {
      if (areVenuesDuplicate(venue, existingVenue.data)) {
        duplicates.push(existingVenue);
      }
    }

    if (duplicates.length === 0) {
      return null;
    }

    // Sort duplicates by completeness
    duplicates.sort((a, b) => {
      const aFields = Object.keys(a.data).length;
      const bFields = Object.keys(b.data).length;
      return bFields - aFields;
    });

    // Use the most complete venue as primary
    const primaryVenue = duplicates[0];
    let mergedData = { ...primaryVenue.data };

    // Merge all duplicates into the primary venue
    for (let i = 1; i < duplicates.length; i++) {
      const duplicate = duplicates[i];
      mergedData = mergeVenueData(mergedData, duplicate.data);
      
      // Delete the duplicate
      await deleteDoc(doc(db, 'venues', duplicate.id));
    }

    // Update the primary venue with merged data
    await updateDoc(doc(db, 'venues', primaryVenue.id), {
      ...mergedData,
      updatedAt: serverTimestamp(),
      mergeHistory: arrayUnion({
        timestamp: new Date().toISOString(),
        source: 'Import Cleanup',
        mergedVenues: duplicates.slice(1).map(d => d.id)
      })
    });

    return primaryVenue;
  };

  // Add new status display component
  const ImportStatus = ({ status, progress, details }) => (
    <View style={styles.importStatusContainer}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <Text style={styles.importStatusTitle}>Import Status</Text>
        {importReport && (
          <TouchableOpacity
            style={{ 
              backgroundColor: '#2ecc71', 
              paddingVertical: 6, 
              paddingHorizontal: 12, 
              borderRadius: 5 
            }}
            onPress={() => downloadImportReport(importReport)}
          >
            <Text style={{ color: 'white', fontSize: 12, fontWeight: '500' }}>
              Download Report
            </Text>
          </TouchableOpacity>
        )}
      </View>
      
      {progress > 0 && (
        <View style={styles.progressBarContainer}>
          <View style={[styles.progressBar, { width: `${progress * 100}%` }]} />
          <Text style={styles.progressText}>{`${Math.round(progress * 100)}%`}</Text>
        </View>
      )}
      
      {/* Show timing information if available */}
      {importStartTime && (
        <View style={{ marginBottom: 10 }}>
          <Text style={{ color: '#ccc', fontSize: 12 }}>
            Started: {importStartTime.toLocaleTimeString()}
            {importEndTime && ` • Duration: ${formatDuration((importEndTime - importStartTime) / 1000)}`}
          </Text>
        </View>
      )}
      
      <Text style={styles.importStatusText}>{status}</Text>
      
      {details && details.length > 0 && (
        <View style={{ marginTop: 10 }}>
          <Text style={{ color: 'white', fontSize: 14, marginBottom: 5 }}>
            Log ({details.length} entries):
          </Text>
          <ScrollView style={styles.detailsContainer}>
            {details.map((detail, index) => (
              <Text key={index} style={[
                styles.detailText,
                detail.type === 'error' && styles.errorText,
                detail.type === 'success' && styles.successText,
                detail.type === 'warning' && styles.warningText,
                detail.type === 'debug' && { color: '#aaa', fontSize: 12 }
              ]}>
                {new Date(detail.timestamp).toLocaleTimeString().split(' ')[0]} • {detail.message}
              </Text>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );

  // Add styles for the status display
  const styles = StyleSheet.create({
    importStatusContainer: {
      marginTop: 20,
      padding: 15,
      backgroundColor: '#2c2c2c',
      borderRadius: 8,
      maxWidth: 600,
    },
    importStatusTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: '#fff',
      marginBottom: 10,
    },
    progressBarContainer: {
      height: 20,
      backgroundColor: '#1a1a1a',
      borderRadius: 10,
      marginBottom: 10,
      overflow: 'hidden',
      position: 'relative',
    },
    progressBar: {
      height: '100%',
      backgroundColor: '#4a90e2',
      borderRadius: 10,
    },
    progressText: {
      position: 'absolute',
      width: '100%',
      textAlign: 'center',
      color: '#fff',
      fontSize: 12,
      lineHeight: 20,
    },
    importStatusText: {
      color: '#fff',
      marginBottom: 10,
    },
    detailsContainer: {
      maxHeight: 200,
      marginTop: 10,
    },
    detailText: {
      color: '#ccc',
      fontSize: 14,
      marginBottom: 5,
    },
    errorText: {
      color: '#ff6b6b',
    },
    successText: {
      color: '#69db7c',
    },
    warningText: {
      color: '#ffd43b',
    },
  });

  // Add state for detailed status messages
  const [importDetails, setImportDetails] = useState([]);

  // Update the handleImportCSV function to include more detailed status reporting
  const handleImportCSV = async (event) => {
    try {
      // Record start time for the report
      const startTime = new Date();
      setImportStartTime(startTime);
      
      const fileInput = document.getElementById('venue-csv-upload');
      const file = fileInput?.files?.[0];
      
      setImportDetails([]);
      setImportProgress(0);
      setImportReport(null);
      
      const addDetail = (message, type = 'info') => {
        const detail = { message, type, timestamp: new Date().toISOString() };
        setImportDetails(prev => [...prev, detail]);
        console.log(`${type.toUpperCase()}: ${message}`);
        
        // Update status message with the latest information
        if (type !== 'debug') {
          setImportStatus(message);
        }
        
        return detail;
      };

      addDetail(`Starting CSV import process in ${importMode.toUpperCase()} mode...`, 'info');
      addDetail(`User: ${user.email} (${user.firstName} ${user.lastName})`, 'info');
      addDetail(`File: ${file?.name || 'Unknown'}, Size: ${file ? (file.size / 1024).toFixed(2) + ' KB' : 'Unknown'}`, 'info');
    
      if (!file) {
        addDetail('Error: No file selected. Please choose a CSV file.', 'error');
        return;
      }

      const rejectReason = csvRejectReason(file);
      if (rejectReason) {
        addDetail(`Error: ${rejectReason}`, 'error');
        return;
      }

      // COMMENT OUT FOR NOW TO REMOVE PASSWORD PROTECTION
      // Handle overwrite mode password protection
      // if (importMode === 'overwrite') {
      //   setShowOverwritePasswordPrompt(true);
      //   return;
      // }

      // Confirm based on import mode
      let confirmMessage = '';
      if (importMode === 'add') {
        confirmMessage = 'This will import and add new venues from the selected CSV file. Continue?';
      } 
      else {
        confirmMessage = 'This will overwrite all existing venues with the new data from the selected CSV file. Continue?';
      }
      
      if (!confirm(confirmMessage)) {
        addDetail('Import cancelled by user.', 'info');
        return;
      }

      setIsImporting(true);
      addDetail('Reading CSV file...', 'info');
      
      const fileContent = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => {
          console.error('Error reading file:', e);
          addDetail('Error reading file. Please try again.', 'error');
          setImportStatus('Error reading file. Please try again.');
          setIsImporting(false);
        };
        reader.readAsText(file);
      });
      
      if (!fileContent) {
        addDetail('Error: File appears to be empty', 'error');
        setImportStatus('Error: File appears to be empty');
        setIsImporting(false);
        return;
      }

      addDetail('File read complete. Starting to parse CSV data...', 'info');
      addDetail(`File size: ${(fileContent.length / 1024).toFixed(2)} KB`, 'info');
      
      const results = await new Promise((resolve) => {
        addDetail('Initializing CSV parser...', 'info');
        Papa.parse(fileContent, {
          header: true,
          skipEmptyLines: 'greedy',
          delimitersToGuess: [',', '\t', ';', '|'],
          transformHeader: (header) => {
            if (!header) return null;
            addDetail(`Processing header: "${header}"`, 'debug');
            const mappedHeader = mapVenueCsvHeader(header);
            addDetail(`Mapped header: "${header}" → "${mappedHeader}"`, 'debug');
            return mappedHeader;
          },
          transform: (value, field) => {
            if (!value) return null;
            if (typeof value === 'string') {
              value = value.replace(/^\uFEFF/, '').trim();
              return value === '' ? null : value;
            }
            return value;
          },
          complete: (results) => {
            addDetail(`CSV parsing complete. Raw row count: ${results.data.length}`, 'info');
            
            // Filter out rows where all fields are empty or undefined
            const initialCount = results.data.length;
            results.data = results.data.filter(row => {
              const hasData = Object.values(row).some(value => 
                value !== undefined && value !== null && value.toString().trim() !== ''
              );
              return hasData;
            });
            
            addDetail(`Filtered out ${initialCount - results.data.length} empty rows`, 'info');

            // Clean up each row by removing empty/null fields and empty string fields
            addDetail('Cleaning up data rows...', 'info');
            results.data = results.data.map(row => {
              const cleanRow = {};
              Object.entries(row).forEach(([key, value]) => {
                // Only keep fields that have a value and aren't empty strings.
                // Unnamed spreadsheet columns arrive as null / null_1 … (Papa renames duplicates).
                if (value !== null && value !== undefined && key !== '' && !/^null(_\d+)?$/.test(key) && 
                    (typeof value !== 'string' || value.trim() !== '')) {
                  cleanRow[key] = value;
                }
              });
              return cleanRow;
            });

            addDetail(`CSV processing complete. Found ${results.data.length} valid rows with data.`, 'success');
            if (results.data.length > 0) {
              addDetail(`Found columns: ${Object.keys(results.data[0] || {}).join(', ')}`, 'info');
            }
            
            resolve(results);
        },
        error: (error) => {
          console.error('CSV parsing error:', error);
            addDetail(`CSV parsing error: ${error.message}`, 'error');
            resolve({ data: [], errors: [error] });
          }
        });
      });

      if (!results.data || results.data.length === 0) {
        addDetail('Error: No valid data found in CSV file', 'error');
        setImportStatus('Error: No valid data found in CSV file');
        setIsImporting(false);
        return;
      }

      addDetail(`Starting database operations for ${results.data.length} venues...`, 'info');
      const db = getFirestore();
      let processedCount = 0;
      let successCount = 0;
      let errorCount = 0;
      let skippedCount = 0;
      let newCount = 0;
      let updatedCount = 0;
      let missingRequiredFields = 0;
      let embedCoordsResolvedCount = 0;
      
      // For detailed reporting
      const processedVenues = [];
      
      // If in overwrite mode, delete all existing venues first
      if (importMode === 'overwrite' && results.data.length !== 0) {
        addDetail('Overwrite mode: Deleting all existing venues...', 'warning');
        const querySnapshot = await getDocs(collection(db, 'venues'));
        addDetail(`Found ${querySnapshot.size} existing venues to delete`, 'warning');
        const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
        addDetail(`Deleted ${querySnapshot.size} existing venues.`, 'info');
      }

      addDetail('Starting to process venues...', 'info');
      // Process venues
      for (const venueData of results.data) {
        try {
          processedCount++;
          setImportProgress(processedCount / results.data.length);
          
          if (processedCount % 10 === 0 || processedCount === results.data.length) {
            addDetail(`Progress: ${processedCount}/${results.data.length} venues (${Math.round(processedCount / results.data.length * 100)}%)`, 'info');
          }

          const venueName = String(venueData?.name || '').trim();
          const venueState = String(venueData?.state || '').trim();
          // Skip if required fields are missing or undefined
          if (!venueName || !venueState) {
            addDetail(`Skipping venue at row ${processedCount}: Missing required fields (name: ${venueName || 'missing'}, state: ${venueState || 'missing'})`, 'warning');
            skippedCount++;
            missingRequiredFields++;
            
            processedVenues.push({
              status: 'skipped',
              reason: 'Missing required fields',
              data: venueData
            });
            
            continue;
          }

          venueData.name = venueName;
          venueData.state = venueState;

          addDetail(`Processing venue ${processedCount}/${results.data.length}: ${venueData.name} (${venueData.city || 'Unknown City'}, ${venueData.state})`, 'info');

          // Generate venue ID
          const venueId = generateConsistentVenueId({
            name: venueData.name,
            city: venueData.city || '',
            state: venueData.state
          });

          addDetail(`Generated venue ID: ${venueId}`, 'debug');

          // Check if venue exists
          const venueRef = doc(db, 'venues', venueId);
          const venueDoc = await getDoc(venueRef);

          // Clean up the venue data before saving
          const cleanVenueData = Object.fromEntries(
            Object.entries(venueData).filter(([key, value]) => 
              value !== null && value !== undefined && key !== '' && 
              (typeof value !== 'string' || value.trim() !== '')
            )
          );

          cleanVenueData.manualsLinks = findRelevantManuals(cleanVenueData);

          if (cleanVenueData.mapEmbedLink) {
            const rawEmbed = String(cleanVenueData.mapEmbedLink).trim();
            cleanVenueData.mapEmbedLink = (rawEmbed.includes('<iframe') || rawEmbed.includes('google.com/maps'))
              ? (normalizeMapEmbed(rawEmbed) || rawEmbed)
              : rawEmbed;

            addDetail(`Resolving map coordinates for ${venueData.name}…`, 'info');
            const embedCoords = await attachEmbedCoordinatesToVenuePayload({
              ...cleanVenueData,
              name: cleanVenueData.name,
              city: cleanVenueData.city || '',
              state: cleanVenueData.state,
            });
            Object.assign(cleanVenueData, embedCoords);

            if (embedCoords.latitude != null && embedCoords.longitude != null) {
              embedCoordsResolvedCount += 1;
              addDetail(
                `Resolved coordinates for ${venueData.name}: ${embedCoords.latitude}, ${embedCoords.longitude}`,
                'success'
              );
            } else if (embedCoords.embedResolveError) {
              addDetail(
                `Could not resolve embed for ${venueData.name}: ${embedCoords.embedResolveError}`,
                'warning'
              );
            }
          }

          if (venueDoc.exists() && importMode === 'add') {
            // In add mode, merge with existing data
            const existingData = venueDoc.data();
            const mergedData = mergeVenueData(existingData, {
              ...cleanVenueData,
              updatedAt: new Date(),
              lastImported: new Date()
            });
            await setDoc(venueRef, mergedData, { merge: true });
            addDetail(`Updated existing venue: ${venueData.name} (${venueData.city || 'Unknown City'}, ${venueData.state})`, 'success');
            updatedCount++;
            
            processedVenues.push({
              status: 'updated',
              id: venueId,
              name: venueData.name,
              city: venueData.city,
              state: venueData.state
            });
          } else {
            // Create new venue
            await setDoc(venueRef, {
              ...cleanVenueData,
              createdAt: new Date(),
              updatedAt: new Date(),
              lastImported: new Date(),
              status: 'pending',
              approved: {}
            });
            addDetail(`Created new venue: ${venueData.name} (${venueData.city || 'Unknown City'}, ${venueData.state})`, 'success');
            newCount++;
            
            processedVenues.push({
              status: 'created',
              id: venueId,
              name: venueData.name,
              city: venueData.city,
              state: venueData.state
            });
          }
          successCount++;
    } catch (error) {
          console.error('Error processing venue:', error);
          addDetail(`Error processing venue ${venueData?.name || 'unknown'}: ${error.message}`, 'error');
          errorCount++;
          
          processedVenues.push({
            status: 'error',
            error: error.message,
            data: venueData
          });
        }
      }

      // Record end time
      const endTime = new Date();
      setImportEndTime(endTime);
      const duration = (endTime - startTime) / 1000; // Duration in seconds
      
      const summary = `Import complete. Duration: ${formatDuration(duration)}. Processed: ${processedCount}, Success: ${successCount} (New: ${newCount}, Updated: ${updatedCount}), Coordinates resolved: ${embedCoordsResolvedCount}, Errors: ${errorCount}, Skipped: ${skippedCount}`;
      addDetail(summary, 'info');
      setImportStatus(summary);
      
      // Generate the report
      const reportData = {
        user: {
          email: user.email,
          name: `${user.firstName} ${user.lastName}`,
          id: user.id
        },
        import: {
          mode: importMode,
          filename: file.name,
          filesize: file.size,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          duration: duration,
          formattedDuration: formatDuration(duration)
        },
        stats: {
          total: processedCount,
          success: successCount,
          new: newCount,
          updated: updatedCount,
          errors: errorCount,
          skipped: skippedCount,
          missingRequiredFields: missingRequiredFields,
          embedCoordsResolved: embedCoordsResolvedCount,
        },
        venues: processedVenues,
        details: importDetails
      };
      
      setImportReport(reportData);
      
      // Generate and download the report
      downloadImportReport(reportData);
      
    } catch (error) {
      console.error('Import error:', error);
      addDetail(`Import error: ${error.message}`, 'error');
      setImportStatus('Import failed');
    } finally {
      setIsImporting(false);
      setImportProgress(1);
    }
  };

  // Add this helper function for formatting duration
  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds.toFixed(1)} seconds`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes} min ${remainingSeconds.toFixed(0)} sec`;
  };

  // Add this function to download the report as JSON
  const downloadImportReport = (reportData) => {
    try {
      // Generate timestamped filename
      const timestamp = new Date().toISOString().replace(/:/g, '-').replace(/\..+/, '');
      const filename = `venue_import_report_${timestamp}.json`;
      
      // Convert data to formatted JSON string
      const jsonContent = JSON.stringify(reportData, null, 2);
      
      // Create a blob with the data
      const blob = new Blob([jsonContent], { type: 'application/json' });
      
      // Create a URL for the blob
      const url = URL.createObjectURL(blob);
      
      // Create a temporary anchor element
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      
      // Trigger the download
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);
      
      setImportStatus(prev => `${prev} - Report downloaded as ${filename}`);
    } catch (error) {
      console.error('Error downloading report:', error);
      setImportStatus(prev => `${prev} - Error creating report: ${error.message}`);
    }
  };

  // Add a new function to handle overwrite with password confirmation
  const handleOverwriteWithPassword = async () => {
    try {
      // if (overwritePassword !== "REDACTED_DEV_PASSWORD") {
      //   alert("Incorrect password. Overwrite operation cancelled.");
      //   setShowOverwritePasswordPrompt(false);
      //   setOverwritePassword('');
      //   return;
      // }
      
      // setShowOverwritePasswordPrompt(false);
      // setOverwritePassword('');
      
      // Continue with the import after password verification using the enhanced handleImportCSV function
      // (we'll reuse all the logic there)
      const fileInput = document.getElementById('venue-csv-upload');
      if (!fileInput?.files?.[0]) {
        alert("No file selected. Please choose a CSV file.");
        return;
      }
      
      // Force importMode to overwrite and then call handleImportCSV
      const originalImportMode = importMode;
      setImportMode('overwrite');
      
      // Small delay to ensure state updates
      setTimeout(() => {
        handleImportCSV();
        // Reset importMode after a delay
        // setTimeout(() => {
        //   setImportMode(originalImportMode);
        // }, 500);
      }, 100);
      
    } catch (error) {
      console.error('Overwrite error:', error);
      setImportStatus('Overwrite failed: ' + error.message);
      setIsImporting(false);
    }
  };

  const getMerchCsvValue = (row, keys) => {
    const entries = Object.entries(row || {});
    for (const key of keys) {
      const want = String(key).toLowerCase().replace(/[\s_/-]+/g, '');
      const match = entries.find(([header]) =>
        String(header).toLowerCase().replace(/[\s_/-]+/g, '') === want
      );
      if (match && match[1] != null && String(match[1]).trim() !== '') {
        return String(match[1]).trim();
      }
    }
    return '';
  };

  const normalizeMerchWebsite = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    return `https://${raw}`;
  };

  const parseMerchEcoFlag = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    return raw === 'yes' || raw === 'y' || raw === 'true' || raw === '1';
  };

  const handleImportMerchCSV = async () => {
    const fileInput = document.getElementById('merch-csv-upload');
    const file = fileInput?.files?.[0];
    setMerchImportProgress(0);
    setMerchImportStatus('');

    if (!file) {
      setMerchImportStatus('Select a merch CSV file first.');
      return;
    }
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setMerchImportStatus(`Invalid file type: ${file.type || file.name}`);
      return;
    }
    if (!confirm('Import merch makers into the map? Existing shops with the same name will be updated. Continue?')) {
      setMerchImportStatus('Import cancelled.');
      return;
    }

    setIsImportingMerch(true);
    setMerchImportStatus(`Reading ${file.name}…`);

    try {
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsText(file);
      });

      const results = await new Promise((resolve) => {
        Papa.parse(fileContent, {
          header: true,
          skipEmptyLines: 'greedy',
          transformHeader: (header) => String(header || '').replace(/^\uFEFF/, '').trim(),
          complete: resolve,
          error: (error) => resolve({ data: [], errors: [error] }),
        });
      });

      const rows = (results.data || []).filter((row) =>
        Object.values(row || {}).some((value) => value != null && String(value).trim() !== '')
      );
      if (!rows.length) {
        setMerchImportStatus('No merch rows found in this CSV.');
        setIsImportingMerch(false);
        return;
      }

      const db = getFirestore();
      const existingSnap = await getDocs(collection(db, 'screenprinters'));
      const existingByName = new Map();
      existingSnap.docs.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const key = String(data.name || '').trim().toLowerCase();
        if (key && !existingByName.has(key)) existingByName.set(key, { id: docSnap.id, ...data });
      });

      let created = 0;
      let updated = 0;
      let skipped = 0;
      let resolvedCoords = 0;
      const failures = [];

      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        const name = getMerchCsvValue(row, ['COMPANY', 'NAME', 'SHOP', 'BUSINESS']);
        setMerchImportProgress((i + 1) / rows.length);
        setMerchImportStatus(`Importing ${i + 1} of ${rows.length}${name ? `: ${name}` : ''}…`);

        if (!name) {
          skipped += 1;
          failures.push(`Row ${i + 2}: missing company name`);
          continue;
        }

        const rawEmbed = getMerchCsvValue(row, ['ADDRESS', 'MAP EMBED LINK', 'MAP', 'EMBED']);
        const mapEmbedLink = rawEmbed && (rawEmbed.includes('<iframe') || rawEmbed.includes('google.com/maps'))
          ? (normalizeMapEmbed(rawEmbed) || rawEmbed)
          : (rawEmbed || '');
        const services = getMerchCsvValue(row, ['TYPE', 'SERVICES']);
        const website = normalizeMerchWebsite(getMerchCsvValue(row, ['WEBSITE', 'URL', 'SITE']));
        const eco = parseMerchEcoFlag(getMerchCsvValue(row, ['ECO']));
        const phone = getMerchCsvValue(row, ['PHONE', 'TEL']);
        const email = getMerchCsvValue(row, ['EMAIL']);

        let embedCoords = {
          latitude: null,
          longitude: null,
          googlePlaceId: null,
          googleCid: null,
          embedResolvedBy: null,
          embedResolvedMethod: null,
        };
        if (mapEmbedLink) {
          embedCoords = await resolveEmbedCoordinatesForSave(mapEmbedLink, { name });
          if (embedCoords.latitude != null && embedCoords.longitude != null) resolvedCoords += 1;
        }

        const payload = {
          name,
          website,
          services,
          eco,
          phone,
          email,
          mapEmbedLink: mapEmbedLink || null,
          latitude: embedCoords.latitude,
          longitude: embedCoords.longitude,
          googlePlaceId: embedCoords.googlePlaceId || null,
          googleCid: embedCoords.googleCid || null,
          embedResolvedBy: embedCoords.embedResolvedBy || null,
          embedResolvedMethod: embedCoords.embedResolvedMethod || null,
          embedResolvedAt: embedCoords.latitude != null ? serverTimestamp() : null,
          importedFromCsv: true,
          updatedAt: serverTimestamp(),
        };

        const existing = existingByName.get(name.toLowerCase());
        if (existing) {
          await updateDoc(doc(db, 'screenprinters', existing.id), payload);
          existingByName.set(name.toLowerCase(), { ...existing, ...payload });
          updated += 1;
        } else {
          const createdRef = await addDoc(collection(db, 'screenprinters'), {
            ...payload,
            notes: '',
            imageUrl: null,
            city: '',
            state: '',
            createdAt: serverTimestamp(),
            approvedBy: getAuth().currentUser?.uid || null,
          });
          existingByName.set(name.toLowerCase(), { id: createdRef.id, ...payload });
          created += 1;
        }
      }

      const summary = `Merch import complete. Created ${created}, updated ${updated}, skipped ${skipped}. Coordinates resolved for ${resolvedCoords} of ${rows.length}.`;
      setMerchImportStatus(
        failures.length
          ? `${summary} ${failures.slice(0, 5).join(' | ')}${failures.length > 5 ? ` (+${failures.length - 5} more)` : ''}`
          : summary
      );
      setMerchImportProgress(1);
    } catch (error) {
      console.error('Merch CSV import failed:', error);
      setMerchImportStatus(`Merch import failed: ${error.message || error}`);
    } finally {
      setIsImportingMerch(false);
    }
  };

  // Add this new function after the other venue-related functions
const cleanupDuplicateVenues = async () => {
  if (!confirm('This will scan for and merge duplicate venues. Continue?')) {
    return;
  }

  setLoading(true);
  setImportStatus('Scanning for duplicates...');
  
  try {
    const db = getFirestore();
    const venuesRef = collection(db, 'venues');
    const snapshot = await getDocs(venuesRef);
    
    // Create a map to group venues by their normalized names
    const venueGroups = new Map();
    
    // First pass: group venues by normalized name and location
    snapshot.forEach(doc => {
      const venue = { id: doc.id, ...doc.data() };
      const normalizedName = normalizeVenueName(venue.name);
      const normalizedState = venue.state?.toUpperCase().trim();
      const normalizedCity = venue.city ? normalizeVenueName(venue.city) : '';
      
      const key = `${normalizedName}_${normalizedCity}_${normalizedState}`;
      if (!venueGroups.has(key)) {
        venueGroups.set(key, []);
      }
      venueGroups.get(key).push(venue);
    });

    let mergedCount = 0;
    let deletedCount = 0;
    let processedGroups = 0;
    const totalGroups = venueGroups.size;

    // Second pass: merge duplicates
    for (const [key, venues] of venueGroups) {
      if (venues.length > 1) {
        console.log(`Found ${venues.length} duplicates for key: ${key}`);
        
        // Sort venues by completeness (most complete first)
        venues.sort((a, b) => {
          const aFields = Object.keys(a).length;
          const bFields = Object.keys(b).length;
          return bFields - aFields;
        });

        // Use the first venue as the base
        const primaryVenue = venues[0];
        
        // Merge all other venues into the primary venue
        for (let i = 1; i < venues.length; i++) {
          const duplicateVenue = venues[i];
          
          // Merge the data
          const mergedData = mergeVenueData(primaryVenue, duplicateVenue);
          
          // Update the primary venue with merged data
          await updateDoc(doc(db, 'venues', primaryVenue.id), {
            ...mergedData,
            updatedAt: serverTimestamp(),
            mergedFrom: arrayUnion(duplicateVenue.id)
          });

          // Delete the duplicate
          await deleteDoc(doc(db, 'venues', duplicateVenue.id));
          
          mergedCount++;
          deletedCount++;
        }
      }
      
      processedGroups++;
      setImportProgress(processedGroups / totalGroups);
      setImportStatus(`Processing: ${mergedCount} venues merged, ${deletedCount} duplicates removed\n${Math.round((processedGroups / totalGroups) * 100)}% complete`);
    }

    // Final status
    setImportStatus(`Cleanup complete: ${mergedCount} venues merged, ${deletedCount} duplicates removed`);
    refreshVenues();
    
  } catch (error) {
    console.error('Error cleaning up duplicates:', error);
    setImportStatus(`Error cleaning up duplicates: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

  // Export venues to CSV
  const handleExportCSV = () => {
    // Add headers
    const headers = [
      'STATE/COUNTRY',
      'VENUE',
      'CITY',
      'WEBSITE',
      'CAPACITY',
      'AFFILIATE',
      'STAGE',
      'SPEC SHEET LINK',
      'POWER',
      'LIGHTING CONTROL',
      'LIGHTING FIXTURES',
      'VIDEO',
      'FOH',
      'PA',
      'MONITORS',
      'BACKSTAGE',
      'BACKLINE',
      'MERCH',
      'PARKING',
      'SHORE POWER',
      'NOTES',
      'MAP Embed Link',
      'UPDATED AT',
      'ARTIST GRADE',
      'EMPLOYEE GRADE',
      'CREW GRADE',
      'OTHER GRADE',
      'OVERALL GRADE',
      'NUMBER OF REPORTS'
    ];

    // Helper function to escape CSV field
    const escapeField = (field) => {
      if (field === null || field === undefined) return '';
      const stringField = String(field);
      
      // Trim BOM characters but preserve internal spaces and line breaks
      const cleanField = stringField.replace(/^\uFEFF/, '');
      
      // If field contains quotes, commas, or newlines, wrap in quotes and escape existing quotes
      if (cleanField.includes('"') || cleanField.includes(',') || cleanField.includes('\n') || cleanField.includes('\r')) {
        // Double the quotes but preserve line breaks
        return `"${cleanField.replace(/"/g, '""')}"`;
      }
      return cleanField;
    };

    // Create CSV content with properly escaped fields
    const csvRows = [
      headers.join(','),
      ...venues.map(venue => [
        escapeField(venue.state),
        escapeField(venue.name),
        escapeField(venue.city),
        escapeField(venue.website),
        escapeField(venue.capacity),
        escapeField(venue.affiliate),
        escapeField(venue.stage),
        escapeField(venue.specSheetLink),
        escapeField(venue.power),
        escapeField(venue.lightingControl),
        escapeField(venue.lightingFixtures),
        escapeField(venue.video),
        escapeField(venue.foh),
        escapeField(venue.pa),
        escapeField(venue.monitors),
        escapeField(venue.backstage),
        escapeField(venue.backline),
        escapeField(venue.merch),
        escapeField(venue.parking),
        escapeField(venue.shorePower),
        escapeField(venue.notes),
        escapeField(venue.mapEmbedLink),
        escapeField(venue.updatedAt ? new Date(venue.updatedAt.seconds * 1000).toISOString() : ''),
        escapeField(venue.approved?.artistGrade),
        escapeField(venue.approved?.employeeGrade),
        escapeField(venue.approved?.crewGrade),
        escapeField(venue.approved?.otherGrade),
        escapeField(venue.approved?.overallGrade),
        escapeField(venue.approved?.reportCount)
      ].join(','))
    ];

    // Create and download the file
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `venues_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper functions for venue processing

  // Enhanced venue name normalization
  const normalizeVenueName = (name) => {
    if (!name) return '';
    
    // Convert to lowercase and remove accents
    let normalized = name.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    
    // Remove special characters but keep spaces and ampersands initially
    normalized = normalized
      .replace(/[^a-z0-9\s&]/g, ' ') // Replace special chars with space, keep &
      .replace(/\s+/g, ' ')         // Normalize multiple spaces to single space
      .trim();                      // Remove leading/trailing spaces
    
    // Remove common prefixes/articles/suffixes
    const wordsToRemove = [
      'the', 'at', 'in', 'of', 'on', 'by', 'and',
      'center', 'centre', 'theater', 'theatre',
      'amphitheater', 'amphitheatre', 'arena',
      'stadium', 'pavilion', 'hall', 'auditorium',
      'complex', 'venue'
    ];
    
    let words = normalized.split(' ');
    
    // Remove common words from start and end
    while (words.length > 0 && wordsToRemove.includes(words[0])) {
      words = words.slice(1);
    }
    while (words.length > 0 && wordsToRemove.includes(words[words.length - 1])) {
      words = words.slice(0, -1);
    }
    
    // Handle common venue type variations and abbreviations
    const venueTypes = {
      'theatre': 'theater',
      'amphitheatre': 'amphitheater',
      'centre': 'center',
      'auditorium': 'theater',
      'pavilion': 'pav',
      'stadium': 'stad',
      'arena': 'arn',
      'concert hall': 'hall',
      'music hall': 'hall',
      'performing arts center': 'pac',
      'performing arts': 'pa',
      'entertainment center': 'ec',
      'entertainment complex': 'ec',
      'civic center': 'cc',
      'civic auditorium': 'ca',
      'memorial auditorium': 'ma',
      'memorial hall': 'mh',
      'convention center': 'cc',
      'event center': 'ec',
      'amphitheatre': 'amp',
      'amphitheater': 'amp',
      'coliseum': 'col',
      'colosseum': 'col',
      'theatre': 'thtr',
      'theater': 'thtr',
      'stadium': 'stad',
      'pavilion': 'pav',
      'arena': 'arn',
      'center': 'ctr',
      'centre': 'ctr',
      'hall': 'hl',
      'auditorium': 'aud',
      'palace': 'pal',
      'garden': 'gdn',
      'gardens': 'gdn',
      'square': 'sq',
      'place': 'pl',
      'point': 'pt',
      'park': 'pk',
      'field': 'fld',
      'grounds': 'grds',
      'house': 'hse',
      'room': 'rm',
      'lounge': 'lng',
      'club': 'clb'
    };
    
    // Replace venue type variations
    words = words.map(word => venueTypes[word] || word);
    
    // Handle special cases for ampersands
    normalized = words.join('')
      .replace(/\s*&\s*/g, 'and')  // Replace & with 'and'
      .replace(/\s+/g, '');        // Remove any remaining spaces
    
    return normalized;
  };

  // New function to check if venues are likely duplicates
  const areVenuesDuplicate = (venue1, venue2) => {
    // Only consider venues duplicates if names match exactly (case-insensitive)
    const exactNameMatch = venue1.name.toLowerCase().trim() === venue2.name.toLowerCase().trim();
    
    if (!exactNameMatch) {
      return false; // If names don't match exactly, they're not duplicates
    }
    
    // Helper function to normalize location data
    const normalizeLocation = (city, state) => {
      const normalizedCity = city ? normalizeVenueName(city) : '';
      const normalizedState = state ? state.toUpperCase().trim() : '';
      return { city: normalizedCity, state: normalizedState };
    };

    // Get locations
    const loc1 = normalizeLocation(venue1.city, venue1.state);
    const loc2 = normalizeLocation(venue2.city, venue2.state);
    
      // Check if locations match
    const locationMatches = loc1.state === loc2.state && 
      (!loc1.city || !loc2.city || loc1.city === loc2.city);
    
    if (!locationMatches) {
      return false; // If locations don't match, they're not duplicates
    }
    
    // Count how many fields match between the venues
    const fieldsToCompare = [
      'capacity', 'affiliate', 'website', 'stage', 'specSheetLink', 
      'power', 'lightingControl', 'lightingFixtures', 'video',
      'foh', 'pa', 'monitors', 'backstage', 'backline', 
      'merch', 'parking', 'shorePower'
    ];
    
    let matchingFields = 0;
    let totalNonEmptyFields = 0;
    
    fieldsToCompare.forEach(field => {
      const val1 = venue1[field];
      const val2 = venue2[field];
      
      if (val1 && val2) {
        totalNonEmptyFields++;
        if (val1.toString().toLowerCase().trim() === val2.toString().toLowerCase().trim()) {
          matchingFields++;
        }
      } else if (val1 || val2) {
        totalNonEmptyFields++;
      }
    });
    
    // Calculate matching percentage (if there are fields to compare)
    const matchPercentage = totalNonEmptyFields > 0 
      ? (matchingFields / totalNonEmptyFields) 
      : 0;
    
    // Require at least 3 matching fields or 50% of non-empty fields to match
    return matchingFields >= 3 || matchPercentage >= 0.5;
  };

  // Update the generateConsistentVenueId function to use the new normalization
  const generateConsistentVenueId = (venue) => {
    const normalizedName = normalizeVenueName(venue.name);
    const normalizedState = venue.state.toUpperCase().trim();
    const normalizedCity = venue.city ? normalizeVenueName(venue.city) : '';
    
    // Include city in ID if available for more uniqueness
    return normalizedCity 
      ? `${normalizedName}_${normalizedCity}_${normalizedState}`.substring(0, 100)
      : `${normalizedName}_${normalizedState}`.substring(0, 100);
  };

  // Intelligently merge venue data, preserving the most information
  const mergeVenueData = (existingData, newData) => {
    const merged = { ...existingData };
    
    // Helper function to determine which value to keep
    const getBestValue = (oldVal, newVal, field) => {
      if (!oldVal && !newVal) return null;
      if (!oldVal) return newVal;
      if (!newVal) return oldVal;

      // For certain fields, prefer the longer value as it might contain more information
      const preferLongerFields = ['description', 'notes', 'specSheetLink', 'mapEmbedLink'];
      if (preferLongerFields.includes(field)) {
        // If new value is substantively different, concatenate both values
        if (oldVal.toLowerCase() !== newVal.toLowerCase() && 
            !oldVal.includes(newVal) && 
            !newVal.includes(oldVal)) {
          return `${oldVal} | ${newVal}`;
        }
        return oldVal.length > newVal.length ? oldVal : newVal;
      }

      // For numeric fields, keep the newer value but validate it's a number
      const numericFields = ['capacity', 'max_capacity', 'theater_set_capacity', 'end_stage_capacity'];
      if (numericFields.includes(field)) {
        const oldNum = parseInt(oldVal);
        const newNum = parseInt(newVal);
        if (!isNaN(newNum)) return newNum;
        if (!isNaN(oldNum)) return oldNum;
        return null;
      }

      // For technical specifications, merge the information if different
      const techSpecFields = ['stage', 'power', 'lightingControl', 'lightingFixtures', 'video', 
                            'foh', 'pa', 'monitors', 'backline', 'backstage', 'merch', 'parking', 'shorePower',
                            'venue_type', 'curtains'];
      if (techSpecFields.includes(field)) {
        if (oldVal === newVal) return oldVal;
        if (oldVal) {
          let oldValUnique = oldVal;

          // check for duplicates and clean up values
          const splitOldVal = oldVal.split(' | ');
          if (splitOldVal.length > 1) {
            // remove duplicates
            const uniqueValues = [...new Set(splitOldVal)];
            // remove entries that are a substring to another entry
            const uniqueWithoutDuplicates = uniqueValues.filter((value, index, self) => 
              self.findIndex(t => t !== value && t.includes(value)) === -1
            );
            
            oldValUnique = uniqueWithoutDuplicates.join(' | ');
          }
          // if the old value vs new value have similarities, return the new value
          // meaning, admin added or deleted some specs to the existing spec
          if (newVal && (oldValUnique.includes(newVal) || newVal.includes(oldValUnique))) return newVal;
          if (!oldVal) return newVal;
          if (!newVal) return oldVal;
        }
        return [oldVal, newVal].filter(Boolean).join(' | ');
      }

      // For arrays, combine and remove duplicates
      if (Array.isArray(oldVal) && Array.isArray(newVal)) {
        return [...new Set([...oldVal, ...newVal])];
      }

      // For objects that aren't arrays, deep merge
      if (typeof oldVal === 'object' && typeof newVal === 'object' && !Array.isArray(oldVal)) {
        return { ...oldVal, ...newVal };
      }

      // For URLs, prefer the one that seems more complete or valid
      const urlFields = ['website'];
      if (urlFields.includes(field)) {
        const isValidUrl = (url) => {
          try {
            new URL(url);
            return true;
          } catch {
            return false;
          }
        };
        if (isValidUrl(newVal)) return newVal;
        if (isValidUrl(oldVal)) return oldVal;
        return oldVal || newVal;
      }

      // For location fields, prefer existing values unless new is different
      const locationFields = ['city', 'state'];
      if (locationFields.includes(field)) {
        // If the old value is UNKNOWN and new has a better value, use new value
        if (oldVal === 'UNKNOWN' && newVal !== 'UNKNOWN') {
          return newVal;
        }
        // Otherwise prefer the existing database value
        return oldVal;
      }

      // For other fields, prefer the newer value but keep old if new is empty
      return newVal || oldVal;
    };

    // Merge each field
    Object.entries(newData).forEach(([key, value]) => {
      if (key === 'id' || key === 'createdAt') return; // Don't merge these fields
      merged[key] = getBestValue(merged[key], value, key);
    });

    // Special handling for photos and media
    if (newData.photos || newData.venue_media) {
      merged.photos = merged.photos || [];
      if (newData.photos) {
        merged.photos = [...new Set([...merged.photos, ...newData.photos])];
      }
      if (newData.venue_media) {
        try {
          const mediaData = typeof newData.venue_media === 'string' 
            ? JSON.parse(newData.venue_media) 
            : newData.venue_media;
          if (mediaData.photos) {
            merged.photos = [...new Set([...merged.photos, ...mediaData.photos])];
          }
        } catch (error) {
          console.error('Error parsing venue media:', error);
        }
      }
    }

    // Keep track of merge history
    merged.mergeHistory = merged.mergeHistory || [];
    merged.mergeHistory.push({
      timestamp: new Date(),
      source: 'CSV Import',
      fields: Object.keys(newData).filter(key => newData[key] && key !== 'id' && key !== 'createdAt')
    });

    return merged;
  };

  const handleFetchVenuePhotos = async () => {
    if (!confirm('This will attempt to fetch photos for venues and may take a while. Continue?')) return;

    setIsFetchingPhotos(true);
    setPhotoFetchProgress(0);
    setVenuePhotos([]); // Initialize venue photos array
    
    const venuesWithoutPhotos = venues.filter(venue => 
      (!venue.photos || venue.photos.length === 0) && 
      !venue.notFoundOnLiveNation
    );
    const targetVenues = testMode ? venuesWithoutPhotos.slice(0, testVenueCount) : venuesWithoutPhotos;
    
    setTotalVenues(targetVenues.length);
    setProcessedVenues(0);

    try {
      const db = getFirestore();
      let success = 0;
      let failed = 0;
      let notFound = 0;
      
      // Process venues in batches of 5
      const BATCH_SIZE = 5;
      const DELAY_BETWEEN_BATCHES = 5000; // 5 seconds between batches
      const RETRY_DELAY = 30000; // 30 seconds after a 429

      for (let i = 0; i < targetVenues.length; i += BATCH_SIZE) {
        const batch = targetVenues.slice(i, i + BATCH_SIZE);
        
        for (const venue of batch) {
          try {
            const photo = await findLiveNationVenuePhoto(venue.name, venue.city, venue.state);
            
            if (photo) {
              const venueRef = doc(db, 'venues', venue.id);
              await updateDoc(venueRef, {
                photos: [{
                  url: photo,
                  name: 'livenation_photo_1'
                }],
                backgroundImage: photo,
                notFoundOnLiveNation: false
              });
              
              // Add to the venuePhotos array to display in UI
              setVenuePhotos(prev => [...prev, { 
                id: venue.id, 
                name: venue.name, 
                photoCount: 1 
              }]);
              
              success++;
            } else {
              const venueRef = doc(db, 'venues', venue.id);
              await updateDoc(venueRef, {
                notFoundOnLiveNation: true,
                notFoundTimestamp: new Date()
              });
              notFound++;
            }
          } catch (error) {
            if (error?.response?.status === 429) {
              // If we hit rate limit, wait and retry the current batch
              console.log('Rate limit hit, waiting before retry...');
              await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
              i -= BATCH_SIZE; // Retry this batch
              continue;
            }
            console.error(`Error processing venue ${venue.name}:`, error);
            failed++;
          }

          setProcessedVenues(prev => prev + 1);
          setPhotoFetchProgress(((processedVenues + 1) / targetVenues.length) * 100);
        }

        // Wait between batches to avoid rate limiting
        if (i + BATCH_SIZE < targetVenues.length) {
          await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES));
        }
      }

      alert(`Photo fetch completed!\nSuccess: ${success}\nNot Found: ${notFound}\nFailed: ${failed}`);
      refreshVenues();
    } catch (error) {
      console.error('Error in photo fetch process:', error);
      alert('Error fetching venue photos. Please try again.');
    } finally {
      setIsFetchingPhotos(false);
    }
  };

  const handleUserAction = async (userId, action) => {
    const db = getFirestore();
    const auth = getAuth();
    const userRef = doc(db, 'users', userId);
    const functions = getFunctions();
    
    switch (action) {
      case 'suspend':
        await updateDoc(userRef, { status: 'suspended' });
        break;
      case 'activate':
        try {
          const userDoc = await getDoc(userRef);
          const userData = userDoc.data();
          
          await updateDoc(userRef, { 
            status: 'active',
            activatedAt: new Date(),
            activatedBy: auth.currentUser?.uid
          });

          // Try to send activation email (non-critical)
          try {
            const handleAccountActivation = httpsCallable(functions, 'handleAccountActivation');
            await handleAccountActivation({ userData: { ...userData, uid: userId } });
          } catch (emailError) {
            console.warn('Email sending failed, but activation succeeded:', emailError);
            // Don't throw error - activation was successful
          }
          
          alert('User activated successfully');
        } catch (error) {
          console.error('Error activating user:', error);
          alert('Error activating user: ' + error.message);
        }
        break;
      case 'makePublic':
        try {
          await updateDoc(userRef, { isPublic: true });
          // Refresh users to update the display
          fetchUsers();
          alert('User profile set to public');
        } catch (error) {
          console.error('Error making user public:', error);
          alert('Error updating user: ' + error.message);
        }
        break;
      case 'makePrivate':
        try {
          await updateDoc(userRef, { isPublic: false });
          // Refresh users to update the display
          fetchUsers();
          alert('User profile set to private');
        } catch (error) {
          console.error('Error making user private:', error);
          alert('Error updating user: ' + error.message);
        }
        break;
      case 'makeCommunityMember':
        try {
          await updateDoc(userRef, { isCommunityMember: true });
          // Refresh users to update the display
          fetchUsers();
          alert('User converted to community member');
        } catch (error) {
          console.error('Error converting to community member:', error);
          alert('Error updating user: ' + error.message);
        }
        break;
      case 'removeCommunityMember':
        try {
          await updateDoc(userRef, { isCommunityMember: false });
          // Refresh users to update the display
          fetchUsers();
          alert('User converted to non-community member');
        } catch (error) {
          console.error('Error removing community member status:', error);
          alert('Error updating user: ' + error.message);
        }
        break;
      case 'delete':
        if (confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
          try {
            // Check if we're deleting the current user
            const isCurrentUser = userId === auth.currentUser.uid;
            
            // Delete from Firestore first
          await deleteDoc(userRef);
            
            // Try to delete from Auth if it's the current user
            if (isCurrentUser) {
              try {
                await auth.currentUser.delete();
                // Only navigate to login if we deleted the current user
                navigation.replace('Login');
              } catch (authError) {
                console.error('Error deleting from Auth:', authError);
              }
            }
            
            alert('User deleted from database successfully');
          } catch (error) {
            console.error('Error deleting user:', error);
            alert('Error deleting user: ' + error.message);
            return;
          }
        }
        break;
    }
    fetchUsers();
  };

  const handleMaintenanceMode = async () => {
    if (maintenanceMode) {
      // Turn off maintenance mode
      const db = getFirestore();
      await updateDoc(doc(db, 'system', 'maintenance'), {
        enabled: false,
        message: '',
        timestamp: new Date()
      });
      setMaintenanceMode(false);
    } else {
      // Show password prompt
      setShowPasswordPrompt(true);
    }
  };

  const confirmMaintenanceMode = async () => {
    try {
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, auth.currentUser.email, adminPassword);
      
      if (userCredential) {
        const db = getFirestore();
        await updateDoc(doc(db, 'system', 'maintenance'), {
          enabled: true,
          message: 'System is currently under maintenance. Please check back later.',
          timestamp: new Date(),
          estimatedDuration: '1 hour'
        });
        setMaintenanceMode(true);
        setShowPasswordPrompt(false);
        setAdminPassword('');
      }
    } catch (error) {
      alert('Invalid password');
    }
  };

  // Helper function to check if user is online (last login within last 15 minutes)
  const isUserOnline = (user) => {
    if (!user.lastLogin) return false;
    const lastLoginTime = user.lastLogin?.toDate ? user.lastLogin.toDate() : new Date(user.lastLogin);
    const now = new Date();
    const diffMinutes = (now - lastLoginTime) / (1000 * 60);
    return diffMinutes <= 15;
  };

  // User list filtering — see AdminUsersList

  const handlePhotoUpload = async (venueId, file, type = 'background') => {
    try {
      setLoading(true);
      const storage = getStorage();
      const db = getFirestore();
      
      // Create a reference to the file in Firebase Storage
      const fileRef = ref(storage, `venues/${venueId}/${type}_${Date.now()}_${file.name}`);
      
      // Upload the file
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);

      // Update Firestore document
      const venueRef = doc(db, 'venues', venueId);
      const venueDoc = await getDoc(venueRef);

      if (venueDoc.exists()) {
        const currentData = venueDoc.data();
        let updateData = {};

        if (type === 'background') {
          updateData = {
            backgroundImage: downloadURL,
            updatedAt: new Date()
          };
        } else {
          const newPhoto = {
            url: downloadURL,
            name: file.name,
            uploadedAt: new Date()
          };

          updateData = {
            photos: [...(currentData.photos || []), newPhoto],
            updatedAt: new Date()
          };
        }

        await updateDoc(venueRef, updateData);

        // Refresh venues in context
      refreshVenues();

      alert('Photo uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  const handleDeletePhotos = async (venueId) => {
    if (!confirm('Are you sure you want to delete all photos for this venue?')) {
      return;
    }

    try {
      const storage = getStorage();
      const db = getFirestore();
      const venue = venues.find(v => v.id === venueId);
      
      // Delete all photos from storage
      if (venue.photos) {
        for (const photo of venue.photos) {
          const photoRef = ref(storage, `venues/${venueId}/${photo.name}`);
          await deleteObject(photoRef);
        }
      }

      // Update venue document
      const venueRef = doc(db, 'venues', venueId);
      await updateDoc(venueRef, {
        photos: [],
        backgroundImage: null
      });

      refreshVenues();
      alert('Photos deleted successfully!');
    } catch (error) {
      console.error('Error deleting photos:', error);
      alert('Error deleting photos. Please try again.');
    }
  };

  const handleMigrateUserIds = async () => {
    if (window.confirm('Are you sure you want to migrate user IDs? This will update all user document IDs to match their Auth UIDs.')) {
      try {
        await migrateUserIds();
        alert('Migration completed successfully');
      } catch (error) {
        console.error('Migration error:', error);
        alert('Error during migration. Check console for details.');
      }
    }
  };

  const handleDevModeUserChange = async (email) => {
    try {
      // Debug logging
      console.log('Development configuration:', {
        devAdminEmail: Constants.expoConfig.extra.devAdminEmail,
        devUserEmail: Constants.expoConfig.extra.devUserEmail,
        devPassword: Constants.expoConfig.extra.devPassword ? '[EXISTS]' : '[MISSING]'
      });

      // Only allow switching between approved dev test users
      const allowedDevUsers = [
        Constants.expoConfig.extra.devAdminEmail,
        Constants.expoConfig.extra.devUserEmail
      ];

      console.log('Allowed users:', allowedDevUsers);
      console.log('Attempting to switch to:', email);

      if (!allowedDevUsers.includes(email)) {
        alert('You can only switch between approved dev test users.');
        return;
      }

      // Sign out current user first
      const auth = getAuth();
      await signOut(auth);

      // Clear any existing dev mode user
      localStorage.removeItem('devModeUser');

      // Set the new dev mode user
      setDevModeEmail(email);
      setDevModeUser(email);
      
      // Get password from config
      const password = Constants.expoConfig.extra.devPassword;
      if (!password) {
        throw new Error('Development password not found in configuration');
      }

      // Sign in with the new user
      await signInWithEmailAndPassword(auth, email, password);

      // Store the new dev mode user
      localStorage.setItem('devModeUser', email);

      // Show feedback
      alert(`Switched to user: ${email}`);
    } catch (error) {
      console.error('Error switching dev user:', error);
      if (error.code === 'auth/missing-password') {
        alert('Error: Development password not properly configured.');
      } else {
        alert(`Failed to switch user: ${error.message}`);
      }
    }
  };

  const handleUpdateCSV = async () => {
    if (!confirm('This will update the VenueDatabase.csv with photo data from Firestore. Continue?')) return;

    try {
      const functions = getFunctions();
      const updateCSV = httpsCallable(functions, 'updateVenueCSV');

      setLoading(true);
      const result = await updateCSV();
      
      if (result.data.success) {
        alert(`Successfully updated CSV with photo data for ${result.data.venuesUpdated} venues`);
      } else {
        throw new Error(result.data.error || 'Failed to update CSV');
      }
    } catch (error) {
      console.error('Error updating CSV:', error);
      alert('Error updating CSV: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateVenueData = async () => {
    if (!confirm('This will regenerate the venue data JSON file. Continue?')) return;
    
    setLoading(true);
    try {
      const data = await generateVenueData();
      // Save to localStorage
      localStorage.setItem('venueData', JSON.stringify(data));
      alert('Venue data successfully regenerated!');
      refreshVenues();
    } catch (error) {
      console.error('Error regenerating venue data:', error);
      alert('Error regenerating venue data');
    } finally {
      setLoading(false);
    }
  };

  const renderPhotoUploadButtons = (venue) => {
    return (
      <View style={adminStyles.uploadButtonsContainer}>
        <TouchableOpacity
          style={adminStyles.uploadButton}
          onPress={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.onchange = (e) => {
              const file = e.target.files[0];
              if (file) {
                handlePhotoUpload(venue.id, file, 'background');
              }
            };
            input.click();
          }}
        >
          <Text style={adminStyles.uploadButtonText}>
            {venue.backgroundImage ? 'Update Background' : 'Add Background'}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={adminStyles.uploadButton}
          onPress={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.onchange = (e) => {
              const file = e.target.files[0];
              if (file) {
                handlePhotoUpload(venue.id, file, 'media');
              }
            };
            input.click();
          }}
        >
          <Text style={adminStyles.uploadButtonText}>Add Media Photo</Text>
        </TouchableOpacity>
      </View>
    );
  };


  const renderVenueContent = () => {
    // return (
    //   <View style={{ flex: 1, width: '100%' }}>
    //     {renderVenueFilters()}
    //     <ScrollView style={{ flex: 1, width: '100%' }}>
    //       <View style={adminStyles.venueGrid}>
    //         {getFilteredVenues().map(venue => renderVenue(venue))}
    //       </View>
    //       {renderPaginationControls(getFilteredVenues().length)}
    //     </ScrollView>
    //   </View>
    
    return (
      <VenuesList
        venues={venues}
        handlePhotoUpload={handlePhotoUpload}
        handleDeletePhotos={handleDeletePhotos}
      />
    );
  };

  // Helper function to send a Team VRC notification message to a user
  const sendTeamVRCMessage = async (userEmail, messageText) => {
    try {
      const db = getFirestore();
      const TEAM_VRC_ID = 'team_vrc'; // Special ID for Team VRC
      
      // Find user by email to get their UID
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', userEmail)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      if (usersSnapshot.empty) {
        console.warn(`User not found for email: ${userEmail}`);
        return;
      }
      
      const userDoc = usersSnapshot.docs[0];
      const userId = userDoc.id;
      
      // Find or create conversation between Team VRC and the user
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', TEAM_VRC_ID),
        where('type', '==', 'direct')
      );
      const conversationsSnapshot = await getDocs(conversationsQuery);
      
      let conversationId = null;
      let existingConversation = conversationsSnapshot.docs.find(doc => {
        const data = doc.data();
        return data.participants.includes(userId) && data.participants.length === 2;
      });
      
      if (existingConversation) {
        conversationId = existingConversation.id;
        // Ensure it's marked as a system conversation
        const existingData = existingConversation.data();
        if (!existingData.isSystemConversation) {
          await updateDoc(doc(db, 'conversations', conversationId), {
            isSystemConversation: true
          });
        }
      } else {
        // Create new conversation
        const conversationData = {
          participants: [TEAM_VRC_ID, userId],
          type: 'direct',
          name: null,
          lastMessage: null,
          lastActivity: serverTimestamp(),
          createdAt: serverTimestamp(),
          createdBy: TEAM_VRC_ID,
          unreadCounts: {
            [TEAM_VRC_ID]: 0,
            [userId]: 0
          },
          isActive: true,
          isSystemConversation: true // Mark as system conversation (no replies allowed)
        };
        
        const conversationRef = await addDoc(collection(db, 'conversations'), conversationData);
        conversationId = conversationRef.id;
      }
      
      // Send the message
      const messageData = {
        text: messageText,
        senderId: TEAM_VRC_ID,
        senderName: 'Team VRC',
        senderPhoto: '', // You can add a Team VRC logo URL here if you have one
        timestamp: serverTimestamp(),
        type: 'system', // System message type - prevents replies
        readBy: [TEAM_VRC_ID],
        edited: false,
        isSystemMessage: true // Additional flag to prevent replies
      };
      
      // Add message to conversation
      await addDoc(collection(db, 'conversations', conversationId, 'messages'), messageData);
      
      // Update conversation's last message and activity
      await updateDoc(doc(db, 'conversations', conversationId), {
        lastMessage: {
          text: messageData.text,
          senderId: messageData.senderId,
          timestamp: messageData.timestamp,
          type: messageData.type
        },
        lastActivity: serverTimestamp()
      });
      
      // Increment unread count for the user (separate update to handle increment properly)
      const conversationDoc = await getDoc(doc(db, 'conversations', conversationId));
      const currentUnreadCounts = conversationDoc.data()?.unreadCounts || {};
      await updateDoc(doc(db, 'conversations', conversationId), {
        [`unreadCounts.${userId}`]: (currentUnreadCounts[userId] || 0) + 1
      });
      
      console.log(`✅ Team VRC message sent to ${userEmail}`);
    } catch (error) {
      console.error('Error sending Team VRC message:', error);
      // Don't throw - we don't want to fail the approval/rejection if messaging fails
    }
  };

  const handleApproveReport = async (report) => {
    setIsApprovingReport(true);
    setApprovingReportId(report.id);
    try {
      const db = getFirestore();
      const auth = getAuth();

      if (reportType === 'venue_submission') {
        const embedCoords = await attachEmbedCoordinatesToVenuePayload(report);
        // Create new venue document with data from the submission
        const venueData = {
          name: report.name,
          address: report.address || '',
          city: report.city || '',
          state: report.state || '',
          capacity: report.capacity || '',
          website: report.website || '',
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: report.submittedBy?.uid || auth.currentUser?.uid,
          status: 'active',
          // Technical specifications
          stage: report.stage || '',
          power: report.power || '',
          lightingControl: report.lightingControl || '',
          lightingFixtures: report.lightingFixtures || '',
          video: report.video || '',
          foh: report.foh || '',
          pa: report.pa || '',
          monitors: report.monitors || '',
          // Additional fields
          affiliate: report.affiliate || '',
          backline: report.backline || '',
          backstage: report.backstage || '',
          merch: report.merch || '',
          parking: report.parking || '',
          shorePower: report.shorePower || '',
          specSheetLink: report.specSheetLink || '',
          mapEmbedLink: report.mapEmbedLink || '',
          notes: report.notes || '',
          ...embedCoords,
        };

        // Add the venue to the venues collection
        const venueRef = await addDoc(collection(db, 'venues'), venueData);

        // Update submission status
        const submissionRef = doc(db, 'new_venues', report.id);
        await updateDoc(submissionRef, {
          status: 'approved',
          venueId: venueRef.id,
          approvedAt: new Date(),
          approvedBy: auth.currentUser?.uid
        });

        const venueContributorUid = report.userId || report.submittedBy?.uid || report.createdBy;
        if (venueContributorUid) {
          try {
            await creditUserFundEarn({
              uid: venueContributorUid,
              amountCents: creditEconomy.creditEarnCentsForApproval({ category: report.category }),
              label: `Approved venue: ${report.name || 'New venue'} (+${creditEconomy.creditEarnUnitsForApproval({ category: report.category })} Credits)`,
              sourceCollection: 'new_venues',
              sourceId: report.id,
            });
          } catch (fundErr) {
            console.warn('Funds credit failed (venue):', fundErr);
          }
        }

        // Refresh the venues list
        refreshVenues();
        
        // Send notification message to user if they submitted it
        if (report.submittedBy?.email || report.userEmail) {
          const userEmail = report.submittedBy?.email || report.userEmail;
          const messageText = `Your venue submission "${report.name}" has been approved! The venue has been added to the database.`;
          await sendTeamVRCMessage(userEmail, messageText);
        }
        
        alert('Venue submission approved successfully!');
      } else if (reportType === 'venue_document_submission') {
        const documents = Array.isArray(report.documents) ? report.documents : [];
        if (!report.venueId || documents.length === 0) {
          throw new Error('This submission is missing its venue or documents.');
        }
        const venueRef = doc(db, 'venues', report.venueId);
        const submissionRef = doc(db, 'venue_document_submissions', report.id);
        const batch = writeBatch(db);
        batch.update(venueRef, {
          uploads: arrayUnion(...documents.map((document) => ({
            ...document,
            submissionId: report.id,
          }))),
        });
        batch.update(submissionRef, {
          status: 'approved',
          approvedAt: new Date(),
          approvedBy: auth.currentUser?.uid,
          rejectedAt: null,
          rejectedBy: null,
          denyReason: null,
        });
        await batch.commit();

        if (report.userEmail) {
          await sendTeamVRCMessage(
            report.userEmail,
            `Your document upload for ${report.venueName || 'the venue'} has been approved and is now listed under Links & Manuals.`
          );
        }
        alert('Venue documents approved and published!');
      } else if (reportType === 'venue_archive_request') {
        if (!report.venueId) throw new Error('This request is missing its venue.');
        const venueRef = doc(db, 'venues', report.venueId);
        const venueSnapshot = await getDoc(venueRef);
        if (!venueSnapshot.exists()) {
          throw new Error('This venue is no longer active (it may already be archived).');
        }
        const batch = writeBatch(db);
        batch.set(doc(db, 'archived_venues', report.venueId), {
          ...venueSnapshot.data(),
          archivedAt: new Date(),
          archiveReason: report.reason || null,
          archiveRequestId: report.id,
        });
        batch.delete(venueRef);
        batch.update(doc(db, 'venue_archive_requests', report.id), {
          status: 'approved',
          approvedAt: new Date(),
          approvedBy: auth.currentUser?.uid,
          rejectedAt: null,
          rejectedBy: null,
          denyReason: null,
        });
        await batch.commit();
        refreshVenues();

        if (report.userEmail) {
          await sendTeamVRCMessage(
            report.userEmail,
            `Thanks for letting us know about ${report.venueName || 'the venue'}. Your archive request was approved and the venue has been archived.`
          );
        }
        alert('Venue archived!');
      } else if (reportType === 'resource_submission') {
        const submissionRef = doc(db, 'resource_submissions', report.id);
        const approvalUpdates = {
          status: 'approved',
          approvedAt: new Date(),
          approvedBy: auth.currentUser?.uid,
          rejectedAt: null,
          rejectedBy: null,
          denyReason: null,
        };

        // Merch submissions publish into the existing screenprinters collection.
        // Keep the published document id on the submission so re-approval is idempotent.
        if ((report.submissionType === 'merch' || report.category === 'merch') &&
            !report.publishedScreenprinterId) {
          const screenprinterRef = await addDoc(collection(db, 'screenprinters'), {
            name: report.resourceName || '',
            website: report.url || '',
            imageUrl: report.imageUrl || null,
            address: report.venueName || '',
            city: report.venueCity || '',
            state: report.venueState || '',
            latitude: report.latitude ?? null,
            longitude: report.longitude ?? null,
            services: report.services || '',
            notes: report.notes || '',
            phone: report.phone || '',
            email: report.email || '',
            eco: Boolean(report.eco),
            mapEmbedLink: report.mapEmbedLink || null,
            googlePlaceId: report.googlePlaceId || null,
            googleCid: report.googleCid || null,
            sourceSubmissionId: report.id,
            createdAt: new Date(),
            approvedBy: auth.currentUser?.uid || null,
          });
          approvalUpdates.publishedScreenprinterId = screenprinterRef.id;
        }

        await updateDoc(submissionRef, approvalUpdates);

        const resourceContributorUid = report.userId || report.submittedBy?.uid || report.createdBy;
        if (resourceContributorUid) {
          try {
            await creditUserFundEarn({
              uid: resourceContributorUid,
              amountCents: creditEconomy.creditEarnCentsForApproval({ category: report.category, submissionType: report.submissionType }),
              label: `Approved ${report.submissionType === 'merch' || report.category === 'merch' ? 'merch' : 'resource'}: ${report.resourceName || 'Submission'} (+${creditEconomy.creditEarnUnitsForApproval({ category: report.category, submissionType: report.submissionType })} Credits)`,
              sourceCollection: 'resource_submissions',
              sourceId: report.id,
            });
          } catch (fundErr) {
            console.warn('Funds credit failed (resource):', fundErr);
          }
        }

        if (report.userEmail) {
          const scopeLabel = report.scope === 'state' && report.venueState
            ? `all venues in ${report.venueState}`
            : report.scope === 'city' && report.venueCity
              ? `all venues in ${report.venueCity}${report.venueState ? `, ${report.venueState}` : ''}`
              : (report.venueName || 'the venue');
          await sendTeamVRCMessage(
            report.userEmail,
            `Your resource "${report.resourceName}" for ${scopeLabel} has been approved!`
          );
        }

        alert(
          report.submissionType === 'merch' || report.category === 'merch'
            ? 'Merch shop approved and added to screenprinters!'
            : 'Resource submission approved successfully!'
        );
      } else if (reportType === 'spec_flag') {
        const submissionRef = doc(db, 'venue_spec_flags', report.id);
        await updateDoc(submissionRef, {
          status: 'approved',
          approvedAt: new Date(),
          approvedBy: auth.currentUser?.uid,
          rejectedAt: null,
          rejectedBy: null,
          denyReason: null,
        });

        const flagContributorUid = report.userId || report.submittedBy?.uid || report.createdBy;
        if (flagContributorUid) {
          try {
            await creditUserFundEarn({
              uid: flagContributorUid,
              amountCents: creditEconomy.creditEarnCentsForApproval({ category: report.category }),
              label: `Approved ${report.category === 'accessibility' ? 'accessibility' : 'spec'} note: ${report.venueName || 'Venue'} (+${creditEconomy.creditEarnUnitsForApproval({ category: report.category })} Credits)`,
              sourceCollection: 'venue_spec_flags',
              sourceId: report.id,
            });
          } catch (fundErr) {
            console.warn('Funds credit failed (spec flag):', fundErr);
          }
        }

        if (report.userEmail) {
          const categoryLabel = report.categoryLabel || getResourceCategoryLabel(report.category);
          await sendTeamVRCMessage(
            report.userEmail,
            `Your ${categoryLabel || 'category'} note for ${report.venueName || 'the venue'} has been reviewed. Thank you!`
          );
        }

        alert('Spec flag marked as reviewed.');
      } else if (reportType === 'venue_interest_tag') {
        const submissionRef = doc(db, 'venue_interest_tags', report.id);
        await updateDoc(submissionRef, {
          status: 'approved',
          approvedAt: new Date(),
          approvedBy: auth.currentUser?.uid,
          rejectedAt: null,
          rejectedBy: null,
          denyReason: null,
        });

        if (report.userEmail) {
          const interestList = Array.isArray(report.interests) ? report.interests.join(', ') : 'interests';
          await sendTeamVRCMessage(
            report.userEmail,
            `Your interest tags for ${report.venueName || 'the venue'} (${interestList}) have been approved. Thank you!`
          );
        }

        alert('Venue interest tags approved.');
      } else if (report.type === 'verification') {
        // Handle verification reports...
        // ... existing verification handling code ...
      } else {
        // Handle venue report approval
        const venueRef = doc(db, 'venues', report.venueId);
        const reportRef = doc(db, 'venue_reports', report.id);

        const reportsQuery = query(
          collection(db, 'venue_reports'),
          where('venueId', '==', report.venueId),
          where('status', '==', 'approved')
        );
        const reportsSnapshot = await getDocs(reportsQuery);
        // Convert DocumentSnapshots to actual report data and add the current report
        const approvedReports = [...reportsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })), report];
        const gradedApprovedReports = approvedReports.filter((r) => !r.isTechSpecsOnly);

        // Calculate new grades without this report
        const gradesByType = calculateGradesByUserType(gradedApprovedReports);
        const overallGrade = calculateOverallGrade(gradesByType);

        await updateDoc(venueRef, {
          'approved.reportCount': gradedApprovedReports.length,
          'approved.artistGrade': gradesByType.artistGrade,
          'approved.employeeGrade': gradesByType.employeeGrade,
          'approved.crewGrade': gradesByType.crewGrade,
          'approved.otherGrade': gradesByType.otherGrade,
          'approved.overallGrade': overallGrade
        });

        await updateDoc(reportRef, {
          status: 'approved',
          approvedAt: new Date(),
          approvedBy: auth.currentUser?.uid,
          rejectedAt: null,
          rejectedBy: null
        });

        // Employee grades earn contribution Credits (other report roles don't, yet).
        const graderUid = report.userId || report.submittedBy?.uid || report.createdBy;
        const isEmployeeGrade = !report.isTechSpecsOnly && String(report.userType || '').toLowerCase() === 'employee';
        if (graderUid && isEmployeeGrade) {
          try {
            await creditUserFundEarn({
              uid: graderUid,
              amountCents: creditEconomy.creditEarnCentsForApproval({}),
              label: `Approved employee grade: ${report.venueName || 'Venue'} (+${creditEconomy.creditEarnUnitsForApproval({})} Credits)`,
              sourceCollection: 'venue_reports',
              sourceId: report.id,
            });
          } catch (fundErr) {
            console.warn('Funds credit failed (employee grade):', fundErr);
          }
        }

        // Send notification message to user
        if (report.userEmail) {
          const reportType = report.isTechSpecsOnly ? 'tech specs' : 'venue report';
          const venueName = report.venueName || 'the venue';
          const messageText = `Your ${reportType} for ${venueName} has been approved! Thank you for your participation.`;
          await sendTeamVRCMessage(report.userEmail, messageText);
          
          // Send email notification
          try {
            const functions = getFunctions();
            const handleEmail = httpsCallable(functions, 'handleEmail');
            await handleEmail({
              template: 'venue_report_approved',
              recipient: report.userEmail,
              emailData: {
                userName: report.userName || report.submittedBy?.name || null,
                venueName: venueName,
                reportType: reportType,
                venueId: report.venueId || null
              }
            });
          } catch (emailError) {
            console.warn('Email sending failed, but approval succeeded:', emailError);
            // Don't throw error - approval was successful
          }
        }
      }

      // Reset loading state before reloading reports
      setIsApprovingReport(false);
      setApprovingReportId(null);
      
      loadReports();
    } catch (error) {
      console.error('Error approving report:', error);
      alert('Error approving report: ' + error.message);
      // Reset loading state on error too
      setIsApprovingReport(false);
      setApprovingReportId(null);
    }
  };

  const handleRejectReport = (report) => {
    setRejectingReport(report);
    setDenyReason('');
    setShowRejectModal(true);
  };

  const confirmRejectReport = async () => {
    if (!rejectingReport) return;

    setIsRejectingReport(true);
    try {
      const db = getFirestore();
      const auth = getAuth();

      if (reportType === 'venue_submission') {
        // Update submission status
        const submissionRef = doc(db, 'new_venues', rejectingReport.id);
        await updateDoc(submissionRef, {
          status: 'rejected',
          rejectedAt: new Date(),
          rejectedBy: auth.currentUser?.uid,
          denyReason: denyReason.trim() || null
        });

        alert('Venue submission rejected successfully!');
      } else if (reportType === 'venue_document_submission') {
        const submissionRef = doc(db, 'venue_document_submissions', rejectingReport.id);
        await updateDoc(submissionRef, {
          status: 'rejected',
          rejectedAt: new Date(),
          rejectedBy: auth.currentUser?.uid,
          denyReason: denyReason.trim() || null,
        });

        if (rejectingReport.userEmail) {
          let messageText = `Your document upload for ${rejectingReport.venueName || 'the venue'} was declined.`;
          if (denyReason.trim()) messageText += ` Reason: ${denyReason.trim()}`;
          await sendTeamVRCMessage(rejectingReport.userEmail, messageText);
        }
        alert('Venue document submission declined successfully!');
      } else if (reportType === 'venue_archive_request') {
        await updateDoc(doc(db, 'venue_archive_requests', rejectingReport.id), {
          status: 'rejected',
          rejectedAt: new Date(),
          rejectedBy: auth.currentUser?.uid,
          denyReason: denyReason.trim() || null,
        });

        if (rejectingReport.userEmail) {
          let messageText = `Your archive request for ${rejectingReport.venueName || 'the venue'} was declined.`;
          if (denyReason.trim()) messageText += ` Reason: ${denyReason.trim()}`;
          await sendTeamVRCMessage(rejectingReport.userEmail, messageText);
        }
        alert('Venue archive request declined.');
      } else if (reportType === 'resource_submission') {
        const submissionRef = doc(db, 'resource_submissions', rejectingReport.id);
        await updateDoc(submissionRef, {
          status: 'rejected',
          rejectedAt: new Date(),
          rejectedBy: auth.currentUser?.uid,
          denyReason: denyReason.trim() || null,
        });

        if (rejectingReport.userEmail) {
          let messageText = `Your resource "${rejectingReport.resourceName}" has been declined.`;
          if (denyReason.trim()) {
            messageText += ` Reason: ${denyReason.trim()}`;
          }
          await sendTeamVRCMessage(rejectingReport.userEmail, messageText);
        }

        alert('Resource submission declined successfully!');
      } else if (reportType === 'spec_flag') {
        const submissionRef = doc(db, 'venue_spec_flags', rejectingReport.id);
        await updateDoc(submissionRef, {
          status: 'rejected',
          rejectedAt: new Date(),
          rejectedBy: auth.currentUser?.uid,
          denyReason: denyReason.trim() || null,
        });

        if (rejectingReport.userEmail) {
          const categoryLabel = rejectingReport.categoryLabel || getResourceCategoryLabel(rejectingReport.category);
          let messageText = `Your ${categoryLabel || 'category'} note for ${rejectingReport.venueName || 'the venue'} could not be accepted.`;
          if (denyReason.trim()) {
            messageText += ` Reason: ${denyReason.trim()}`;
          }
          await sendTeamVRCMessage(rejectingReport.userEmail, messageText);
        }

        alert('Spec flag declined successfully!');
      } else if (reportType === 'venue_interest_tag') {
        const submissionRef = doc(db, 'venue_interest_tags', rejectingReport.id);
        await updateDoc(submissionRef, {
          status: 'rejected',
          rejectedAt: new Date(),
          rejectedBy: auth.currentUser?.uid,
          denyReason: denyReason.trim() || null,
        });

        if (rejectingReport.userEmail) {
          const interestList = Array.isArray(rejectingReport.interests)
            ? rejectingReport.interests.join(', ')
            : 'interests';
          let messageText = `Your interest tags for ${rejectingReport.venueName || 'the venue'} (${interestList}) could not be accepted.`;
          if (denyReason.trim()) {
            messageText += ` Reason: ${denyReason.trim()}`;
          }
          await sendTeamVRCMessage(rejectingReport.userEmail, messageText);
        }

        alert('Venue interest tags declined successfully!');
      } else if (rejectingReport.type === 'verification') {
        // Update user's verification status
        const userRef = doc(db, 'users', rejectingReport.userId);
        await updateDoc(userRef, {
          verificationStatus: null
        });

        // Update report status
        const reportRef = doc(db, 'user_reports', rejectingReport.id);
        await updateDoc(reportRef, {
          status: 'rejected',
          rejectedAt: new Date(),
          rejectedBy: auth.currentUser?.uid,
          denyReason: denyReason.trim() || null
        });

        // Send notification message to user
        if (rejectingReport.userEmail) {
          let messageText = 'Your verification request has been rejected.';
          if (denyReason.trim()) {
            messageText += ` Reason: ${denyReason.trim()}`;
          }
          await sendTeamVRCMessage(rejectingReport.userEmail, messageText);
        }
      } else {
        // Handle venue report rejection
        const reportRef = doc(db, 'venue_reports', rejectingReport.id);
        await updateDoc(reportRef, {
          status: 'rejected',
          rejectedAt: new Date(),
          rejectedBy: auth.currentUser?.uid,
          denyReason: denyReason.trim() || null
        });

        // Send notification message to user
        if (rejectingReport.userEmail) {
          const reportType = rejectingReport.isTechSpecsOnly ? 'tech specs' : 'venue report';
          const venueName = rejectingReport.venueName || 'the venue';
          let messageText = `Your ${reportType} for ${venueName} has been rejected.`;
          if (denyReason.trim()) {
            messageText += ` Reason: ${denyReason.trim()}`;
          }
          await sendTeamVRCMessage(rejectingReport.userEmail, messageText);
          
          // Send email notification
          try {
            const functions = getFunctions();
            const handleEmail = httpsCallable(functions, 'handleEmail');
            await handleEmail({
              template: 'venue_report_rejected',
              recipient: rejectingReport.userEmail,
              emailData: {
                userName: rejectingReport.userName || rejectingReport.submittedBy?.name || null,
                venueName: venueName,
                reportType: reportType,
                venueId: rejectingReport.venueId || null,
                denyReason: denyReason.trim() || null
              }
            });
          } catch (emailError) {
            console.warn('Email sending failed, but rejection succeeded:', emailError);
            // Don't throw error - rejection was successful
          }
        }
      }

      setShowRejectModal(false);
      setRejectingReport(null);
      setDenyReason('');
      
      // Reset loading state before reloading reports
      setIsRejectingReport(false);
      
      loadReports();
    } catch (error) {
      console.error('Error rejecting report:', error);
      alert('Error rejecting report: ' + error.message);
      // Reset loading state on error too
      setIsRejectingReport(false);
    }
  };

  const handleRevertToPending = async (report) => {
    try {
      const db = getFirestore();

      if (reportType === 'venue_submission') {
        // Update submission status
        const submissionRef = doc(db, 'new_venues', report.id);
        await updateDoc(submissionRef, {
          status: 'pending',
          approvedAt: null,
          rejectedAt: null,
          approvedBy: null,
          rejectedBy: null
        });

        alert('Venue submission reverted to pending successfully!');
      } else if (reportType === 'venue_document_submission') {
        const submissionRef = doc(db, 'venue_document_submissions', report.id);
        if (report.status === 'approved' && report.venueId) {
          const venueRef = doc(db, 'venues', report.venueId);
          const venueSnapshot = await getDoc(venueRef);
          if (venueSnapshot.exists()) {
            const uploads = Array.isArray(venueSnapshot.data().uploads) ? venueSnapshot.data().uploads : [];
            const batch = writeBatch(db);
            batch.update(venueRef, {
              uploads: uploads.filter((upload) => upload.submissionId !== report.id),
            });
            batch.update(submissionRef, {
              status: 'pending',
              approvedAt: null,
              rejectedAt: null,
              approvedBy: null,
              rejectedBy: null,
              denyReason: null,
            });
            await batch.commit();
          } else {
            throw new Error('The venue for this document submission no longer exists.');
          }
        } else {
          await updateDoc(submissionRef, {
            status: 'pending',
            approvedAt: null,
            rejectedAt: null,
            approvedBy: null,
            rejectedBy: null,
            denyReason: null,
          });
        }
        alert('Venue document submission reverted to pending successfully!');
      } else if (reportType === 'venue_archive_request') {
        const requestRef = doc(db, 'venue_archive_requests', report.id);
        const batch = writeBatch(db);
        if (report.status === 'approved' && report.venueId) {
          // Restore the venue that this request archived
          const archivedRef = doc(db, 'archived_venues', report.venueId);
          const archivedSnapshot = await getDoc(archivedRef);
          if (archivedSnapshot.exists()) {
            const { archived, archivedAt, archiveReason, archiveRequestId, ...restoredVenue } = archivedSnapshot.data();
            batch.set(doc(db, 'venues', report.venueId), restoredVenue);
            batch.delete(archivedRef);
          }
        }
        batch.update(requestRef, {
          status: 'pending',
          approvedAt: null,
          rejectedAt: null,
          approvedBy: null,
          rejectedBy: null,
          denyReason: null,
        });
        await batch.commit();
        if (report.status === 'approved') refreshVenues();
        alert('Venue archive request reverted to pending (venue restored if it was archived).');
      } else if (reportType === 'resource_submission') {
        const submissionRef = doc(db, 'resource_submissions', report.id);
        await updateDoc(submissionRef, {
          status: 'pending',
          approvedAt: null,
          rejectedAt: null,
          approvedBy: null,
          rejectedBy: null,
          denyReason: null,
        });

        alert('Resource submission reverted to pending successfully!');
      } else if (reportType === 'spec_flag') {
        const submissionRef = doc(db, 'venue_spec_flags', report.id);
        await updateDoc(submissionRef, {
          status: 'pending',
          approvedAt: null,
          rejectedAt: null,
          approvedBy: null,
          rejectedBy: null,
          denyReason: null,
        });

        alert('Spec flag reverted to pending successfully!');
      } else if (reportType === 'venue_interest_tag') {
        const submissionRef = doc(db, 'venue_interest_tags', report.id);
        await updateDoc(submissionRef, {
          status: 'pending',
          approvedAt: null,
          rejectedAt: null,
          approvedBy: null,
          rejectedBy: null,
          denyReason: null,
        });

        alert('Venue interest tags reverted to pending successfully!');
      } else if (report.type === 'verification') {
        // Update report status
        const reportRef = doc(db, 'user_reports', report.id);
        await updateDoc(reportRef, {
          status: 'pending',
          approvedAt: null,
          rejectedAt: null,
          approvedBy: null,
          rejectedBy: null
        });
      } else {
        // Handle venue report reversion
        const reportRef = doc(db, 'venue_reports', report.id);
        const venueRef = doc(db, 'venues', report.venueId);

        // Get all approved reports for this venue EXCEPT the one being reverted
        const reportsQuery = query(
          collection(db, 'venue_reports'),
          where('venueId', '==', report.venueId),
          where('status', '==', 'approved')
        );
        const reportsSnapshot = await getDocs(reportsQuery);
        const approvedReports = reportsSnapshot.docs
          .map(doc => ({
            ...doc.data(),
            id: doc.id
          }))
          .filter(r => r.id !== report.id); // Exclude the report being reverted
        const gradedApprovedReports = approvedReports.filter((r) => !r.isTechSpecsOnly);

        // Calculate new grades without this report
        const gradesByType = calculateGradesByUserType(gradedApprovedReports);
        const overallGrade = calculateOverallGrade(gradesByType);

        // Update venue document with new grades
        await updateDoc(venueRef, {
          'approved.reportCount': gradedApprovedReports.length,
          'approved.artistGrade': gradesByType.artistGrade,
          'approved.employeeGrade': gradesByType.employeeGrade,
          'approved.crewGrade': gradesByType.crewGrade,
          'approved.otherGrade': gradesByType.otherGrade,
          'approved.overallGrade': overallGrade
        });

        // Update report status
        await updateDoc(reportRef, {
          status: 'pending',
          approvedAt: null,
          rejectedAt: null,
          approvedBy: null,
          rejectedBy: null
        });
      }

      loadReports();
    } catch (error) {
      console.error('Error reverting report:', error);
      alert('Error reverting report: ' + error.message);
    }
  };

  // Helper function to merge tech specs data (similar to mergeVenueData logic)
  const mergeTechSpecs = (existingValue, newValue) => {
    if (!existingValue && !newValue) return null;
    if (!existingValue) return newValue;
    if (!newValue) return existingValue;
    if (existingValue === newValue) return existingValue;

    // Check if new value is a substring of existing (or vice versa)
    // If so, return the longer/more complete value
    if (existingValue.includes(newValue)) return existingValue;
    if (newValue.includes(existingValue)) return newValue;

    // Check for duplicates in existing value (if it has | separator)
    let existingUnique = existingValue;
    if (existingValue.includes(' | ')) {
      const splitExisting = existingValue.split(' | ');
      // Remove duplicates
      const uniqueValues = [...new Set(splitExisting)];
      // Remove entries that are a substring of another entry
      const uniqueWithoutDuplicates = uniqueValues.filter((value, index, self) => 
        self.findIndex(t => t !== value && t.includes(value)) === -1
      );
      existingUnique = uniqueWithoutDuplicates.join(' | ');
      
      // Check again if new value is substring after cleanup
      if (existingUnique.includes(newValue)) return existingUnique;
      if (newValue.includes(existingUnique)) return newValue;
    }

    // If different, append with separator
    return `${existingUnique} | ${newValue}`;
  };

  const handleHideLiveCommunityReport = async (report) => {
    if (!report?.id) return;
    setIsRejectingReport(true);
    try {
      const db = getFirestore();
      const auth = getAuth();
      await updateDoc(doc(db, 'venue_reports', report.id), {
        status: LIVE_COMMUNITY_HIDDEN_STATUS,
        hiddenAt: new Date(),
        hiddenBy: auth.currentUser?.uid || null,
      });
      alert('LIVE submission hidden from the public venue page.');
      loadReports();
    } catch (error) {
      console.error('Error hiding LIVE community report:', error);
      alert('Error hiding LIVE submission: ' + error.message);
    } finally {
      setIsRejectingReport(false);
    }
  };

  const handlePromoteLiveToVrc = async (report) => {
    if (!report?.id || !report?.venueId) return;
    setIsApprovingReport(true);
    setApprovingReportId(report.id);
    try {
      const db = getFirestore();
      const auth = getAuth();
      const venueRef = doc(db, 'venues', report.venueId);
      const venueDoc = await getDoc(venueRef);
      if (!venueDoc.exists()) {
        alert('Venue not found.');
        return;
      }
      const venueData = venueDoc.data() || {};
      const updates = {};
      LIVE_TECH_SPEC_FIELDS.forEach((field) => {
        const newValue = String(report[field] || '').trim();
        if (!newValue) return;
        const merged = mergeTechSpecs(String(venueData[field] || '').trim(), newValue);
        if (merged) updates[field] = merged;
      });
      if (!Object.keys(updates).length) {
        alert('No spec fields to promote.');
        return;
      }
      await updateDoc(venueRef, updates);
      await updateDoc(doc(db, 'venue_reports', report.id), {
        promotedToVrc: true,
        promotedAt: new Date(),
        promotedBy: auth.currentUser?.uid || null,
      });
      alert(`Promoted LIVE specs into VRC for ${report.venueName || 'venue'}.`);
      loadReports();
    } catch (error) {
      console.error('Error promoting LIVE specs to VRC:', error);
      alert('Error promoting to VRC: ' + error.message);
    } finally {
      setIsApprovingReport(false);
      setApprovingReportId(null);
    }
  };

  const handleArchiveVenueSubmissions = async (status) => {
    setIsUploadingReports(true);
    try {
      const db = getFirestore();
      const auth = getAuth();

      if (status === 'approved') {
        // Get all approved tech specs only reports
        const reportsQuery = query(
          collection(db, 'venue_reports'),
          where('status', '==', 'approved'),
          where('isTechSpecsOnly', '==', true)
        );
        const reportsSnapshot = await getDocs(reportsQuery);
        
        // Filter to only get reports that haven't been uploaded yet
        const reportsToUpload = reportsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(report => !report.uploadedToVenue);
        
        if (reportsToUpload.length === 0) {
          alert('No approved tech specs reports to upload (all have already been uploaded).');
          setIsUploadingReports(false);
          return;
        }

        const techSpecsFields = [
          'stage', 'power', 'lightingControl', 'lightingFixtures', 'video',
          'foh', 'pa', 'monitors', 'backline', 'backstage', 'parking',
          'shorePower', 'merch', 'adaAccessibilityArtist', 'notes'
        ];

        // Prepare venue updates for review
        const venueUpdates = [];

        for (const report of reportsToUpload) {
          try {
            if (!report.venueId) {
              console.warn(`Report ${report.id} has no venueId, skipping`);
              continue;
            }

            const venueRef = doc(db, 'venues', report.venueId);
            const venueDoc = await getDoc(venueRef);

            if (!venueDoc.exists()) {
              console.warn(`Venue ${report.venueId} not found for report ${report.id}`);
              continue;
            }

            const venueData = venueDoc.data();
            const fieldUpdates = {};

            // Merge each tech spec field
            for (const field of techSpecsFields) {
              if (report[field]) {
                const existingValue = venueData[field] || '';
                const newValue = report[field];
                const mergedValue = mergeTechSpecs(existingValue, newValue);
                if (mergedValue) {
                  fieldUpdates[field] = {
                    previous: existingValue || '(empty)',
                    updating: newValue,
                    result: mergedValue,
                    final: mergedValue // Editable final value
                  };
                }
              }
            }

            // Handle capacity separately
            if (report.capacity) {
              fieldUpdates.capacity = {
                previous: venueData.capacity || '(empty)',
                updating: report.capacity,
                result: report.capacity,
                final: report.capacity
              };
            }

            if (Object.keys(fieldUpdates).length > 0) {
              venueUpdates.push({
                venueId: report.venueId,
                venueName: venueData.name || report.venueName || 'Unknown Venue',
                reportId: report.id,
                reportEmail: report.userEmail,
                fields: fieldUpdates
              });
            } else {
              // No tech specs to merge, but still mark as uploaded
              const reportRef = doc(db, 'venue_reports', report.id);
              await updateDoc(reportRef, {
                uploadedToVenue: true,
                uploadedAt: new Date(),
                uploadedBy: auth.currentUser?.uid
              });
            }
          } catch (error) {
            console.error(`Error processing report ${report.id}:`, error);
          }
        }

        if (venueUpdates.length === 0) {
          alert('No venue updates to review.');
          setIsUploadingReports(false);
          return;
        }

        // Reset loading state before showing review modal
        setIsUploadingReports(false);
        
        // Show review modal
        setPendingVenueUpdates(venueUpdates);
        setShowUploadReviewModal(true);
      } else if (status === 'rejected') {
        // Archive rejected reports (mark them as archived)
        const reportsQuery = query(
          collection(db, 'venue_reports'),
          where('status', '==', 'rejected')
        );
        const reportsSnapshot = await getDocs(reportsQuery);
        
        if (reportsSnapshot.empty) {
          alert('No rejected reports to archive.');
          return;
        }

        if (!confirm(`This will archive ${reportsSnapshot.size} rejected report(s). Continue?`)) {
          return;
        }

        const batch = writeBatch(db);
        reportsSnapshot.docs.forEach(doc => {
          batch.update(doc.ref, {
            archived: true,
            archivedAt: new Date(),
            archivedBy: auth.currentUser?.uid
          });
        });

        await batch.commit();
        
        // Reset loading state before reloading
        setIsUploadingReports(false);
        
        alert(`Archived ${reportsSnapshot.size} rejected report(s).`);
        loadReports();
      }
    } catch (error) {
      console.error('Error archiving submissions:', error);
      alert('Error archiving submissions: ' + error.message);
      // Reset loading state on error
      setIsUploadingReports(false);
    }
  };

  const handleFinishUpload = async () => {
    setIsFinishingUpload(true);
    try {
      const db = getFirestore();
      const auth = getAuth();
      const batch = writeBatch(db);

      let processedCount = 0;
      let successCount = 0;
      let errorCount = 0;

      for (const venueUpdate of pendingVenueUpdates) {
        try {
          const venueRef = doc(db, 'venues', venueUpdate.venueId);
          const updates = {};

          // Build updates from final values
          Object.entries(venueUpdate.fields).forEach(([field, data]) => {
            if (data.final && data.final.trim() !== '') {
              updates[field] = data.final.trim();
            }
          });

          if (Object.keys(updates).length > 0) {
            updates.updatedAt = new Date();
            batch.update(venueRef, updates);
            
            // Mark report as uploaded
            const reportRef = doc(db, 'venue_reports', venueUpdate.reportId);
            batch.update(reportRef, {
              uploadedToVenue: true,
              uploadedAt: new Date(),
              uploadedBy: auth.currentUser?.uid
            });

            processedCount++;
            successCount++;
          }
        } catch (error) {
          console.error(`Error updating venue ${venueUpdate.venueId}:`, error);
          errorCount++;
        }
      }

      // Commit all updates
      await batch.commit();

      // Reset loading state before reloading
      setIsFinishingUpload(false);
      
      alert(`Upload complete! Processed: ${processedCount}, Success: ${successCount}, Errors: ${errorCount}`);
      setShowUploadReviewModal(false);
      setPendingVenueUpdates([]);
      loadReports();
      refreshVenues();
    } catch (error) {
      console.error('Error finishing upload:', error);
      alert('Error finishing upload: ' + error.message);
      // Reset loading state on error
      setIsFinishingUpload(false);
    }
  };

  const handleUpdateFinalValue = (venueIndex, field, value) => {
    setPendingVenueUpdates(prev => {
      const updated = [...prev];
      if (updated[venueIndex] && updated[venueIndex].fields[field]) {
        updated[venueIndex].fields[field].final = value;
      }
      return updated;
    });
  };

  const renderVenueSubmission = (report) => {
    console.log('Rendering venue submission:', report);
    return (
      <View key={report.id} style={adminStyles.reportCard}>
        <View style={adminStyles.reportHeader}>
          <Text style={adminStyles.venueName}>{report.name}</Text>
          <View style={adminStyles.headerActions}>
            <Text style={[adminStyles.statusBadge, { backgroundColor: getStatusColor(report.status) }]}>
              {report.status?.toUpperCase() || 'PENDING'}
            </Text>
            </View>
            </View>

        <View style={adminStyles.submissionDetails}>
          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Submitted By:</Text>
            <Text style={adminStyles.detailValue}>
              {report.submittedBy?.firstName 
                ? `${report.submittedBy.firstName} ${report.submittedBy.lastName}`
                : report.submittedBy?.email || 'Anonymous'}
            </Text>
            </View>

          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Submission Date:</Text>
            <Text style={adminStyles.detailValue}>
              {report.createdAt?.toDate().toLocaleDateString()}
            </Text>
            </View>
          
          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Location:</Text>
            <Text style={adminStyles.detailValue}>
              {report.city}, {report.state}
            </Text>
          </View>

          {report.capacity && (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Capacity:</Text>
              <Text style={adminStyles.detailValue}>{report.capacity}</Text>
        </View>
          )}

          {report.website && (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Website:</Text>
              <Text style={[adminStyles.detailValue, adminStyles.link]}>
                {report.website}
              </Text>
            </View>
          )}
        </View>

        <View style={adminStyles.sectionTitle}>
          <Text style={adminStyles.sectionTitleText}>Technical Details</Text>
        </View>

        <View style={adminStyles.technicalDetails}>
          {report.stage && (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Stage:</Text>
              <Text style={adminStyles.detailValue}>{report.stage}</Text>
            </View>
          )}
          {report.power && (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Power:</Text>
              <Text style={adminStyles.detailValue}>{report.power}</Text>
            </View>
          )}
          {report.lightingControl && (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Lighting Control:</Text>
              <Text style={adminStyles.detailValue}>{report.lightingControl}</Text>
            </View>
          )}
          {report.lightingFixtures && (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Lighting Fixtures:</Text>
              <Text style={adminStyles.detailValue}>{report.lightingFixtures}</Text>
            </View>
          )}
          {report.video && (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Video:</Text>
              <Text style={adminStyles.detailValue}>{report.video}</Text>
            </View>
          )}
          {report.foh && (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>FOH:</Text>
              <Text style={adminStyles.detailValue}>{report.foh}</Text>
            </View>
          )}
          {report.pa && (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>PA System:</Text>
              <Text style={adminStyles.detailValue}>{report.pa}</Text>
            </View>
          )}
          {report.monitors && (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Monitors:</Text>
              <Text style={adminStyles.detailValue}>{report.monitors}</Text>
            </View>
          )}
        </View>

        {report.notes && (
          <>
            <View style={adminStyles.sectionTitle}>
              <Text style={adminStyles.sectionTitleText}>Additional Notes</Text>
            </View>
            <Text style={adminStyles.notes}>{report.notes}</Text>
          </>
        )}

        <View style={adminStyles.actionButtonsContainer}>
          {report.status === 'pending' ? (
            <>
          <TouchableOpacity 
                style={[
                  adminStyles.actionButton, 
                  { backgroundColor: '#2ecc71' },
                  (isApprovingReport || isRejectingReport) && { opacity: 0.5 }
                ]}
                onPress={() => handleApproveReport(report)}
                disabled={isApprovingReport || isRejectingReport}
          >
                {isApprovingReport && approvingReportId === report.id ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={adminStyles.actionButtonText}>APPROVE</Text>
                )}
          </TouchableOpacity>
          <TouchableOpacity 
                style={[
                  adminStyles.actionButton, 
                  { backgroundColor: '#e74c3c' },
                  (isApprovingReport || isRejectingReport) && { opacity: 0.5 }
                ]}
                onPress={() => handleRejectReport(report)}
                disabled={isApprovingReport || isRejectingReport}
          >
                <Text style={adminStyles.actionButtonText}>DENY</Text>
          </TouchableOpacity>
            </>
          ) : (
          <TouchableOpacity 
              style={[adminStyles.actionButton, { backgroundColor: '#f1c40f' }]}
              onPress={() => handleRevertToPending(report)}
            >
              <Text style={adminStyles.actionButtonText}>REVERT TO PENDING</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const openEditResourceSubmission = (report) => {
    setEditingResourceSubmission(report);
    setEditResourceCategory(report.category || '');
    setEditResourceNewCategory('');
    setEditResourcePhoto({
      file: null,
      preview: report.imageUrl || null,
      width: report.imageWidth || null,
      height: report.imageHeight || null,
      removed: false,
    });
  };

  const closeEditResourceSubmission = () => {
    setEditingResourceSubmission(null);
    setEditResourceCategory('');
    setEditResourceNewCategory('');
    setEditResourcePhoto({ file: null, preview: null, width: null, height: null, removed: false });
  };

  const handleEditResourcePhotoSelect = async (file) => {
    if (!file) return;
    let preview = file.uri || null;
    if (!preview && typeof URL !== 'undefined' && URL.createObjectURL) {
      preview = URL.createObjectURL(file);
    }
    const { width, height } = await readImageDimensions(file);
    setEditResourcePhoto({ file, preview, width, height, removed: false });
  };

  const handleEditResourcePhotoPick = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({ type: 'image/*', copyToCacheDirectory: true });
      if (!result.canceled && result.assets?.length) {
        const asset = result.assets[0];
        await handleEditResourcePhotoSelect({ ...asset, uri: asset.uri, name: asset.name });
      }
    } catch (error) {
      console.error('Error picking resource photo:', error);
      setNotification({ message: 'Could not pick image.', type: 'error' });
    }
  };

  const handleSaveResourceSubmissionEdit = async () => {
    if (!editingResourceSubmission) return;
    if (!editResourceCategory && !editResourceNewCategory.trim()) {
      setNotification({ message: 'Please choose a resource category.', type: 'error' });
      return;
    }
    try {
      setIsSavingResourceEdit(true);
      let categoryId = editResourceCategory;
      let categoryLabel = resourceCategoryCatalog.find((category) => category.id === categoryId)?.label
        || editingResourceSubmission.categoryLabel
        || getResourceCategoryLabel(categoryId);
      if (editResourceNewCategory.trim()) {
        const savedCategory = await saveResourceCategory(
          db,
          editResourceNewCategory,
          auth.currentUser?.uid,
        );
        categoryId = savedCategory.id;
        categoryLabel = savedCategory.label;
        setResourceCategoryCatalog((prev) => (
          prev.some((category) => category.id === savedCategory.id)
            ? prev
            : [...prev, savedCategory]
        ));
      }
      const updates = {
        category: categoryId,
        categoryLabel,
        updatedAt: new Date(),
      };

      if (editResourcePhoto.removed) {
        updates.imageUrl = null;
        updates.imageWidth = null;
        updates.imageHeight = null;
      } else if (editResourcePhoto.file) {
        const storage = getStorage();
        const file = editResourcePhoto.file;
        let blob;
        if (file.uri) {
          blob = await (await fetch(file.uri)).blob();
        } else if (file instanceof Blob) {
          blob = file;
        } else {
          blob = await (await fetch(file)).blob();
        }
        const venueId = editingResourceSubmission.venueId || 'unknown';
        const ext = (file.name || 'image').split('.').pop()?.toLowerCase() || 'jpg';
        const path = `resource-submissions/${venueId}/${editingResourceSubmission.id}_${Date.now()}.${ext}`;
        const fileRef = ref(storage, path);
        await uploadBytes(fileRef, blob);
        updates.imageUrl = await getDownloadURL(fileRef);
        updates.imageWidth = editResourcePhoto.width || null;
        updates.imageHeight = editResourcePhoto.height || null;
      }

      await updateDoc(doc(db, 'resource_submissions', editingResourceSubmission.id), updates);
      setReports((prev) =>
        prev.map((r) => (r.id === editingResourceSubmission.id ? { ...r, ...updates } : r))
      );
      setNotification({ message: 'Resource updated.', type: 'success' });
      closeEditResourceSubmission();
    } catch (error) {
      console.error('Error saving resource edit:', error);
      setNotification({ message: 'Failed to save resource changes.', type: 'error' });
    } finally {
      setIsSavingResourceEdit(false);
    }
  };

  const renderResourceSubmission = (report) => {
    const scopeLabel = report.scope === 'state' && report.venueState
      ? `All venues in ${report.venueState}`
      : report.scope === 'city' && report.venueCity
        ? `All venues in ${report.venueCity}${report.venueState ? `, ${report.venueState}` : ''}`
        : (report.venueName || 'This venue');
    const resourceCategory = getResourceCategoryById(report.category);
    const categoryLabel = report.categoryLabel || getResourceCategoryLabel(report.category);

    return (
      <View key={report.id} style={adminStyles.reportCard}>
        <View style={adminStyles.reportHeader}>
          <Text style={adminStyles.venueName}>{report.resourceName}</Text>
          <View style={adminStyles.headerActions}>
            <Text style={[adminStyles.statusBadge, { backgroundColor: getStatusColor(report.status) }]}>
              {report.status?.toUpperCase() || 'PENDING'}
            </Text>
          </View>
        </View>

        <View style={adminStyles.submissionDetails}>
          {report.category ? (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Category:</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
                {resourceCategory ? (
                  <Image
                    source={resourceCategory.source}
                    style={{
                      width: getResourceCategoryIconSize(resourceCategory, 24),
                      height: getResourceCategoryIconSize(resourceCategory, 24),
                      ...(resourceCategory.invertInLightMode && !adminIsDarkMode && Platform.OS === 'web'
                        ? { filter: 'invert(1)' }
                        : null),
                    }}
                    resizeMode="contain"
                  />
                ) : null}
                <Text style={adminStyles.detailValue}>{categoryLabel}</Text>
              </View>
            </View>
          ) : (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Category:</Text>
              <Text style={[adminStyles.detailValue, { color: '#e67e22' }]}>Not assigned — use Edit</Text>
            </View>
          )}

          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Submitted By:</Text>
            <Text style={adminStyles.detailValue}>
              {report.userName || report.userEmail || 'Anonymous'}
            </Text>
          </View>

          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Submission Date:</Text>
            <Text style={adminStyles.detailValue}>
              {report.createdAt?.toDate?.().toLocaleDateString?.() || 'N/A'}
            </Text>
          </View>

          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Venue:</Text>
            <Text style={adminStyles.detailValue}>{report.venueName || 'N/A'}</Text>
          </View>

          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Share With:</Text>
            <Text style={adminStyles.detailValue}>{scopeLabel}</Text>
          </View>

          {report.url ? (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>URL:</Text>
              <Text style={[adminStyles.detailValue, adminStyles.link]}>{report.url}</Text>
            </View>
          ) : null}

          {report.description ? (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Description:</Text>
              <Text style={adminStyles.detailValue}>{report.description}</Text>
            </View>
          ) : null}

          {report.imageUrl ? (
            <View style={{ marginTop: 12, marginBottom: 4 }}>
              <Text style={[adminStyles.detailLabel, { marginBottom: 8 }]}>Cover Photo:</Text>
              <View style={{
                width: '100%',
                maxWidth: 480,
                aspectRatio: report.imageWidth && report.imageHeight
                  ? report.imageWidth / report.imageHeight
                  : 2.5,
                maxHeight: 240,
                borderRadius: 8,
                overflow: 'hidden',
                backgroundColor: adminIsDarkMode ? '#0f1a2e' : '#f5f5f0',
                borderWidth: 1,
                borderColor: adminIsDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
              }}>
                <Image
                  source={{ uri: report.imageUrl }}
                  style={{ width: '100%', height: '100%' }}
                  resizeMode="contain"
                />
              </View>
              {report.imageWidth && report.imageHeight ? (
                <Text style={[adminStyles.detailValue, { marginTop: 6, fontSize: 11, opacity: 0.7 }]}>
                  {report.imageWidth} × {report.imageHeight}px
                </Text>
              ) : null}
            </View>
          ) : null}
        </View>

        <View style={adminStyles.actionButtonsContainer}>
          <TouchableOpacity
            style={[adminStyles.actionButton, { backgroundColor: '#3498db' }]}
            onPress={() => openEditResourceSubmission(report)}
          >
            <Text style={adminStyles.actionButtonText}>EDIT</Text>
          </TouchableOpacity>
          {report.status === 'pending' ? (
            <>
              <TouchableOpacity
                style={[
                  adminStyles.actionButton,
                  { backgroundColor: '#2ecc71' },
                  (isApprovingReport || isRejectingReport) && { opacity: 0.5 }
                ]}
                onPress={() => handleApproveReport(report)}
                disabled={isApprovingReport || isRejectingReport}
              >
                {isApprovingReport && approvingReportId === report.id ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={adminStyles.actionButtonText}>APPROVE</Text>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  adminStyles.actionButton,
                  { backgroundColor: '#e74c3c' },
                  (isApprovingReport || isRejectingReport) && { opacity: 0.5 }
                ]}
                onPress={() => handleRejectReport(report)}
                disabled={isApprovingReport || isRejectingReport}
              >
                <Text style={adminStyles.actionButtonText}>DENY</Text>
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity
              style={[adminStyles.actionButton, { backgroundColor: '#f1c40f' }]}
              onPress={() => handleRevertToPending(report)}
            >
              <Text style={adminStyles.actionButtonText}>REVERT TO PENDING</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const renderSpecFlagSubmission = (report) => {
    const flagCategory = getResourceCategoryById(report.category);
    const categoryLabel = report.categoryLabel || getResourceCategoryLabel(report.category);

    return (
      <View key={report.id} style={adminStyles.reportCard}>
        <View style={adminStyles.reportHeader}>
          <Text style={adminStyles.venueName}>{report.venueName || 'Venue'}</Text>
          <View style={adminStyles.headerActions}>
            <Text style={[adminStyles.statusBadge, { backgroundColor: getStatusColor(report.status) }]}>
              {report.status?.toUpperCase() || 'PENDING'}
            </Text>
          </View>
        </View>

        <View style={adminStyles.submissionDetails}>
          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Category:</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
              {flagCategory ? (
                <Image
                  source={flagCategory.source}
                  style={{
                    width: 24,
                    height: 24,
                    ...(flagCategory.invertInLightMode && !adminIsDarkMode && Platform.OS === 'web'
                      ? { filter: 'invert(1)' }
                      : null),
                  }}
                  resizeMode="contain"
                />
              ) : null}
              <Text style={adminStyles.detailValue}>{categoryLabel || report.category || 'N/A'}</Text>
            </View>
          </View>

          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Submitted By:</Text>
            <Text style={adminStyles.detailValue}>
              {report.userName || report.userEmail || 'Anonymous'}
            </Text>
          </View>

          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Submission Date:</Text>
            <Text style={adminStyles.detailValue}>
              {report.createdAt?.toDate?.().toLocaleDateString?.() || 'N/A'}
            </Text>
          </View>

          {report.notes ? (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Note:</Text>
              <Text style={adminStyles.detailValue}>{report.notes}</Text>
            </View>
          ) : null}

          {report.denyReason ? (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Deny Reason:</Text>
              <Text style={adminStyles.detailValue}>{report.denyReason}</Text>
            </View>
          ) : null}
        </View>

        <View style={adminStyles.actionButtonsContainer}>
          {report.status === 'pending' ? (
            <>
              <TouchableOpacity
                style={[
                  adminStyles.actionButton,
                  { backgroundColor: '#2ecc71' },
                  (isApprovingReport || isRejectingReport) && { opacity: 0.5 },
                ]}
                onPress={() => handleApproveReport(report)}
                disabled={isApprovingReport || isRejectingReport}
              >
                {isApprovingReport && approvingReportId === report.id ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={adminStyles.actionButtonText}>REVIEWED</Text>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  adminStyles.actionButton,
                  { backgroundColor: '#e74c3c' },
                  (isApprovingReport || isRejectingReport) && { opacity: 0.5 },
                ]}
                onPress={() => handleRejectReport(report)}
                disabled={isApprovingReport || isRejectingReport}
              >
                <Text style={adminStyles.actionButtonText}>DENY</Text>
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity
              style={[adminStyles.actionButton, { backgroundColor: '#f1c40f' }]}
              onPress={() => handleRevertToPending(report)}
            >
              <Text style={adminStyles.actionButtonText}>REVERT TO PENDING</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const renderVenueInterestTagSubmission = (report) => {
    const interests = Array.isArray(report.interests) ? report.interests : [];

    return (
      <View key={report.id} style={adminStyles.reportCard}>
        <View style={adminStyles.reportHeader}>
          <Text style={adminStyles.venueName}>{report.venueName || 'Venue'}</Text>
          <View style={adminStyles.headerActions}>
            <Text style={[adminStyles.statusBadge, { backgroundColor: getStatusColor(report.status) }]}>
              {report.status?.toUpperCase() || 'PENDING'}
            </Text>
          </View>
        </View>

        <View style={adminStyles.submissionDetails}>
          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Interests:</Text>
            <Text style={adminStyles.detailValue}>
              {interests.length ? interests.join(', ') : 'None listed'}
            </Text>
          </View>

          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Submitted By:</Text>
            <Text style={adminStyles.detailValue}>
              {report.userName || report.userEmail || 'Anonymous'}
            </Text>
          </View>

          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Submission Date:</Text>
            <Text style={adminStyles.detailValue}>
              {report.createdAt?.toDate?.().toLocaleDateString?.() || 'N/A'}
            </Text>
          </View>

          {report.denyReason ? (
            <View style={adminStyles.detailRow}>
              <Text style={adminStyles.detailLabel}>Deny Reason:</Text>
              <Text style={adminStyles.detailValue}>{report.denyReason}</Text>
            </View>
          ) : null}
        </View>

        <View style={adminStyles.actionButtonsContainer}>
          {report.status === 'pending' ? (
            <>
              <TouchableOpacity
                style={[
                  adminStyles.actionButton,
                  { backgroundColor: '#2ecc71' },
                  (isApprovingReport || isRejectingReport) && { opacity: 0.5 },
                ]}
                onPress={() => handleApproveReport(report)}
                disabled={isApprovingReport || isRejectingReport}
              >
                {isApprovingReport && approvingReportId === report.id ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={adminStyles.actionButtonText}>APPROVE</Text>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  adminStyles.actionButton,
                  { backgroundColor: '#e74c3c' },
                  (isApprovingReport || isRejectingReport) && { opacity: 0.5 },
                ]}
                onPress={() => handleRejectReport(report)}
                disabled={isApprovingReport || isRejectingReport}
              >
                <Text style={adminStyles.actionButtonText}>DENY</Text>
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity
              style={[adminStyles.actionButton, { backgroundColor: '#f1c40f' }]}
              onPress={() => handleRevertToPending(report)}
            >
              <Text style={adminStyles.actionButtonText}>REVERT TO PENDING</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const renderReport = (report) => {
    console.log('Rendering report:', { reportType, report });
    if (reportType === 'venue_submission') {
      return renderVenueSubmission(report);
    } else if (reportType === 'venue_document_submission') {
      return renderVenueDocumentSubmission(report);
    } else if (reportType === 'venue_archive_request') {
      return renderVenueArchiveRequest(report);
    } else if (reportType === 'resource_submission') {
      return renderResourceSubmission(report);
    } else if (reportType === 'spec_flag') {
      return renderSpecFlagSubmission(report);
    } else if (reportType === 'venue_interest_tag') {
      return renderVenueInterestTagSubmission(report);
    } else if (reportType === 'venue') {
      return renderVenueReport(report, reportColumnCount > 1);
    } else {
      return renderUserReport(report);
    }
  };

  const renderVenueArchiveRequest = (report) => (
    <View key={report.id} style={adminStyles.reportCard}>
      <View style={adminStyles.reportHeader}>
        <Text style={adminStyles.venueName}>{report.venueName || 'Venue archive request'}</Text>
        <View style={adminStyles.headerActions}>
          <Text style={[adminStyles.statusBadge, { backgroundColor: getStatusColor(report.status) }]}>
            {report.status?.toUpperCase() || 'PENDING'}
          </Text>
        </View>
      </View>

      <View style={adminStyles.submissionDetails}>
        <View style={adminStyles.detailRow}>
          <Text style={adminStyles.detailLabel}>Reason:</Text>
          <Text style={adminStyles.detailValue}>{report.reasonLabel || report.reason || 'N/A'}</Text>
        </View>
        <View style={adminStyles.detailRow}>
          <Text style={adminStyles.detailLabel}>Venue ID:</Text>
          <Text style={adminStyles.detailValue}>{report.venueId || 'N/A'}</Text>
        </View>
        <View style={adminStyles.detailRow}>
          <Text style={adminStyles.detailLabel}>Submitted By:</Text>
          <Text style={adminStyles.detailValue}>{report.userName || report.userEmail || 'Anonymous'}</Text>
        </View>
        <View style={adminStyles.detailRow}>
          <Text style={adminStyles.detailLabel}>Submission Date:</Text>
          <Text style={adminStyles.detailValue}>
            {report.createdAt?.toDate?.().toLocaleDateString?.() || 'N/A'}
          </Text>
        </View>
        <View style={{ marginTop: 8 }}>
          <Text style={adminStyles.detailLabel}>Details:</Text>
          <Text style={[adminStyles.detailValue, { marginTop: 4 }]}>{report.details || 'N/A'}</Text>
        </View>
        {report.denyReason ? (
          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Deny Reason:</Text>
            <Text style={adminStyles.detailValue}>{report.denyReason}</Text>
          </View>
        ) : null}
      </View>

      <View style={adminStyles.actionButtonsContainer}>
        {report.status === 'pending' ? (
          <>
            <TouchableOpacity
              style={[adminStyles.actionButton, { backgroundColor: '#2ecc71' }, (isApprovingReport || isRejectingReport) && { opacity: 0.5 }]}
              onPress={() => handleApproveReport(report)}
              disabled={isApprovingReport || isRejectingReport}
            >
              {isApprovingReport && approvingReportId === report.id
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={adminStyles.actionButtonText}>APPROVE & ARCHIVE</Text>}
            </TouchableOpacity>
            <TouchableOpacity
              style={[adminStyles.actionButton, { backgroundColor: '#e74c3c' }, (isApprovingReport || isRejectingReport) && { opacity: 0.5 }]}
              onPress={() => handleRejectReport(report)}
              disabled={isApprovingReport || isRejectingReport}
            >
              <Text style={adminStyles.actionButtonText}>DENY</Text>
            </TouchableOpacity>
          </>
        ) : (
          <TouchableOpacity
            style={[adminStyles.actionButton, { backgroundColor: '#f1c40f' }]}
            onPress={() => handleRevertToPending(report)}
          >
            <Text style={adminStyles.actionButtonText}>REVERT TO PENDING</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  const renderVenueDocumentSubmission = (report) => (
    <View key={report.id} style={adminStyles.reportCard}>
      <View style={adminStyles.reportHeader}>
        <Text style={adminStyles.venueName}>{report.venueName || 'Venue document upload'}</Text>
        <View style={adminStyles.headerActions}>
          <Text style={[adminStyles.statusBadge, { backgroundColor: getStatusColor(report.status) }]}>
            {report.status?.toUpperCase() || 'PENDING'}
          </Text>
        </View>
      </View>

      <View style={adminStyles.submissionDetails}>
        <View style={adminStyles.detailRow}>
          <Text style={adminStyles.detailLabel}>Submitted By:</Text>
          <Text style={adminStyles.detailValue}>{report.userName || report.userEmail || 'Anonymous'}</Text>
        </View>
        <View style={adminStyles.detailRow}>
          <Text style={adminStyles.detailLabel}>Submission Date:</Text>
          <Text style={adminStyles.detailValue}>
            {report.createdAt?.toDate?.().toLocaleDateString?.() || 'N/A'}
          </Text>
        </View>
        <View style={{ marginTop: 8 }}>
          <Text style={adminStyles.detailLabel}>Documents:</Text>
          {(Array.isArray(report.documents) ? report.documents : []).map((document, index) => (
            <TouchableOpacity
              key={`${document.storagePath || document.url}-${index}`}
              onPress={() => {
                if (document.url && typeof window !== 'undefined') window.open(document.url, '_blank');
              }}
              style={{ paddingVertical: 8 }}
            >
              <Text style={[adminStyles.detailValue, adminStyles.link]}>
                {document.name || document.fileName || `Document ${index + 1}`}
                {document.fileName ? ` (${document.fileName})` : ''}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        {report.denyReason ? (
          <View style={adminStyles.detailRow}>
            <Text style={adminStyles.detailLabel}>Deny Reason:</Text>
            <Text style={adminStyles.detailValue}>{report.denyReason}</Text>
          </View>
        ) : null}
      </View>

      <View style={adminStyles.actionButtonsContainer}>
        {report.status === 'pending' ? (
          <>
            <TouchableOpacity
              style={[adminStyles.actionButton, { backgroundColor: '#2ecc71' }, (isApprovingReport || isRejectingReport) && { opacity: 0.5 }]}
              onPress={() => handleApproveReport(report)}
              disabled={isApprovingReport || isRejectingReport}
            >
              {isApprovingReport && approvingReportId === report.id
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={adminStyles.actionButtonText}>APPROVE & PUBLISH</Text>}
            </TouchableOpacity>
            <TouchableOpacity
              style={[adminStyles.actionButton, { backgroundColor: '#e74c3c' }, (isApprovingReport || isRejectingReport) && { opacity: 0.5 }]}
              onPress={() => handleRejectReport(report)}
              disabled={isApprovingReport || isRejectingReport}
            >
              <Text style={adminStyles.actionButtonText}>DENY</Text>
            </TouchableOpacity>
          </>
        ) : (
          <TouchableOpacity
            style={[adminStyles.actionButton, { backgroundColor: '#f1c40f' }]}
            onPress={() => handleRevertToPending(report)}
          >
            <Text style={adminStyles.actionButtonText}>REVERT TO PENDING</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  const renderUserReport = (report) => {
    const userData = usersData[report.userEmail] || null;
    // Get the user type from the report data instead of defaulting to member
    const userType = Object.values(USER_TYPES).find(type => type.value === report.userType) || USER_TYPES.MEMBER;
    const userTypeColor = userType.color;

    return (
      <View key={report.id} style={[
        adminStyles.reportCard,
        { 
          borderLeftWidth: 4,
          borderLeftColor: userTypeColor
        }
      ]}>
        <View style={adminStyles.reportGrid}>
          {/* Left Column - User Profile */}
          <View style={adminStyles.columnLeft}>
            <View style={[adminStyles.profileCard, { marginBottom: 20 }]}>
              <View style={adminStyles.profileHeader}>
                <View style={adminStyles.profileImageContainer}>
                  <Image
                    source={report.photoURL ? { uri: report.photoURL } : require('../../assets/profile_photos/koi.png')}
                    style={adminStyles.profileImage}
                  />
                </View>
                <View style={adminStyles.profileInfo}>
                  <Text style={adminStyles.userName}>{report.username || 'Username N/A'}</Text>
                  <Text style={adminStyles.userFullName}>{report.firstName} {report.lastName}</Text>
                  <Text style={adminStyles.userEmail}>{report.userEmail}</Text>
                  <Text style={[adminStyles.userRole, { color: userTypeColor }]}>
                    {userType.label}
            </Text>
                  <Text style={adminStyles.userWorkplace}>{report.workplace || 'Workplace N/A'}</Text>
                  <Text style={adminStyles.userSubmissions}>Total Submissions: {report.submissions || 0}</Text>
                </View>
              </View>
            </View>

            {/* Bottom Left - User Bio */}
            <View style={[adminStyles.submissionCard, { flex: 1 }]}>
              <Text style={adminStyles.cardTitle}>User Bio</Text>
              <Text style={adminStyles.detailText}>{report.bio || 'No bio provided'}</Text>
            </View>
          </View>

          {/* Right Column - Verification Details */}
          <View style={[adminStyles.columnMiddle, { flex: 2 }]}>
            <View style={adminStyles.detailedGradesCard}>
              <Text style={adminStyles.cardTitle}>Verification Request Details</Text>
              <View style={adminStyles.submissionDetails}>
                <Text style={adminStyles.detailText}>
                  Submitted: {report.createdAt?.toDate().toLocaleDateString()}
                </Text>
                <Text style={adminStyles.detailText}>
                  Current Status: {report.status.toUpperCase()}
                </Text>
                {report.approvedAt && (
                  <Text style={adminStyles.detailText}>
                    Approved: {report.approvedAt.toDate().toLocaleDateString()}
                  </Text>
                )}
                {report.rejectedAt && (
                  <Text style={adminStyles.detailText}>
                    Rejected: {report.rejectedAt.toDate().toLocaleDateString()}
                  </Text>
                )}
              </View>
            </View>
          </View>
        </View>

        {/* Action Buttons */}
        <View style={adminStyles.actionButtonsContainer}>
          {report.status === 'pending' ? (
            <>
              <TouchableOpacity
                style={[
                  adminStyles.actionButton, 
                  { backgroundColor: '#2ecc71' },
                  (isApprovingReport || isRejectingReport) && { opacity: 0.5 }
                ]}
                onPress={() => handleApproveReport(report)}
                disabled={isApprovingReport || isRejectingReport}
              >
                {isApprovingReport && approvingReportId === report.id ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={adminStyles.actionButtonText}>VERIFY USER</Text>
                )}
          </TouchableOpacity>
              <TouchableOpacity
                style={[
                  adminStyles.actionButton, 
                  { backgroundColor: '#e74c3c' },
                  (isApprovingReport || isRejectingReport) && { opacity: 0.5 }
                ]}
                onPress={() => handleRejectReport(report)}
                disabled={isApprovingReport || isRejectingReport}
              >
                <Text style={adminStyles.actionButtonText}>DENY VERIFICATION</Text>
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity
              style={[adminStyles.actionButton, { backgroundColor: '#f1c40f' }]}
              onPress={() => handleRevertToPending(report)}
            >
              <Text style={adminStyles.actionButtonText}>REVERT TO PENDING</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const renderVenueReport = (report, compact = false) => {
    const userData = usersData[report.userEmail] || null;
    const userType = report.userType || 'other';
    const submitterName =
      userData?.username ||
      report.userName ||
      report.userEmail?.split('@')[0] ||
      'Unknown';
    const submissionSource = report.submissionSource
      || (report.isTechSpecsOnly && report.userType === 'technical' ? 'live' : null);
    
    // Build ratings object based on userType (matching VenueReportForm.js)
    let ratings = {};
    if (userType === 'artist') {
      ratings = {
        musicalEquipment: report.musicalEquipmentGrade,
        stageSafety: report.stageSafetyGrade,
        hospitality: report.hospitality,
        backstage: report.backstage,
        venuePromotion: report.venuePromotion,
        audioStaff: report.audioStaff,
        lightingStaff: report.lightingStaff,
        pay: report.pay
      };
    } else if (userType === 'employee') {
      ratings = {
        audioEquipment: report.audioEquipment,
        lightingEquipment: report.lightingEquipment,
        workplaceCleaniness: report.workplaceCleaniness,
        overallSafety: report.overallSafety,
        payComparison: report.payComparison
      };
    } else if (userType === 'touring') {
      ratings = {
        venueManagement: report.venueManagement,
        venueStaff: report.venueStaff,
        audioStaff: report.audioStaff,
        lightingStaff: report.lightingStaff,
        audioEquipment: report.audioEquipment,
        lightingEquipment: report.lightingEquipment,
        overallSafety: report.overallSafety,
        cityWalkability: report.cityWalkability
      };
    } else {
      // Other or default
      ratings = {
        safety: report.safetyRating,
        equipment: report.equipmentRating,
        staff: report.staffRating,
        workingConditions: report.workingConditionsRating
      };
    }
    
    const userGrade = calculateAverageGrade(ratings);

    // Add tech specs fields array
    const techSpecsFields = [
      { label: 'Stage', field: 'stage' },
      { label: 'Power', field: 'power' },
      { label: 'Lighting Control', field: 'lightingControl' },
      { label: 'Lighting Fixtures', field: 'lightingFixtures' },
      { label: 'Video', field: 'video' },
      { label: 'FOH', field: 'foh' },
      { label: 'PA System', field: 'pa' },
      { label: 'Monitors', field: 'monitors' },
      { label: 'Backline', field: 'backline' },
      { label: 'Backstage', field: 'backstage' },
      { label: 'Parking', field: 'parking' },
      { label: 'Shore Power', field: 'shorePower' },
      { label: 'Merch', field: 'merch' },
      { label: 'ADA Accessibility for Artist', field: 'adaAccessibilityArtist' }
    ];

    // Check if this is a tech specs submission
    const hasTechSpecs = techSpecsFields.some(field => report[field.field]);

    return (
                <View 
        key={report.id}
                  style={[
          adminStyles.reportCard,
          {
            marginBottom: 20,
            borderRadius: 10,
            overflow: 'hidden',
            borderLeftWidth: 4,
            borderLeftColor: USER_TYPE_COLORS[userData?.userType || 'member']
          }
        ]}
      >
        {/* Title Card */}
        <View style={[adminStyles.reportTitleCard]}>
          <Text style={[adminStyles.venueName]}>{report.venueName}</Text>
          <View style={adminStyles.headerActions}>
            {report.submissionSource === LIVE_COMMUNITY_SOURCE && (
              <Text style={[adminStyles.statusBadge, { backgroundColor: '#4e7de0', marginRight: 10 }]}>
                LIVE COMMUNITY
              </Text>
            )}
            {report.isTechSpecsOnly && report.submissionSource !== LIVE_COMMUNITY_SOURCE && (
              <Text style={[adminStyles.statusBadge, { backgroundColor: '#9b4dff', marginRight: 10 }]}>
                TECH SPECS
              </Text>
            )}
            {submissionSource === 'vrc' && (
              <Text style={[adminStyles.statusBadge, { backgroundColor: '#3498db', marginRight: 10 }]}>
                VRC
              </Text>
            )}
            {report.promotedToVrc ? (
              <Text style={[adminStyles.statusBadge, { backgroundColor: '#2ecc71', marginRight: 10 }]}>
                IN VRC
              </Text>
            ) : null}
            <Text style={[adminStyles.statusBadge, { backgroundColor: getStatusColor(report.status) }]}>
              {report.status?.toUpperCase()}
            </Text>
          </View>
        </View>

        <View style={[
          adminStyles.reportGrid,
          compact && { flexDirection: 'column' },
        ]}>
          {/* Left Column */}
          <View style={[
            adminStyles.columnLeft,
            compact && { minWidth: 0, width: '100%', flex: undefined },
          ]}>
            <View style={[adminStyles.profileCard, { marginBottom: 20 }]}>
              <View style={adminStyles.profileHeader}>
                <View style={adminStyles.profileImageContainer}>
                  <Image
                    source={userData?.photoURL ? { uri: userData.photoURL } : require('../../assets/profile_photos/koi.png')}
                    style={adminStyles.profileImage}
                  />
                  {userData?.verificationStatus === 'approved' && (
                    <VerifiedBadge style={{ position: 'absolute', left: -5, top: -5, zIndex: 2 }} />
                  )}
              </View>
                <View style={adminStyles.profileInfo}>
                  <Text style={adminStyles.userName}>{submitterName}</Text>
                  <Text style={adminStyles.userFullName}>{userData?.firstName} {userData?.lastName}</Text>
                  <Text style={adminStyles.userEmail}>{report.userEmail}</Text>
                  <Text style={adminStyles.userRole}>{userData?.occupation || 'Role N/A'}</Text>
                  <Text style={adminStyles.userWorkplace}>{userData?.workplace || 'Workplace N/A'}</Text>
                </View>
              </View>
            </View>

            <View style={[adminStyles.submissionCard, { flex: 1 }]}>
              <Text style={adminStyles.cardTitle}>Submission Details</Text>
              <View style={adminStyles.submissionDetails}>
                <Text style={adminStyles.detailText}>
                  Submitted: {report.createdAt?.toDate().toLocaleDateString()}
              </Text>
                <Text style={adminStyles.detailText}>
                  Date Worked: {report.dateWorked || 'N/A'}
                </Text>
                <Text style={adminStyles.detailText}>Status: {report.status.toUpperCase()}</Text>
              </View>
            </View>
          </View>

          {/* Middle Column - Show either tech specs or ratings */}
          <View style={[
            adminStyles.columnMiddle,
            compact && { minWidth: 0, width: '100%', flex: undefined },
          ]}>
            {report.isTechSpecsOnly || hasTechSpecs ? (
              <View style={[adminStyles.detailedGradesCard, { flex: 1 }]}>
                <Text style={adminStyles.cardTitle}>Technical Specifications</Text>
                <View style={adminStyles.techSpecsGrid}>
                  {techSpecsFields.map(({ label, field }) => (
                    report[field] && (
                      <View key={field} style={adminStyles.techSpecItem}>
                        <Text style={adminStyles.techSpecLabel}>{label}:</Text>
                        <Text style={adminStyles.techSpecValue}>{report[field]}</Text>
                      </View>
                    )
                  ))}
                </View>
              </View>
            ) : (
              <View style={[adminStyles.gradesCard, { marginBottom: 20 }]}>
                <View style={adminStyles.gradeContainer}>
                  <View style={adminStyles.gradeBox}>
                    <Text style={adminStyles.gradeLabel}>USER GRADE</Text>
                    <Text style={adminStyles.gradeLetter}>{getLetterGrade(userGrade)}</Text>
                  </View>
                  <View style={adminStyles.gradeBox}>
                    <Text style={adminStyles.gradeLabel}>VENUE AVERAGE</Text>
                    <Text style={adminStyles.gradeLetter}>{getLetterGrade(report.venue?.averageRating)}</Text>
                    <Text style={adminStyles.reportCount}>{report.venue?.reportCount || 0} TOTAL REPORTS</Text>
                  </View>
                </View>
            </View>
          )}

            {!report.isTechSpecsOnly && (
              <View style={[adminStyles.detailedGradesCard, { flex: 1 }]}>
                <Text style={adminStyles.cardTitle}>Overall Experience</Text>
                <Text style={[adminStyles.overallExperience, {
                  fontFamily: 'Neue Haas Grotesk Display Pro',
                  fontSize: 16,
                  color: adminColors.textPrimary,
                  textAlign: 'center',
                  marginBottom: 20,
                  lineHeight: 24
                }]}>
                  {report.overallExperience || 'No overall experience provided'}
                </Text>

                <View style={adminStyles.gradesGrid}>
                  {(() => {
                    const userType = report.userType || 'other';
                    let gradeFields = [];
                    
                    // Define grade fields based on userType (matching VenueReportForm.js)
                    if (userType === 'artist') {
                      gradeFields = [
                        { label: 'Musical Equipment', value: report.musicalEquipmentGrade },
                        { label: 'Stage Safety', value: report.stageSafetyGrade },
                        { label: 'Hospitality', value: report.hospitality },
                        { label: 'Backstage', value: report.backstage },
                        { label: 'Venue Promotion', value: report.venuePromotion },
                        { label: 'Audio Staff', value: report.audioStaff },
                        { label: 'Lighting Staff', value: report.lightingStaff },
                        { label: 'Pay', value: report.pay }
                      ];
                    } else if (userType === 'employee') {
                      gradeFields = [
                        { label: 'Audio Equipment', value: report.audioEquipment },
                        { label: 'Lighting Equipment', value: report.lightingEquipment },
                        { label: 'Workplace Cleanliness', value: report.workplaceCleaniness },
                        { label: 'Overall Safety', value: report.overallSafety },
                        { label: 'Pay comparison', value: report.payComparison }
                      ];
                    } else if (userType === 'touring') {
                      gradeFields = [
                        { label: 'Venue Management', value: report.venueManagement },
                        { label: 'Audio Staff', value: report.audioStaff },
                        { label: 'Lighting Staff', value: report.lightingStaff },
                        { label: 'Audio Equipment', value: report.audioEquipment },
                        { label: 'Lighting Equipment', value: report.lightingEquipment },
                        { label: 'Overall Safety', value: report.overallSafety },
                        { label: 'City Walkability', value: report.cityWalkability }
                      ];
                    } else {
                      // Other or default
                      gradeFields = [
                        { label: 'Safety', value: report.safetyRating },
                        { label: 'Equipment', value: report.equipmentRating },
                        { label: 'Staff', value: report.staffRating },
                        { label: 'Working Conditions', value: report.workingConditionsRating }
                      ];
                    }
                    
                    // Filter to only show grades that exist
                    return gradeFields
                      .concat(extraGradeDisplayFields(report).map((line) => ({
                        label: line.label,
                        value: line.value,
                      })))
                      .filter(grade => grade.value)
                      .map((grade, index) => (
                        <View key={`${report.id}-${grade.label}-${index}`} style={adminStyles.gradeItem}>
                          <Text style={adminStyles.gradeItemLabel}>{grade.label}</Text>
                          <Text style={adminStyles.gradeItemValue}>
                            {getLetterGrade(grade.value)}
                          </Text>
                        </View>
                      ));
                  })()}
                </View>

                <View style={adminStyles.questionnaireContainer}>
                  <Text style={adminStyles.cardTitle}>Other Answers</Text>
                  {(() => {
                    const userType = report.userType || 'other';
                    let otherFields = [];
                    
                    // Define grade fields based on userType (matching VenueReportForm.js)
                    if (userType === 'artist') {
                      otherFields = [
                        { label: 'Date of Performance', value: report.dateOfPerformance },
                        { label: 'Band Name', value: report.bandName },
                        { label: 'Ticket Price', value: report.ticketPrice },
                        { label: 'Total Tickets Sold', value: report.totalTicketsSold },
                        { label: 'Payout Structure', value: report.payoutStructure },
                        { label: 'Band Take Home Amount', value: report.bandTakeHomeAmount },
                        { label: 'Venue Merch Cut', value: report.venueMerchCut },
                      ];
                    } else if (userType === 'employee') {
                      otherFields = [
                        { label: 'Date Worked', value: report.dateWorked },
                        { label: 'Job Title', value: report.jobTitle },
                        { label: 'Pay Type', value: report.payType },
                        { label: 'Pay Amount', value: report.payAmount },
                        { label: 'Overtime Type', value: report.overtimeType },
                        { label: 'Overtime Hours', value: report.overtimeHours },
                        { label: 'Overtime Other', value: report.overtimeOther },
                        { label: 'Average Shift Length', value: report.averageShiftLength },
                        { label: 'Paid Shadow Day', value: report.paidShadowDay },
                        { label: 'Paid Shadow Day Amount', value: report.paidShadowDayAmount },
                        { label: 'Consistent Training', value: report.consistentTraining },
                        { label: 'Consistent Training Description', value: report.consistentTrainingDescription },
                        { label: 'Is Union House', value: report.isUnionHouse },
                        { label: 'Union Name', value: report.unionName },
                      ];
                    } else if (userType === 'touring') {
                      otherFields = [
                        { label: 'Day Rate', value: report.dayRate },
                        { label: 'Venue Management', value: report.venueManagement },
                        { label: 'Favorite Shop', value: report.favoriteShop },
                      ];
                    } 
                    
                    // Filter to only show grades that exist
                    return otherFields
                      .filter(other => other.value)
                      .map((other, index) => (
                        <View key={`${report.id}-${other.label}-${index}`} style={adminStyles.gradeItem}>
                          <Text style={adminStyles.gradeItemLabel}>{other.label}</Text>
                          <Text style={[adminStyles.gradeItemValue, { fontFamily: 'VastagoGrotesk', fontSize: 14, fontWeight: '400' }]}>
                            {other.value}
                          </Text>
                        </View>
                      ));
                  })()}
                </View>
              </View>
            )}
          </View>

          {/* Right Column */}
          <View style={[
            adminStyles.columnRight,
            compact && { minWidth: 0, width: '100%', flex: undefined },
          ]}>
            <View style={[adminStyles.venueInfoCard, report.venue?.backgroundImage && {
              backgroundImage: `url(${report.venue.backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }]}>
              <View style={adminStyles.venueInfoContent}>
                <Text style={adminStyles.venueTitle}>ADDRESS:</Text>
                <Text style={adminStyles.venueText}>
                  {report.venue?.address || 'N/A'}{'\n'}
                  {report.venue?.city}, {report.venue?.state} {report.venue?.zipCode}
                </Text>
                <Text style={adminStyles.venueTitle}>CAPACITY:</Text>
                <Text style={adminStyles.venueText}>{report.venue?.capacity || report.capacity || 'N/A'}</Text>

                {report.photos?.length > 0 && (
                  <View style={adminStyles.mediaSection}>
                    <Text style={[adminStyles.mediaLabel, {
                      fontFamily: 'Neue Haas Grotesk Display Pro',
                      fontSize: 20,
                      color: adminColors.textPrimary,
                      marginBottom: 15,
                      textAlign: 'center'
                    }]}>Uploaded Media</Text>
                    <ScrollView horizontal style={adminStyles.mediaScroll}>
                      {report.photos?.map((url, index) => (
                        <Image
                          key={`${report.id}-photo-${index}`}
                          source={{ uri: url }}
                          style={[adminStyles.mediaPreview, {
                            width: 200,
                            height: 200,
                            marginRight: 15,
                            borderRadius: 10
                          }]}
                        />
                      ))}
                    </ScrollView>
                  </View>
                )}
              </View>
            </View>
          </View>
        </View>

        <View style={adminStyles.actionButtonsContainer}>
          {isLiveCommunityTechSpecReport(report) ? (
            <>
              {!report.promotedToVrc ? (
                <TouchableOpacity
                  key={`${report.id}-promote-vrc`}
                  style={[
                    adminStyles.actionButton,
                    { backgroundColor: '#2ecc71' },
                    (isApprovingReport || isRejectingReport) && { opacity: 0.5 },
                  ]}
                  onPress={() => handlePromoteLiveToVrc(report)}
                  disabled={isApprovingReport || isRejectingReport}
                >
                  {isApprovingReport && approvingReportId === report.id ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={adminStyles.actionButtonText}>PROMOTE TO VRC</Text>
                  )}
                </TouchableOpacity>
              ) : null}
              {report.status !== LIVE_COMMUNITY_HIDDEN_STATUS ? (
                <TouchableOpacity
                  key={`${report.id}-hide-live`}
                  style={[
                    adminStyles.actionButton,
                    { backgroundColor: '#e74c3c' },
                    (isApprovingReport || isRejectingReport) && { opacity: 0.5 },
                  ]}
                  onPress={() => handleHideLiveCommunityReport(report)}
                  disabled={isApprovingReport || isRejectingReport}
                >
                  <Text style={adminStyles.actionButtonText}>HIDE FROM LIVE</Text>
                </TouchableOpacity>
              ) : null}
            </>
          ) : report.status === 'pending' ? (
            <>
              <TouchableOpacity 
                key={`${report.id}-approve`}
                style={[
                  adminStyles.actionButton, 
                  { backgroundColor: '#2ecc71' },
                  (isApprovingReport || isRejectingReport) && { opacity: 0.5 }
                ]}
                onPress={() => handleApproveReport(report)}
                disabled={isApprovingReport || isRejectingReport}
              >
                {isApprovingReport && approvingReportId === report.id ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={adminStyles.actionButtonText}>APPROVE</Text>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                key={`${report.id}-deny`}
                style={[
                  adminStyles.actionButton, 
                  { backgroundColor: '#e74c3c' },
                  (isApprovingReport || isRejectingReport) && { opacity: 0.5 }
                ]}
                onPress={() => handleRejectReport(report)}
                disabled={isApprovingReport || isRejectingReport}
              >
                <Text style={adminStyles.actionButtonText}>DENY</Text>
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity
              key={`${report.id}-revert`}
              style={[adminStyles.actionButton, { backgroundColor: '#f1c40f' }]}
              onPress={() => handleRevertToPending(report)}
            >
              <Text style={adminStyles.actionButtonText}>REVERT TO PENDING</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const handleAdminTabSelect = (tabId, options) => {
    if (options?.reportType) {
      setReportType(options.reportType);
    } else if (tabId === 'venue_reports') {
      setReportType('venue');
    } else if (tabId === 'venue_spec_flags') {
      setReportType('spec_flag');
    } else if (tabId === 'venue_interest_tags') {
      setReportType('venue_interest_tag');
    }
    if (typeof options?.showTechSpecsOnly === 'boolean') {
      setShowTechSpecsOnly(options.showTechSpecsOnly);
    } else if (tabId === 'venue_reports') {
      setShowTechSpecsOnly(false);
    }
    setActiveTab(tabId);
  };

  const renderStatusButtons = () => {
    return (
      <View style={adminStyles.statusButtonsContainer}>
        <TouchableOpacity
          style={[
            adminStyles.statusButton,
            activeTab === 'venues' && adminStyles.activeStatusButton
          ]}
          onPress={() => setActiveTab('venues')}
        >
          <Text style={adminStyles.statusButtonText}>VENUES</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            adminStyles.statusButton,
            activeTab === 'new_venues' && adminStyles.activeStatusButton
          ]}
          onPress={() => setActiveTab('new_venues')}
        >
          <Text style={adminStyles.statusButtonText}>NEW VENUES</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            adminStyles.statusButton,
            activeTab === 'venue_reports' && adminStyles.activeStatusButton
          ]}
          onPress={() => {
            setReportType('venue');
            setActiveTab('venue_reports');
          }}
        >
          <Text style={adminStyles.statusButtonText}>VENUE REPORTS</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            adminStyles.statusButton,
            activeTab === 'resource_submissions' && adminStyles.activeStatusButton
          ]}
          onPress={() => {
            setReportType('resource_submission');
            setActiveTab('resource_submissions');
          }}
        >
          <Text style={adminStyles.statusButtonText}>RESOURCES</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            adminStyles.statusButton,
            activeTab === 'venue_document_submissions' && adminStyles.activeStatusButton
          ]}
          onPress={() => {
            setReportType('venue_document_submission');
            setActiveTab('venue_document_submissions');
          }}
        >
          <Text style={adminStyles.statusButtonText}>DOCUMENT UPLOADS</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            adminStyles.statusButton,
            activeTab === 'venue_archive_requests' && adminStyles.activeStatusButton
          ]}
          onPress={() => {
            setReportType('venue_archive_request');
            setActiveTab('venue_archive_requests');
          }}
        >
          <Text style={adminStyles.statusButtonText}>ARCHIVE REQUESTS</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            adminStyles.statusButton,
            activeTab === 'users' && adminStyles.activeStatusButton
          ]}
          onPress={() => setActiveTab('users')}
        >
          <Text style={adminStyles.statusButtonText}>USERS</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            adminStyles.statusButton,
            activeTab === 'site' && adminStyles.activeStatusButton
          ]}
          onPress={() => setActiveTab('site')}
        >
          <Text style={adminStyles.statusButtonText}>SITE MANAGEMENT</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            adminStyles.statusButton,
            activeTab === 'community' && adminStyles.activeStatusButton
          ]}
          onPress={() => setActiveTab('community')}
        >
          <Text style={adminStyles.statusButtonText}>COMMUNITY</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            adminStyles.statusButton,
            activeTab === 'apis' && adminStyles.activeStatusButton
          ]}
          onPress={() => setActiveTab('apis')}
        >
          <Text style={adminStyles.statusButtonText}>APIs</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            adminStyles.statusButton,
            activeTab === 'pois' && adminStyles.activeStatusButton
          ]}
          onPress={() => setActiveTab('pois')}
        >
          <Text style={adminStyles.statusButtonText}>POIs</Text>
        </TouchableOpacity>
      </View>
    );
  };

  const loadReports = async () => {
    try {
      console.log('Loading reports:', { reportType, reportStatus });
      const db = getFirestore();
      let reportsRef;
      let q;

      if (reportType === 'venue') {
        reportsRef = collection(db, 'venue_reports');
        if (reportStatus === 'all') {
          q = query(reportsRef);
        } else {
          q = query(reportsRef, where('status', '==', reportStatus));
        }
      } else if (reportType === 'venue_submission') {
        reportsRef = collection(db, 'new_venues');
        // Only filter by status if not showing 'all'
        if (reportStatus === 'all') {
          q = query(reportsRef);
        } else {
          q = query(reportsRef, where('status', '==', reportStatus));
        }
      } else if (reportType === 'resource_submission') {
        reportsRef = collection(db, 'resource_submissions');
        if (reportStatus === 'all') {
          q = query(reportsRef);
        } else {
          q = query(reportsRef, where('status', '==', reportStatus));
        }
      } else if (reportType === 'venue_document_submission') {
        reportsRef = collection(db, 'venue_document_submissions');
        if (reportStatus === 'all') {
          q = query(reportsRef);
        } else {
          q = query(reportsRef, where('status', '==', reportStatus));
        }
      } else if (reportType === 'venue_archive_request') {
        reportsRef = collection(db, 'venue_archive_requests');
        if (reportStatus === 'all') {
          q = query(reportsRef);
        } else {
          q = query(reportsRef, where('status', '==', reportStatus));
        }
      } else if (reportType === 'spec_flag') {
        reportsRef = collection(db, 'venue_spec_flags');
        if (reportStatus === 'all') {
          q = query(reportsRef);
        } else {
          q = query(reportsRef, where('status', '==', reportStatus));
        }
      } else if (reportType === 'venue_interest_tag') {
        reportsRef = collection(db, 'venue_interest_tags');
        if (reportStatus === 'all') {
          q = query(reportsRef);
        } else {
          q = query(reportsRef, where('status', '==', reportStatus));
        }
      } else {
        // User reports (verification requests)
        reportsRef = collection(db, 'user_reports');
        if (reportStatus === 'all') {
          q = query(reportsRef, where('type', '==', 'verification'));
        } else {
          q = query(reportsRef, 
            where('type', '==', 'verification'),
            where('status', '==', reportStatus)
          );
        }
      }

      const querySnapshot = await getDocs(q);
      const fetchedReports = [];

      querySnapshot.forEach((docSnapshot) => {
        const reportData = {
          id: docSnapshot.id,
          ...docSnapshot.data()
        };
        fetchedReports.push(reportData);
      });

      // Sort reports by date
      const sortedReports = fetchedReports.sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return b.createdAt.toDate() - a.createdAt.toDate();
      });

      console.log('Fetched reports:', { count: sortedReports.length, reports: sortedReports });
      setReports(sortedReports);
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  };

  // Add this function before renderReports
  const getFilteredReports = () => {
    if (!reports) return [];
    
    // Filter reports based on search, tech filter, and status
    const filteredReports = reports.filter(report => {
      const matchesSearch = !reportSearchQuery || 
        report.venueName?.toLowerCase().includes(reportSearchQuery.toLowerCase()) ||
        report.userEmail?.toLowerCase().includes(reportSearchQuery.toLowerCase()) ||
        report.notes?.toLowerCase().includes(reportSearchQuery.toLowerCase()) ||
        report.details?.toLowerCase().includes(reportSearchQuery.toLowerCase()) ||
        (Array.isArray(report.documents)
          ? report.documents.some((document) =>
              `${document.name || ''} ${document.fileName || ''}`.toLowerCase().includes(reportSearchQuery.toLowerCase())
            )
          : false) ||
        report.resourceName?.toLowerCase().includes(reportSearchQuery.toLowerCase()) ||
        report.description?.toLowerCase().includes(reportSearchQuery.toLowerCase()) ||
        report.url?.toLowerCase().includes(reportSearchQuery.toLowerCase()) ||
        report.category?.toLowerCase().includes(reportSearchQuery.toLowerCase()) ||
        report.categoryLabel?.toLowerCase().includes(reportSearchQuery.toLowerCase()) ||
        (Array.isArray(report.interests)
          ? report.interests.join(' ').toLowerCase().includes(reportSearchQuery.toLowerCase())
          : false);

      // Venue Reports shows grades or tech specs, never both (BUG-089).
      const matchesTechFilter = reportType === 'resource_submission' || reportType === 'venue_document_submission' || reportType === 'venue_archive_request' || reportType === 'spec_flag' || reportType === 'venue_interest_tag' || matchesVenueReportKind(report, showTechSpecsOnly);

      const matchesStatus = reportStatus === 'all' ? true : report.status === reportStatus;

      return matchesSearch && matchesTechFilter && matchesStatus;
    });

    // If there's a search query, prioritize exact matches
    if (reportSearchQuery) {
      const searchLower = reportSearchQuery.toLowerCase();
      
      return filteredReports.sort((a, b) => {
        const aExactVenueName = a.venueName?.toLowerCase() === searchLower;
        const bExactVenueName = b.venueName?.toLowerCase() === searchLower;
        const aExactEmail = a.userEmail?.toLowerCase() === searchLower;
        const bExactEmail = b.userEmail?.toLowerCase() === searchLower;
        const aExactResource = a.resourceName?.toLowerCase() === searchLower;
        const bExactResource = b.resourceName?.toLowerCase() === searchLower;
        
        if (aExactVenueName && !bExactVenueName) return -1;
        if (!aExactVenueName && bExactVenueName) return 1;
        if (aExactResource && !bExactResource) return -1;
        if (!aExactResource && bExactResource) return 1;
        if (aExactEmail && !bExactEmail) return -1;
        if (!aExactEmail && bExactEmail) return 1;
        
        const aStartsWithVenue = a.venueName?.toLowerCase().startsWith(searchLower);
        const bStartsWithVenue = b.venueName?.toLowerCase().startsWith(searchLower);
        
        if (aStartsWithVenue && !bStartsWithVenue) return -1;
        if (!aStartsWithVenue && bStartsWithVenue) return 1;
        
        return getReportTimestamp(b) - getReportTimestamp(a);
      });
    }
    
    return sortReportsList(filteredReports, reportSortBy, reportType);
  };

  // Add this function after renderStatusButtons
  const renderReportStatusButtons = () => {
    const statuses = ['all', 'pending', 'live', 'approved', 'hidden', 'rejected'];

    const pillStyle = (active) => ({
      backgroundColor: active ? '#0bb5df' : adminColors.cardBgSubtle,
      borderWidth: active ? 0 : 1,
      borderColor: adminColors.cardBorder,
      paddingVertical: 10,
      paddingHorizontal: 20,
      borderRadius: 8,
      minWidth: 120,
      alignItems: 'center',
      justifyContent: 'center',
    });

    const pillTextStyle = (active) => ({
      color: active ? '#ffffff' : adminColors.textPrimary,
      fontFamily: 'VastagoGrotesk',
      fontSize: 14,
      fontWeight: active ? '600' : '500',
    });
    
    return (
      <View style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
        marginBottom: 15
      }}>
        {statuses.map((status) => (
          <TouchableOpacity
            key={status}
            style={pillStyle(reportStatus === status)}
            onPress={() => {
              console.log('Setting report status:', status);
              setReportStatus(status);
            }}
          >
            <Text style={pillTextStyle(reportStatus === status)}>{status.toUpperCase()}</Text>
          </TouchableOpacity>
        ))}
        
        {/* Reports (grades) vs Tech Specs (venue info sheet): one at a time (BUG-089) */}
        {reportType !== 'resource_submission' && reportType !== 'spec_flag' && reportType !== 'venue_interest_tag' && (
          [{ label: 'REPORTS', techSpecs: false }, { label: 'TECH SPECS', techSpecs: true }].map((kind) => {
            const selected = showTechSpecsOnly === kind.techSpecs;
            return (
              <TouchableOpacity
                key={kind.label}
                accessibilityRole="button"
                accessibilityState={{ selected }}
                style={{
                  backgroundColor: selected ? '#9b4dff' : adminColors.cardBgSubtle,
                  borderWidth: selected ? 0 : 1,
                  borderColor: adminColors.cardBorder,
                  paddingVertical: 10,
                  paddingHorizontal: 20,
                  borderRadius: 8,
                  minWidth: 120,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onPress={() => setShowTechSpecsOnly(kind.techSpecs)}
              >
                <Text style={pillTextStyle(selected)}>{kind.label}</Text>
              </TouchableOpacity>
            );
          })
        )}
      </View>
    );
  };

  // Update the renderReports function to include the status filters
  const renderReports = () => {
    const filteredReports = getFilteredReports();
    const sortOptions = (reportType === 'resource_submission' || reportType === 'venue_document_submission' || reportType === 'venue_archive_request' || reportType === 'spec_flag' || reportType === 'venue_interest_tag') ? RESOURCE_SORT_OPTIONS : REPORT_SORT_OPTIONS;

    const reportColumnBtnStyle = (active) => ({
      width: 36,
      height: 36,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: active ? '#0bb5df' : adminColors.cardBgSubtle,
      borderWidth: active ? 0 : 1,
      borderColor: adminColors.cardBorder,
    });

    const reportSortPillStyle = (active) => ({
      backgroundColor: active ? '#0bb5df' : adminColors.cardBgSubtle,
      borderWidth: active ? 0 : 1,
      borderColor: adminColors.cardBorder,
      paddingVertical: 10,
      paddingHorizontal: 16,
      borderRadius: 8,
      minWidth: 100,
      alignItems: 'center',
      justifyContent: 'center',
    });

    const reportSortPillTextStyle = (active) => ({
      color: active ? '#ffffff' : adminColors.textPrimary,
      fontFamily: 'VastagoGrotesk',
      fontSize: 14,
      fontWeight: active ? '600' : '500',
    });

    const reportsGridStyle =
      Platform.OS === 'web'
        ? {
            display: 'grid',
            gridTemplateColumns: `repeat(${reportColumnCount}, minmax(0, 1fr))`,
            gap: 16,
            width: '100%',
            alignItems: 'start',
          }
        : {
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 16,
            width: '100%',
          };

    const reportCardWrapStyle =
      Platform.OS === 'web'
        ? { minWidth: 0, width: '100%' }
        : { width: reportColumnCount === 1 ? '100%' : reportColumnCount === 2 ? '48%' : '31%' };

    return (
      <ScrollView style={[adminStyles.reportsContainer, { width: '100%' }]}>
        <View style={{
          width: '100%',
          marginBottom: 20,
          gap: 14,
        }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text style={{ color: adminColors.textSecondary, fontFamily: 'VastagoGrotesk', fontSize: 13, fontWeight: '600' }}>
                Columns
              </Text>
              {[1, 2, 3].map((n) => (
                <TouchableOpacity
                  key={n}
                  style={reportColumnBtnStyle(reportColumnCount === n)}
                  onPress={() => setReportColumnCount(n)}
                  accessibilityLabel={`${n} column layout`}
                >
                  <Text style={{ color: reportColumnCount === n ? '#fff' : adminColors.textPrimary, fontWeight: '700', fontFamily: 'VastagoGrotesk' }}>
                    {n}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {renderReportStatusButtons()}

          <View>
            <Text style={{ color: adminColors.textPrimary, fontFamily: 'VastagoGrotesk', fontSize: 14, fontWeight: '600', marginBottom: 10 }}>
              Sort by:
            </Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
              {sortOptions.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={reportSortPillStyle(reportSortBy === option.value)}
                  onPress={() => setReportSortBy(option.value)}
                >
                  <Text style={reportSortPillTextStyle(reportSortBy === option.value)}>{option.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          
          {/* Search Input */}
          <TextInput
            nativeID={ADMIN_REPORTS_SEARCH_ID}
            style={{
              backgroundColor: adminColors.inputBg,
              borderRadius: 8,
              padding: 15,
              color: adminColors.inputText,
              fontFamily: 'VastagoGrotesk',
              fontSize: 16,
              width: '100%',
              borderWidth: 1,
              borderColor: adminColors.inputBorder,
            }}
            placeholder={reportType === 'venue_document_submission'
              ? 'Search by document name, venue, or uploader...'
              : reportType === 'resource_submission'
              ? 'Search by resource name, venue, user email, or description...'
              : reportType === 'venue_interest_tag'
              ? 'Search by venue name, user email, or interest tags...'
              : 'Search reports by venue name, user email, or notes...'}
            placeholderTextColor={reportSearchPlaceholderColor}
            value={reportSearchQuery}
            onChangeText={setReportSearchQuery}
          />

          {/* Results Count */}
          <Text style={{
            color: adminColors.textSecondary,
            fontFamily: 'VastagoGrotesk',
            fontSize: 14,
            marginTop: 5
          }}>
            {filteredReports.length} reports found
          </Text>
        </View>

        {/* Show archive buttons only when needed */}
        {(reportType === 'venue' || reportType === 'venue_submission') && reportStatus !== 'pending' && (
          <View style={[adminStyles.actionButtonsContainer, { 
            width: '100%',
            paddingHorizontal: 0,
            marginBottom: 20
          }]}>
            <TouchableOpacity
              style={[
                adminStyles.actionButton, 
                { backgroundColor: '#2ecc71' },
                isUploadingReports && { opacity: 0.5 }
              ]}
              onPress={() => handleArchiveVenueSubmissions('approved')}
              disabled={isUploadingReports}
            >
              {isUploadingReports ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : (
                <Text style={adminStyles.actionButtonText}>UPLOAD APPROVED</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                adminStyles.actionButton, 
                { backgroundColor: '#e74c3c' },
                isUploadingReports && { opacity: 0.5 }
              ]}
              onPress={() => handleArchiveVenueSubmissions('rejected')}
              disabled={isUploadingReports}
            >
              {isUploadingReports ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : (
                <Text style={adminStyles.actionButtonText}>ARCHIVE REJECTED</Text>
              )}
            </TouchableOpacity>
          </View>
        )}

        <View style={{ width: '100%' }}>
          {filteredReports.length === 0 ? (
            <Text style={adminStyles.emptyStateText}>
              {reports.length === 0 ? 
                `No ${reportStatus} reports available` : 
                'No reports match your search criteria'}
            </Text>
          ) : (
            <View style={reportsGridStyle}>
              {filteredReports.map(report => (
                <View key={report.id} style={reportCardWrapStyle}>
                  {renderReport(report)}
                </View>
              ))}
            </View>
          )}
        </View>
      </ScrollView>
    );
  };

  const handleRevokeVerification = async (user) => {
    try {
      if (!confirm('Are you sure you want to revoke verification for this user?')) {
        return;
      }

      const db = getFirestore();
      const functions = getFunctions();
      const userRef = doc(db, 'users', user.id);

      // Update user document
      await updateDoc(userRef, {
        verificationStatus: null,
        verifiedAt: null,
        verifiedBy: null
      });

      // Try to send verification rejection email (non-critical)
      try {
        const handleVerificationStatus = httpsCallable(functions, 'handleVerificationStatus');
        await handleVerificationStatus({
          userData: { ...user, uid: user.id },
          status: 'rejected'
        });
      } catch (emailError) {
        console.warn('Email sending failed, but verification revocation succeeded:', emailError);
        // Don't throw error - verification revocation was successful
      }

      fetchUsers();
      alert('User verification revoked successfully');
    } catch (error) {
      console.error('Error revoking verification:', error);
      alert('Error revoking verification: ' + error.message);
    }
  };

  const handleUpdateSubscription = async (user, status, planId = null) => {
    try {
      const granting = status === 'active';
      let selectedPlan = planId;
      if (granting && !selectedPlan) {
        const choice = window.prompt(
          'Grant which plan?\n\n1 = Calendar Only ($22)\n2 = Map + Calendar ($44)\n\nEnter 1 or 2:',
          '2'
        );
        if (choice === null) return;
        if (String(choice).trim() === '1') selectedPlan = 'calendar_only';
        else if (String(choice).trim() === '2') selectedPlan = 'map_calendar';
        else {
          alert('Invalid choice. Enter 1 or 2.');
          return;
        }
      }

      const planLabel =
        selectedPlan === 'calendar_only'
          ? 'Calendar Only'
          : selectedPlan === 'map_calendar'
            ? 'Map + Calendar'
            : '';

      if (!confirm(
        granting
          ? `Grant ${planLabel} subscription to this user?`
          : 'Revoke the subscription for this user?'
      )) {
        return;
      }

      const db = getFirestore();
      const userRef = doc(db, 'users', user.id);
      const update = {
        subscriptionStatus: status,
        subscriptionUpdatedAt: new Date(),
      };
      if (granting) {
        update.subscriptionPlan = selectedPlan;
        update.subscriptionApprovedLocationsThisCycle = 0;
        update.subscriptionDiscountCentsThisCycle = 0;
        update.subscriptionEffectivePriceCentsThisCycle =
          selectedPlan === 'calendar_only' ? 2200 : 4400;
      } else {
        update.subscriptionPlan = null;
        update.subscriptionApprovedLocationsThisCycle = 0;
        update.subscriptionDiscountCentsThisCycle = 0;
        update.subscriptionEffectivePriceCentsThisCycle = null;
      }

      await updateDoc(userRef, update);

      fetchUsers();
      alert(`User subscription ${granting ? 'granted' : 'revoked'} successfully`);
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert('Error updating subscription: ' + error.message);
    }
  }

  const renderUserContent = () => (
      <View style={{ width: '100%' }}>
        <View style={{ marginBottom: 20 }}>
          <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap' }}>
            <TouchableOpacity
              style={{
                backgroundColor: activeUserSubTab === 'usersList' ? '#2ecc71' : adminColors.cardBgSubtle,
                borderWidth: activeUserSubTab === 'usersList' ? 0 : 1,
                borderColor: adminColors.cardBorder,
                paddingVertical: 10,
                paddingHorizontal: 20,
                borderRadius: 8,
              }}
              onPress={() => setActiveUserSubTab('usersList')}
            >
              <Text style={{
                color: activeUserSubTab === 'usersList' ? '#fff' : adminColors.textPrimary,
                fontFamily: 'VastagoGrotesk',
                fontWeight: '600',
              }}>
                Users List
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={{
                backgroundColor: activeUserSubTab === 'communityDirectory' ? '#2ecc71' : adminColors.cardBgSubtle,
                borderWidth: activeUserSubTab === 'communityDirectory' ? 0 : 1,
                borderColor: adminColors.cardBorder,
                paddingVertical: 10,
                paddingHorizontal: 20,
                borderRadius: 8,
              }}
              onPress={() => setActiveUserSubTab('communityDirectory')}
            >
              <Text style={{
                color: activeUserSubTab === 'communityDirectory' ? '#fff' : adminColors.textPrimary,
                fontFamily: 'VastagoGrotesk',
                fontWeight: '600',
              }}>
                Community Directory Management
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {activeUserSubTab === 'usersList' ? (
          <AdminUsersList
            users={users}
            organizations={organizations}
            userStats={userStats}
            userIdMismatches={userIdMismatches}
            verifierNames={verifierNames}
            userTypes={USER_TYPES}
            isUserOnline={isUserOnline}
            onUserAction={handleUserAction}
            onRevokeVerification={handleRevokeVerification}
            onApproveVerification={handleApproveVerification}
            onMigrateUserIds={handleMigrateUserIds}
            onUpdateSubscription={handleUpdateSubscription}
            onArchiveUser={handleArchiveUser}
            onArchiveEmailExtras={handleArchiveEmailExtras}
            onOrganizationAction={handleOrganizationAction}
          />
        ) : (
          <CommunityDirectoryManagement />
        )}
      </View>
    );

    const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <AdminPageShell title="" subtitle="">
            <AdminDashboard
              onSelectTab={handleAdminTabSelect}
              userStats={userStats}
              venueStats={venueStats}
              newVenueSubmissions={newVenueSubmissions}
              communityMessages={communityMessages}
              maintenanceMode={maintenanceMode}
              maintenanceList={maintenanceList}
              pendingVenueUpdates={pendingVenueUpdates}
              adminUser={user}
            />
          </AdminPageShell>
        );
      case 'venues':
        return (
          <AdminPageShell title="All Venues" subtitle="Browse, edit, and manage venue records.">
            {renderVenueContent()}
          </AdminPageShell>
        );
      case 'new_venues':
        return (
          <AdminPageShell title="New Venues" subtitle="Review and process new venue submissions.">
            <NewVenues />
          </AdminPageShell>
        );
      case 'venue_reports':
        return (
          <AdminPageShell title="Venue Reports" subtitle="Approve or decline submitted venue reports.">
            {renderReports()}
          </AdminPageShell>
        );
      case 'venue_spec_flags':
        return (
          <AdminPageShell title="Spec Flags" subtitle="Review category notes submitted from LIVE tech specs (Accessibility, Safety, Environment, Education).">
            {renderReports()}
          </AdminPageShell>
        );
      case 'venue_interest_tags':
        return (
          <AdminPageShell title="Venue Interest Tags" subtitle="Review community interest tags submitted for venues from the spec sheet.">
            {renderReports()}
          </AdminPageShell>
        );
      case 'resource_submissions':
        return (
          <AdminPageShell title="Resource Submissions" subtitle="Approve or decline community-shared resources.">
            {renderReports()}
          </AdminPageShell>
        );
      case 'venue_document_submissions':
        return (
          <AdminPageShell title="Venue Document Uploads" subtitle="Review venue documents before publishing them under Links & Manuals.">
            {renderReports()}
          </AdminPageShell>
        );
      case 'venue_archive_requests':
        return (
          <AdminPageShell title="Venue Archive Requests" subtitle="Venues reported as closed or under new ownership. Approving archives the venue.">
            {renderReports()}
          </AdminPageShell>
        );
      case 'users':
        return (
          <AdminPageShell title="Users" subtitle="Manage accounts, verification, and the community directory.">
            {renderUserContent()}
          </AdminPageShell>
        );
      case 'site_operations': {
        const pageMeta = SITE_MERGED_PAGES.site_operations;
        return (
          <AdminPageShell title={pageMeta.title} subtitle={pageMeta.subtitle}>
            {renderSiteManagementContent(pageMeta.sections)}
          </AdminPageShell>
        );
      }
      case 'site_dev_tools': {
        if (!DEV_MODE) return null;
        const pageMeta = SITE_MERGED_PAGES.site_dev_tools;
        return (
          <AdminPageShell title={pageMeta.title} subtitle={pageMeta.subtitle}>
            {renderSiteManagementContent(pageMeta.sections)}
          </AdminPageShell>
        );
      }
      case 'mutual_fund':
        return (
          <AdminPageShell
            title="Mutual Fund"
            subtitle="Page photo, money in, suggested donees, votes, and the voting period."
          >
            <AdminMutualFunds
              onNotify={(message, type) => setNotification({ message, type })}
            />
          </AdminPageShell>
        );
      case 'site_data': {
        const pageMeta = SITE_MERGED_PAGES.site_data;
        return (
          <AdminPageShell title={pageMeta.title} subtitle={pageMeta.subtitle}>
            {renderSiteManagementContent(pageMeta.sections)}
            <AdminSectionHeader title="APIs & Data" subtitle="API integrations, imports, and data maintenance utilities." />
            {renderAPIsContent()}
          </AdminPageShell>
        );
      }
      case 'site_locations':
        return (
          <AdminPageShell
            title={SITE_MERGED_PAGES.site_locations.title}
            subtitle={SITE_MERGED_PAGES.site_locations.subtitle}
          >
            <AdminSectionHeader title="POIs" subtitle="Seed, monitor, and map point-of-interest coverage." isFirst />
            {renderPOIsContent()}
            <AdminSectionHeader title="Location Categories" subtitle="Configure location category labels and metadata." />
            <AdminLocationCategories />
          </AdminPageShell>
        );
      case 'community':
        return (
          <AdminPageShell title="Community Moderation" subtitle="Review and manage discussions across all venues.">
            {renderCommunityContent()}
          </AdminPageShell>
        );
      case 'interests':
        return (
          <AdminPageShell
            title="Interests"
            subtitle="Manage default profile interests and promote community-added ones."
          >
            <AdminInterests />
          </AdminPageShell>
        );
      case 'feature_notify':
        return (
          <AdminPageShell
            title="Notify me"
            subtitle="People who asked to be emailed when a coming-soon page goes live."
          >
            <AdminFeatureNotify />
          </AdminPageShell>
        );
      case 'change_logs':
        return (
          <AdminPageShell
            title="Change Logs"
            subtitle="Configure the What's New modal — version targeting, cards, and in-app preview."
          >
            <AdminChangeLogs
              onNotify={(message, type) => setNotification({ message, type })}
            />
          </AdminPageShell>
        );
      default:
        return (
          <AdminPageShell title="" subtitle="">
            <AdminDashboard
              onSelectTab={handleAdminTabSelect}
              userStats={userStats}
              venueStats={venueStats}
              newVenueSubmissions={newVenueSubmissions}
              communityMessages={communityMessages}
              maintenanceMode={maintenanceMode}
              maintenanceList={maintenanceList}
              pendingVenueUpdates={pendingVenueUpdates}
              adminUser={user}
            />
          </AdminPageShell>
        );
    }
  };

  // Community Management Functions
  const loadCommunityMessages = async () => {
    try {
      setLoadingCommunityMessages(true);
      const db = getFirestore();
      const allMessages = [];

      // Get all venue documents
      const venuesSnapshot = await getDocs(collection(db, 'venues'));
      
      for (const venueDoc of venuesSnapshot.docs) {
        const venueData = venueDoc.data();
        const venueId = venueDoc.id;
        
        // Get discussions for this venue
        const discussionsQuery = query(
          collection(db, `venues/${venueId}/discussions`),
          orderBy('timestamp', 'desc')
        );
        
        const discussionsSnapshot = await getDocs(discussionsQuery);
        
        discussionsSnapshot.forEach((doc) => {
          const messageData = doc.data();
          allMessages.push({
            id: doc.id,
            venueId: venueId,
            venueName: venueData.name || 'Unknown Venue',
            ...messageData,
            timestamp: messageData.timestamp?.toDate() || new Date()
          });
        });
      }

      // Sort all messages by timestamp (newest first)
      allMessages.sort((a, b) => b.timestamp - a.timestamp);
      setCommunityMessages(allMessages);
    } catch (error) {
      console.error('Error loading community messages:', error);
    } finally {
      setLoadingCommunityMessages(false);
    }
  };

  const deleteCommunityMessage = async (venueId, messageId) => {
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, `venues/${venueId}/discussions`, messageId));
      
      // Remove from local state
      setCommunityMessages(prev => prev.filter(msg => msg.id !== messageId));
      alert('Message deleted successfully');
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message');
    }
  };

  const getFilteredCommunityMessages = () => {
    return communityMessages.filter(message => {
      const matchesSearch = !communitySearchQuery || 
        message.text?.toLowerCase().includes(communitySearchQuery.toLowerCase()) ||
        message.venueName?.toLowerCase().includes(communitySearchQuery.toLowerCase()) ||
        message.username?.toLowerCase().includes(communitySearchQuery.toLowerCase());
      
      const matchesVenue = selectedVenueFilter === 'all' || message.venueId === selectedVenueFilter;
      
      return matchesSearch && matchesVenue;
    });
  };



  const renderCommunityContent = () => {
    const filteredMessages = getFilteredCommunityMessages();
    const uniqueVenues = [...new Set(communityMessages.map(msg => ({ id: msg.venueId, name: msg.venueName })))];

    return (
      <ScrollView style={adminStyles.container}>
        {/* Filters */}
        <View style={adminStyles.filtersContainer}>
          <View style={adminStyles.filterGroup}>
            <Text style={adminStyles.filterLabel}>Search:</Text>
            <TextInput
              nativeID={ADMIN_COMMUNITY_SEARCH_ID}
              style={adminStyles.searchInput}
              placeholder="Search messages, venues, or users..."
              placeholderTextColor={reportSearchPlaceholderColor}
              value={communitySearchQuery}
              onChangeText={setCommunitySearchQuery}
            />
          </View>
          
          <View style={adminStyles.filterGroup}>
            <Text style={adminStyles.filterLabel}>Venue:</Text>
            <View style={adminStyles.dropdown}>
              <TouchableOpacity
                style={adminStyles.dropdownButton}
                onPress={() => {/* Implement dropdown */}}
              >
                <Text style={adminStyles.dropdownText}>
                  {selectedVenueFilter === 'all' ? 'All Venues' : 
                   uniqueVenues.find(v => v.id === selectedVenueFilter)?.name || 'Select Venue'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          <TouchableOpacity
            style={adminStyles.refreshButton}
            onPress={loadCommunityMessages}
          >
            <Text style={adminStyles.refreshButtonText}>Refresh</Text>
          </TouchableOpacity>
        </View>

        {/* Stats */}
        <View style={adminStyles.statsContainer}>
          <View style={adminStyles.statCard}>
            <Text style={adminStyles.statNumber}>{communityMessages.length}</Text>
            <Text style={adminStyles.statLabel}>Total Messages</Text>
          </View>
          <View style={adminStyles.statCard}>
            <Text style={adminStyles.statNumber}>{uniqueVenues.length}</Text>
            <Text style={adminStyles.statLabel}>Active Venues</Text>
          </View>
          <View style={adminStyles.statCard}>
            <Text style={adminStyles.statNumber}>{filteredMessages.length}</Text>
            <Text style={adminStyles.statLabel}>Filtered Results</Text>
          </View>
        </View>

        {/* Messages List */}
        <View style={adminStyles.messagesContainer}>
          {loadingCommunityMessages ? (
            <Text style={adminStyles.loadingText}>Loading community messages...</Text>
          ) : filteredMessages.length === 0 ? (
            <Text style={adminStyles.noDataText}>No messages found</Text>
          ) : (
            filteredMessages.map((message) => (
              <View key={`${message.venueId}-${message.id}`} style={adminStyles.messageCard}>
                <View style={adminStyles.messageHeader}>
                  <View style={adminStyles.messageInfo}>
                    <Text style={adminStyles.venueName}>{message.venueName}</Text>
                    <Text style={adminStyles.username}>{message.username || 'Anonymous'}</Text>
                    <Text style={adminStyles.timestamp}>
                      {message.timestamp?.toLocaleString() || 'Unknown time'}
                    </Text>
                  </View>
                  <TouchableOpacity
                    style={adminStyles.deleteButton}
                    onPress={() => {
                      if (confirm('Are you sure you want to delete this message?')) {
                        deleteCommunityMessage(message.venueId, message.id);
                      }
                    }}
                  >
                    <Text style={adminStyles.deleteButtonText}>Delete</Text>
                  </TouchableOpacity>
                </View>
                <Text style={adminStyles.messageText}>{message.text || 'No text content'}</Text>
                {message.photoURL && (
                  <img 
                    src={message.photoURL} 
                    alt="Message attachment" 
                    style={adminStyles.messageImage}
                  />
                )}
              </View>
            ))
          )}
        </View>
      </ScrollView>
    );
  };

  // Manual Management Functions
  const saveManualsDatabase = async (manualsToSave = editableManuals) => {
    try {
      const db = getFirestore();
      const batch = writeBatch(db);
      
      // Clear existing collection and add updated manuals
      const manualsSnapshot = await getDocs(collection(db, 'equipment_manuals'));
      manualsSnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      // Add updated manuals
      Object.entries(manualsToSave).forEach(([keyword, manual]) => {
        const docRef = doc(db, 'equipment_manuals', keyword);
        batch.set(docRef, manual);
      });
      
      await batch.commit();
      console.log('Manuals database saved to Firebase successfully');
    } catch (error) {
      console.error('Error saving manuals database:', error);
      alert('Error saving manuals database: ' + error.message);
    }
  };

  // Function to add manual to master database (called from venue manual confirmation)
  const addManualToMasterDatabase = async (manualData) => {
    try {
      // Generate a keyword from the manual name if not provided
      const keyword = manualData.keyword || manualData.name.toLowerCase()
        .replace(/[^a-z0-9\s]/g, '') // Remove special characters
        .replace(/\s+/g, '') // Remove spaces
        .substring(0, 15); // Limit length

      // Check if manual already exists
      if (editableManuals[keyword]) {
        alert(`A manual with keyword "${keyword}" already exists in the database.`);
        return false;
      }

      const newManual = {
        name: manualData.name,
        url: manualData.url,
        // Include software field if provided
        ...(manualData.software?.name && manualData.software?.url ? { 
          software: { 
            name: manualData.software.name, 
            url: manualData.software.url 
          } 
        } : {}),
        // Include any additional fields that might be present
        ...(manualData.category ? { category: manualData.category } : {}),
        ...(manualData.description ? { description: manualData.description } : {}),
        ...(manualData.manufacturer ? { manufacturer: manualData.manufacturer } : {}),
        ...(manualData.model ? { model: manualData.model } : {}),
        ...(manualData.type ? { type: manualData.type } : {}),
        // Add timestamp for tracking
        addedAt: new Date().toISOString(),
        addedBy: 'admin'
      };

      // Add to Firebase
      const db = getFirestore();
      await setDoc(doc(db, 'equipment_manuals', keyword), newManual);

      // Update local state
      setEditableManuals(prev => ({
        ...prev,
        [keyword]: newManual
      }));
      
      alert(`Manual "${manualData.name}" has been added to the master database!`);
      return true;
    } catch (error) {
      console.error('Error adding manual to master database:', error);
      alert('Error adding manual to master database: ' + error.message);
      return false;
    }
  };

  const handleEditManual = (keyword, manual) => {
    setEditingManual({
      keyword,
      searchWord: manual.searchWord,
      name: manual.name,
      url: manual.url,
      software: manual.software || { name: '', url: '' }
    });
  };

  const handleSaveManualEdit = async () => {
    if (!editingManual) return;

    // Validate the new keyword
    if (!editingManual.searchWord || !editingManual.searchWord.trim()) {
      alert('Keyword cannot be empty.');
      return;
    }
    
    const originalManual = editableManuals[editingManual.keyword];
    // Check if the keyword changed and if the new keyword already exists


    const newManual = {
      searchWord: editingManual.searchWord,
      name: editingManual.name,
      url: editingManual.url,
      ...(editingManual.software?.name ? { software: editingManual.software } : {})
    };

    const updatedManuals = { ...editableManuals, [editingManual.keyword]: newManual };
    
    setEditableManuals(updatedManuals);
    setEditingManual(null);
    
    // Save the updated manuals directly
    await saveManualsDatabase(updatedManuals);
    
    await updateAllVenueManuals(originalManual, newManual);
  };

  const handleDeleteManual = (keyword) => {
    if (confirm(`Are you sure you want to delete the manual for "${keyword}"?`)) {
      handleDeleteManualFromAllVenues(editableManuals[keyword]);
      setEditableManuals(prev => {
        const updated = { ...prev };
        delete updated[keyword];
        return updated;
      });
      saveManualsDatabase();
    }
  };

  const handleAddNewManual = () => {
    if (!newManualForm.id || !newManualForm.searchWord || !newManualForm.name || !newManualForm.url) {
      alert('Please fill in ID, keyword, name, and URL fields.');
      return;
    }

    if (editableManuals[newManualForm.id]) {
      alert('A manual with this ID already exists.');
      return;
    }

    const newManual = {
      name: newManualForm.name,
      url: newManualForm.url,
      searchWord: newManualForm.searchWord,
      ...(newManualForm.software?.name ? { software: newManualForm.software } : {})
    };

    setEditableManuals(prev => ({
      ...prev,
      [newManualForm.id]: newManual
    }));

    setNewManualForm({
      id: '',
      searchWord: '',
      name: '',
      url: '',
      category: 'audio',
      software: { name: '', url: '' }
    });
    setAddingNewManual(false);
    saveManualsDatabase();
    updateAllVenueManuals(null, newManual);
  };

  const handleSyncManualsDatabase = async () => {
    try {
      if (!confirm('This will sync the manuals database with Firebase. This may overwrite local changes. Continue?')) {
        return;
      }

      const db = getFirestore();
      
      // Load the static manual data
      const { EQUIPMENT_MANUALS } = await import('../utils/findRelevantManuals.js');
      
      // Clear existing collection
      const manualsSnapshot = await getDocs(collection(db, 'equipment_manuals'));
      const batch = writeBatch(db);
      
      manualsSnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      // Add all manuals from static data
      Object.entries(EQUIPMENT_MANUALS).forEach(([keyword, manual]) => {
        const docRef = doc(db, 'equipment_manuals', keyword);
        batch.set(docRef, manual);
      });
      
      await batch.commit();
      
      // Reload the local state
      setEditableManuals(EQUIPMENT_MANUALS);
      
      alert(`Successfully synced ${Object.keys(EQUIPMENT_MANUALS).length} manuals to Firebase!`);
    } catch (error) {
      console.error('Error syncing manuals database:', error);
      alert('Error syncing manuals database: ' + error.message);
    }
  };


  // Function to update all venue manual links after keyword changes
  const updateAllVenueManuals = async (originalManual, newManual) => {
    console.log('updateAllVenueManuals', originalManual, newManual);
    try {
      console.log('Starting venue manuals update...');
      const db = getFirestore();
      const venuesSnapshot = await getDocs(collection(db, 'venues'));
      
      let successCount = 0;
      let errorCount = 0;
      const batch = writeBatch(db);
      let batchCount = 0;
      const batchSize = 500; // Firestore batch limit
      
      for (const venueDoc of venuesSnapshot.docs) {
        try {
          const venueData = venueDoc.data();
          // Only update if manuals have changed
          const currentManuals = venueData.manualsLinks || [];
          
          let filteredManuals;

          if (originalManual) {
            const originalManualData = {
              name: originalManual.name,
              url: originalManual.url,
            }

            let originalSoftwareData = null;
            if (originalManual.software) {
              originalSoftwareData = {
                name: originalManual.software.name,
                url: originalManual.software.url
              }
            }
            
            filteredManuals = currentManuals.filter(
              link => JSON.stringify(link) !== JSON.stringify(originalManualData)
            );

            if (originalSoftwareData) {
              filteredManuals = filteredManuals.filter(
                link => JSON.stringify(link) !== JSON.stringify(originalSoftwareData)
              );
            }

          } else {
            filteredManuals = currentManuals;
          }
          const newKeywordManual = findRelevantManuals(venueData, { [newManual.searchWord]: newManual });
          
          const newManuals = [...filteredManuals, ...newKeywordManual];
          
          if (JSON.stringify(currentManuals) !== JSON.stringify(newManuals)) {
            const venueRef = doc(db, 'venues', venueDoc.id);
            batch.update(venueRef, {
              manualsLinks: newManuals,
              manualsUpdatedAt: new Date()
            });
            batchCount++;
            
            // Commit batch if we reach the limit
            if (batchCount >= batchSize) {
              await batch.commit();
              const newBatch = writeBatch(db);
              Object.assign(batch, newBatch);
              batchCount = 0;
            }
          }
          
          successCount++;
        } catch (error) {
          console.error(`Error updating venue ${venueDoc.id}:`, error);
          errorCount++;
        }
      }
      
      // Commit any remaining operations
      if (batchCount > 0) {
        await batch.commit();
      }
      
      alert(`Venue manuals update completed!\nProcessed: ${successCount + errorCount} venues\nSuccessful: ${successCount}\nErrors: ${errorCount}`);
    } catch (error) {
      console.error('Error updating venue manuals:', error);
      alert('Error updating venue manuals: ' + error.message);
    }
  };

  const handleDeleteManualFromAllVenues = async (manual) => {
    const db = getFirestore();
    const venuesSnapshot = await getDocs(collection(db, 'venues'));
    const batch = writeBatch(db);

    for (const venueDoc of venuesSnapshot.docs) {
      const venueData = venueDoc.data();
      const currentManuals = venueData.manualsLinks || [];

      const originalManualData = {
        name: manual.name,
        url: manual.url,
      }
  
      let originalSoftwareData = null;
      if (manual.software) {
        originalSoftwareData = {
          name: manual.software.name,
          url: manual.software.url
        }
      }
      let filteredManuals;
      filteredManuals = currentManuals.filter(
        link => JSON.stringify(link) !== JSON.stringify(originalManualData));

      if (originalSoftwareData) {
        filteredManuals = filteredManuals.filter(
          link => JSON.stringify(link) !== JSON.stringify(originalSoftwareData)
        );
      }

      const venueRef = doc(db, 'venues', venueDoc.id);
      batch.update(venueRef, {
        manualsLinks: filteredManuals,
        manualsUpdatedAt: new Date()
      });
    }

    await batch.commit();
    alert('Manual deleted from all venues');
  }

  const getFilteredManuals = () => {
    const manuals = Object.entries(editableManuals);
    
    let filtered = manuals;

    // Filter by search query
    if (manualsSearchQuery) {
      const query = manualsSearchQuery.toLowerCase();
      filtered = filtered.filter(([keyword, manual]) =>
        keyword.toLowerCase().includes(query) ||
        manual.name.toLowerCase().includes(query) ||
        manual.url.toLowerCase().includes(query)
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(([keyword, manual]) => {
        const name = manual.name.toLowerCase();
        switch (selectedCategory) {
          case 'audio':
            return name.includes('allen') || name.includes('avid') || name.includes('behringer') || 
                   name.includes('digico') || name.includes('midas') || name.includes('soundcraft') || 
                   name.includes('ssl') || name.includes('yamaha') || name.includes('waves');
          case 'pa':
            return name.includes('adamson') || name.includes('danley') || name.includes('electro-voice') ||
                   name.includes('jbl') || name.includes('l-acoustics') || name.includes('meyer') ||
                   name.includes('martin audio') || name.includes('nexo');
          case 'lighting':
            return name.includes('adb') || name.includes('avolites') || name.includes('chamsys') ||
                   name.includes('chauvet') || name.includes('etc') || name.includes('martin') ||
                   name.includes('obsidian') || name.includes('vista');
          case 'video':
            return name.includes('roland') || name.includes('video');
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  // Add new render function for site management  
  const renderSiteManagementContent = (sections) => {
    const sectionList = Array.isArray(sections) ? sections : [sections];
    const showSectionHeaders = sectionList.length > 1;
    const includes = (id) => sectionList.includes(id);
    const sectionHeader = (id) => {
      if (!showSectionHeaders || !SITE_SECTION_LABELS[id]) return null;
      return (
        <AdminSectionHeader
          title={SITE_SECTION_LABELS[id].title}
          subtitle={SITE_SECTION_LABELS[id].subtitle}
          isFirst={sectionList.indexOf(id) === 0}
        />
      );
    };

    const emailTemplates = [
      { label: 'Registration Welcome', value: 'registration' },
      { label: 'Account Activation', value: 'activation' },
      { label: 'Verification Request', value: 'verification_request' },
      { label: 'Verification Approved', value: 'verification_approved' },
      { label: 'Verification Rejected', value: 'verification_rejected' },
      { label: 'Venue Submission', value: 'venue_submission' },
      { label: 'Report Submission', value: 'report_submission' }
    ];

    const handleTestEmail = async () => {
      // Validate inputs first
      if (!selectedEmail && !selectedTemplate) {
        setNotification({
          message: 'Please select both a recipient and an email template',
          type: 'error'
        });
        return;
      }
      
      if (!selectedEmail) {
        setNotification({
          message: 'Please select a recipient',
          type: 'error'
        });
        return;
      }

      if (!selectedTemplate) {
        setNotification({
          message: 'Please select an email template',
          type: 'error'
        });
        return;
      }

      // For registration emails, require user selection
      if (selectedTemplate === 'registration' && !selectedUserId) {
        setNotification({
          message: 'Please select a user for the registration email',
          type: 'error'
        });
        return;
      }

      try {
        const auth = getAuth();
        const idToken = await auth.currentUser?.getIdToken();
        
        if (!idToken) {
          throw new Error('Not authenticated');
        }

        // Create appropriate test data based on template type
        let testData = {
          email: selectedEmail,
          firstName: 'Test',
          lastName: 'User',
          username: 'testuser',
          occupation: 'Test Occupation',
          workplace: 'Test Workplace'
        };

        // If a user is selected, use their data
        if (selectedUserId) {
          const selectedUser = users.find(u => u.id === selectedUserId);
          if (selectedUser) {
            testData = {
              ...testData,
              email: selectedUser.email,
              firstName: selectedUser.firstName,
              lastName: selectedUser.lastName,
              username: selectedUser.username,
              occupation: selectedUser.occupation,
              workplace: selectedUser.workplace
            };
          }
        }

        // Add template-specific data
        switch (selectedTemplate) {
          case 'venue_submission':
            testData = {
              ...testData,
              venueName: 'Test Venue',
              city: 'Test City',
              state: 'Test State',
              submitterName: `${testData.firstName} ${testData.lastName}`,
              submitterEmail: testData.email
            };
            break;
          case 'report_submission':
            testData = {
              ...testData,
              venueName: 'Test Venue',
              city: 'Test City',
              state: 'Test State',
              submittedBy: `${testData.firstName} ${testData.lastName}`,
              overallRating: 4.5
            };
            break;
          case 'verification_request':
          case 'verification_approved':
          case 'verification_rejected':
            testData = {
              ...testData,
              verificationStatus: selectedTemplate === 'verification_approved' ? 'approved' : 'pending'
            };
            break;
        }

        const response = await fetch(`http://127.0.0.1:5002/venue-report-card/us-central1/handleTestEmail`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
            'Accept': 'application/json'
          },
          mode: 'cors',
          body: JSON.stringify({
            template: selectedTemplate,
            recipient: selectedEmail,
            testData,
            selectedUserId
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error?.message || `HTTP error! status: ${response.status}`);
        }

        setNotification({
          message: result.message || 'Email sent successfully!',
          type: 'success'
        });
      } catch (error) {
        console.error('Error testing email:', error);
        setNotification({
          message: `Error: ${error.message}`,
          type: 'error'
        });
      }
    };

    const handleMaintenanceSubmit = async () => {
      try {
        const auth = getAuth();
        const db = getFirestore();
        
        // Verify password
        await signInWithEmailAndPassword(auth, auth.currentUser.email, maintenanceForm.password);
        
        const startTime = new Date(maintenanceForm.scheduledStart);
        const endTime = new Date(startTime.getTime() + parseInt(maintenanceForm.duration) * 60 * 60 * 1000);
        
        await setDoc(doc(db, 'system', 'maintenance'), {
          enabled: true,
          message: maintenanceForm.message || 'System is currently under maintenance',
          scheduledStart: startTime,
          startTime: startTime,
          endTime: endTime,
          duration: parseInt(maintenanceForm.duration),
          updatedAt: new Date(),
          updatedBy: auth.currentUser.uid
        });

        setMaintenanceMode(true);
        setMaintenanceForm({
          password: '',
          duration: '1',
          message: '',
          scheduledStart: new Date().toISOString().slice(0, 16)
        });
        
        alert('Maintenance mode scheduled successfully');
      } catch (error) {
        console.error('Error setting maintenance mode:', error);
        alert('Error: ' + error.message);
      }
    };

    // Add these functions for maintenance management
    const handleStartMaintenance = async () => {
      try {
        const auth = getAuth();
        const db = getFirestore();
        
        // Verify password
        await signInWithEmailAndPassword(auth, auth.currentUser.email, maintenanceForm.password);
        
        await setDoc(doc(db, 'system', 'maintenance'), {
          enabled: true,
          message: maintenanceForm.message || 'System is currently under maintenance',
          startTime: new Date(),
          updatedAt: new Date(),
          updatedBy: auth.currentUser.uid
        });

        setMaintenanceMode(true);
        setMaintenanceForm({
          password: '',
          duration: '1',
          message: '',
          scheduledStart: new Date().toISOString().slice(0, 16)
        });
        
        alert('Maintenance mode started');
      } catch (error) {
        console.error('Error starting maintenance:', error);
        alert('Error: ' + error.message);
      }
    };

    const handleStopMaintenance = async () => {
      try {
        const db = getFirestore();
        await setDoc(doc(db, 'system', 'maintenance'), {
          enabled: false,
          message: '',
          updatedAt: new Date(),
          updatedBy: auth.currentUser.uid
        });
        setMaintenanceMode(false);
        alert('Maintenance mode stopped');
      } catch (error) {
        console.error('Error stopping maintenance:', error);
        alert('Error: ' + error.message);
      }
    };

    const handleUnscheduleMaintenance = async (scheduleId) => {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'system', 'maintenance', 'schedules', scheduleId));
        alert('Maintenance unscheduled');
      } catch (error) {
        console.error('Error unscheduling maintenance:', error);
        alert('Error: ' + error.message);
      }
    };

        return (
      <View style={{ width: '100%' }}>
        {includes('analytics') && (
          <View>
            {sectionHeader('analytics')}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16, alignItems: 'stretch' }}>
            {/* Site Analytics */}
              <View style={[adminStyles.userAnalyticsContainer, { flex: 2, minWidth: 320, marginBottom: 0 }]}>
              <View style={adminStyles.userAnalyticsGrid}>
                <View style={adminStyles.userAnalyticCard}>
                  <Text style={adminStyles.userAnalyticNumber}>{userStats.total}</Text>
                  <Text style={adminStyles.userAnalyticLabel}>Total Users</Text>
                </View>
                <View style={adminStyles.userAnalyticCard}>
                  <Text style={adminStyles.userAnalyticNumber}>{venueStats.total}</Text>
                  <Text style={adminStyles.userAnalyticLabel}>Total Venues</Text>
                </View>
                <View style={adminStyles.userAnalyticCard}>
                  <Text style={adminStyles.userAnalyticNumber}>{reports.length}</Text>
                  <Text style={adminStyles.userAnalyticLabel}>Total Reports</Text>
                </View>
                <View style={adminStyles.userAnalyticCard}>
                  <Text style={adminStyles.userAnalyticNumber}>{emailTemplates.length}</Text>
                  <Text style={adminStyles.userAnalyticLabel}>Email Templates</Text>
                </View>
              </View>
            </View>

            {/* System Information */}
              <View style={[adminStyles.devModeSection, { flex: 1, minWidth: 260, marginTop: 0 }]}>
              <Text style={adminStyles.cardTitle}>System Information</Text>
                <View style={adminStyles.devModeContent}>
                <View style={adminStyles.notesSection}>
                  <Text style={adminStyles.notesLabel}>Environment</Text>
                  <Text style={adminStyles.notesText}>
                    {process.env.NODE_ENV === 'development' ? 'Development' : 'Production'}
                  </Text>
                </View>
                <View style={adminStyles.notesSection}>
                  <Text style={adminStyles.notesLabel}>Version</Text>
                  <Text style={adminStyles.notesText}>1.0.0</Text>
                </View>
                <View style={adminStyles.notesSection}>
                  <Text style={adminStyles.notesLabel}>Last Deployment</Text>
                  <Text style={adminStyles.notesText}>
                    {new Date().toLocaleDateString()}
                  </Text>
                </View>
                </View>
              </View>
            </View>
          </View>
        )}

        {includes('feature-flags') && (
          <View>
            {sectionHeader('feature-flags')}
            <AdminFeatureFlags
              featureFlags={featureFlags}
              persistFeatureFlag={persistFeatureFlag}
              persistTourPlannerFlag={persistTourPlannerFlag}
              adminStyles={adminStyles}
              colors={adminColors}
            />
            <AdminMobileFeatureFlags
              adminStyles={adminStyles}
              colors={adminColors}
              setNotification={setNotification}
            />
          </View>
        )}

        {includes('credit-economy') && (
          <View>
            {sectionHeader('credit-economy')}
            <AdminCreditEconomy
              adminStyles={adminStyles}
              colors={adminColors}
              onNotify={setNotification}
            />
          </View>
        )}

        {(includes('email') || includes('maintenance')) && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
        {includes('email') && (
          <View style={{ flex: 1, minWidth: 320 }}>
            {sectionHeader('email')}
            {/* Email System */}
            <View style={adminStyles.devModeSection}>
              <Text style={adminStyles.cardTitle}>Email System</Text>
              <View style={adminStyles.devModeContent}>
                <Text style={adminStyles.devModeText}>
                  Test email templates by sending them to yourself or other admin users.
                </Text>

                  <View style={adminStyles.devModeControls}>
                  <View style={adminStyles.selectContainer}>
                    <select
                      className="dev-mode-select"
                      style={domSelectStyle}
                      value={selectedEmail}
                      onChange={(e) => setSelectedEmail(e.target.value)}
                    >
                      <option value="">Select Recipient</option>
                      {users
                        .filter(u => u.userType === 'admin')
                        .map(user => (
                          <option key={user.id} value={user.email}>
                            {user.email} ({user.firstName} {user.lastName})
                      </option>
                        ))
                      }
                    </select>
                  </View>

                  <View style={adminStyles.selectContainer}>
                    <select
                      className="dev-mode-select"
                      style={domSelectStyle}
                      value={selectedTemplate}
                      onChange={(e) => setSelectedTemplate(e.target.value)}
                    >
                      {emailTemplates.map(template => (
                        <option key={template.value} value={template.value}>
                          {template.label}
                      </option>
                      ))}
                    </select>
                  </View>

                  {selectedTemplate === 'registration' && (
                    <View style={adminStyles.selectContainer}>
                      <select
                        className="dev-mode-select"
                        style={domSelectStyle}
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                      >
                        <option value="">Select User for Registration</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.email} ({user.firstName} {user.lastName})
                          </option>
                        ))}
                      </select>
                    </View>
                  )}

                    <TouchableOpacity
                    style={[
                      adminStyles.actionButton,
                      { backgroundColor: '#0bb5df', width: '100%' }
                    ]}
                    onPress={handleTestEmail}
                    disabled={!selectedEmail || !selectedTemplate || (selectedTemplate === 'registration' && !selectedUserId)}
                  >
                    <Text style={adminStyles.buttonText}>Send Test Email</Text>
                    </TouchableOpacity>
                </View>
                  </View>
                </View>
              </View>
            )}

        {includes('maintenance') && (
          <View style={{ flex: 1, minWidth: 320 }}>
            {sectionHeader('maintenance')}
            {/* Maintenance Mode */}
            <View style={adminStyles.devModeSection}>
              <Text style={adminStyles.cardTitle}>Maintenance Mode</Text>
              <View style={adminStyles.devModeContent}>
                <Text style={adminStyles.devModeText}>
                  Manage system maintenance mode and schedules.
                </Text>

                <View style={adminStyles.maintenanceForm}>
                  <TextInput
                    style={adminStyles.maintenanceInput}
                    placeholder="Enter your password to confirm"
                    placeholderTextColor={reportSearchPlaceholderColor}
                    secureTextEntry
                    value={maintenanceForm.password}
                    onChangeText={(text) => setMaintenanceForm(prev => ({ ...prev, password: text }))}
                  />

                  <TextInput
                    style={adminStyles.maintenanceInput}
                    placeholder="Maintenance message (optional)"
                    placeholderTextColor={reportSearchPlaceholderColor}
                    multiline
                    numberOfLines={3}
                    value={maintenanceForm.message}
                    onChangeText={(text) => setMaintenanceForm(prev => ({ ...prev, message: text }))}
                  />

                  <View style={adminStyles.actionButtonsContainer}>
                    <TouchableOpacity
                      style={[
                        adminStyles.actionButton,
                        { backgroundColor: '#e74c3c', flex: 1 }
                      ]}
                      onPress={handleStartMaintenance}
                      disabled={!maintenanceForm.password}
                    >
                      <Text style={adminStyles.buttonText}>
                        Start Maintenance Now
                      </Text>
                    </TouchableOpacity>

                    {maintenanceMode && (
                      <TouchableOpacity
                        style={[
                          adminStyles.actionButton,
                          { backgroundColor: '#2ecc71', flex: 1 }
                        ]}
                        onPress={handleStopMaintenance}
                      >
                        <Text style={adminStyles.buttonText}>
                          Stop Maintenance
                        </Text>
                      </TouchableOpacity>
                    )}
                </View>

                  <View style={[adminStyles.maintenanceSchedule, { marginTop: 20 }]}>
                    <TextInput
                      style={[adminStyles.maintenanceInput, { flex: 1 }]}
                      placeholder="Duration (hours)"
                      placeholderTextColor={reportSearchPlaceholderColor}
                      keyboardType="numeric"
                      value={maintenanceForm.duration}
                      onChangeText={(text) => setMaintenanceForm(prev => ({ ...prev, duration: text }))}
                    />

                    <input
                      type="datetime-local"
                      style={{
                        flex: 2,
                        backgroundColor: adminColors.inputBg,
                        color: adminColors.inputText,
                        border: `1px solid ${adminColors.inputBorder}`,
                        borderRadius: 8,
                        padding: 10,
                        fontFamily: 'VastagoGrotesk',
                      }}
                      value={maintenanceForm.scheduledStart}
                      onChange={(e) => setMaintenanceForm(prev => ({ 
                        ...prev, 
                        scheduledStart: e.target.value 
                      }))}
                    />
                </View>

                  <TouchableOpacity
                    style={[
                      adminStyles.actionButton,
                      { width: '100%', marginTop: 10 }
                    ]}
                    onPress={handleMaintenanceSubmit}
                    disabled={!maintenanceForm.password || !maintenanceForm.duration}
                  >
                    <Text style={adminStyles.buttonText}>
                      Schedule Maintenance
                    </Text>
                  </TouchableOpacity>
                </View>

                {/* Scheduled Maintenance List */}
                <View style={[adminStyles.maintenanceForm, { marginTop: 20 }]}>
                  <Text style={[adminStyles.cardTitle, { textAlign: 'left', marginBottom: 10 }]}>
                    Scheduled Maintenance
                  </Text>
                  
                  {maintenanceList.length === 0 ? (
                    <Text style={adminStyles.devModeText}>No scheduled maintenance</Text>
                  ) : (
                    maintenanceList.map(schedule => (
                      <View key={schedule.id} style={adminStyles.maintenanceScheduleItem}>
                        <View style={{ flex: 1 }}>
                          <Text style={adminStyles.maintenanceScheduleTime}>
                            {new Date(schedule.startTime.toDate()).toLocaleString()}
                          </Text>
                          <Text style={adminStyles.maintenanceScheduleDuration}>
                            Duration: {schedule.duration} hours
                          </Text>
                          {schedule.message && (
                            <Text style={adminStyles.maintenanceScheduleMessage}>
                              {schedule.message}
                            </Text>
                          )}
                </View>
                        <TouchableOpacity
                          style={[adminStyles.actionButton, { backgroundColor: '#e74c3c' }]}
                          onPress={() => handleUnscheduleMaintenance(schedule.id)}
                        >
                          <Text style={adminStyles.buttonText}>Unschedule</Text>
                        </TouchableOpacity>
              </View>
                    ))
                  )}
            </View>
              </View>
            </View>
          </View>
        )}
          </View>
        )}

        {includes('manuals') && (
          <View>
            {sectionHeader('manuals')}
            {/* Manuals Database Management */}
            <View style={adminStyles.devModeSection}>
              <Text style={adminStyles.cardTitle}>Equipment Manuals Database</Text>
              <View style={adminStyles.devModeContent}>
                <Text style={adminStyles.devModeText}>
                  Manage the equipment manuals database used across the site for venue suggestions. 
                  Currently displaying {Object.keys(editableManuals).length} manuals.
                </Text>

                {/* Search and Filter Controls */}
                <View style={adminStyles.manualsControls}>
                  <View style={adminStyles.searchContainer}>
              <TextInput
                style={adminStyles.searchInput}
                      placeholder="Search manuals..."
                placeholderTextColor={reportSearchPlaceholderColor}
                      value={manualsSearchQuery}
                      onChangeText={setManualsSearchQuery}
                    />
                  </View>

                  <View style={adminStyles.selectContainer}>
                    <select
                      className="dev-mode-select"
                      style={domSelectStyle}
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="all">All Categories</option>
                      <option value="audio">Audio Consoles</option>
                      <option value="pa">PA Systems</option>
                      <option value="lighting">Lighting</option>
                      <option value="video">Video</option>
                    </select>
                  </View>

                  <View style={{ flexDirection: 'row', gap: 10 }}>
                <TouchableOpacity
                      style={[adminStyles.actionButton, { backgroundColor: '#3498db' }]}
                      onPress={handleSyncManualsDatabase}
                    >
                      <Text style={adminStyles.buttonText}>Sync Database</Text>
                </TouchableOpacity>

                <TouchableOpacity
                      style={[adminStyles.actionButton, { backgroundColor: '#2ecc71' }]}
                      onPress={() => setAddingNewManual(true)}
                    >
                      <Text style={adminStyles.buttonText}>Add New Manual</Text>
                </TouchableOpacity>
                  </View>
                </View>

                {/* Add New Manual Form */}
                {addingNewManual && (
                  <View style={adminStyles.manualForm}>
                    <Text style={adminStyles.formTitle}>Add New Manual</Text>

                    <TextInput
                      style={adminStyles.formInput}
                      placeholder="Manual ID"
                      placeholderTextColor={reportSearchPlaceholderColor}
                      value={newManualForm.id}
                      onChangeText={(text) => setNewManualForm(prev => ({ ...prev, id: text }))}
                    />
                    
                    <TextInput
                      style={adminStyles.formInput}
                      placeholder="Keyword (e.g., 'dlive', 'wing')"
                      placeholderTextColor={reportSearchPlaceholderColor}
                      value={newManualForm.searchWord}
                      onChangeText={(text) => setNewManualForm(prev => ({ ...prev, searchWord: text }))}
                    />

                    <TextInput
                      style={adminStyles.formInput}
                      placeholder="Manual Name (e.g., 'Allen & Heath DLive')"
                      placeholderTextColor={reportSearchPlaceholderColor}
                      value={newManualForm.name}
                      onChangeText={(text) => setNewManualForm(prev => ({ ...prev, name: text }))}
                    />

                    <TextInput
                      style={adminStyles.formInput}
                      placeholder="Manual URL"
                      placeholderTextColor={reportSearchPlaceholderColor}
                      value={newManualForm.url}
                      onChangeText={(text) => setNewManualForm(prev => ({ ...prev, url: text }))}
                    />

                    <TextInput
                      style={adminStyles.formInput}
                      placeholder="Software Name (optional)"
                      placeholderTextColor={reportSearchPlaceholderColor}
                      value={newManualForm.software.name}
                      onChangeText={(text) => setNewManualForm(prev => ({ 
                        ...prev, 
                        software: { ...prev.software, name: text }
                      }))}
                    />

                    <TextInput
                      style={adminStyles.formInput}
                      placeholder="Software URL (optional)"
                      placeholderTextColor={reportSearchPlaceholderColor}
                      value={newManualForm.software.url}
                      onChangeText={(text) => setNewManualForm(prev => ({ 
                        ...prev, 
                        software: { ...prev.software, url: text }
                      }))}
                    />

                    <View style={adminStyles.formButtons}>
                <TouchableOpacity
                        style={[adminStyles.actionButton, { backgroundColor: '#e74c3c', flex: 1 }]}
                        onPress={() => setAddingNewManual(false)}
                      >
                        <Text style={adminStyles.buttonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                        style={[adminStyles.actionButton, { backgroundColor: '#2ecc71', flex: 1 }]}
                        onPress={handleAddNewManual}
                      >
                        <Text style={adminStyles.buttonText}>Add Manual</Text>
                </TouchableOpacity>
              </View>
            </View>
                )}

                {/* Manuals List */}
                <View style={[adminStyles.manualsList, { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }]}>
                  <Text style={adminStyles.manualsCount}>
                    {getFilteredManuals().length} of {Object.keys(editableManuals).length} manuals
                  </Text>
                  
                  {getFilteredManuals().map(([keyword, manual]) => (
                    <View key={keyword} style={adminStyles.manualItem}>
                      {editingManual?.keyword === keyword ? (
                        // Edit Mode
                        <View style={adminStyles.manualEditForm}>
                          <TextInput
                            style={adminStyles.editInput}
                            value={editingManual.searchWord}
                            onChangeText={(text) => setEditingManual(prev => ({ ...prev, searchWord: text }))}
                            placeholder="Keyword"
                            placeholderTextColor={reportSearchPlaceholderColor}
                          />
                          <TextInput
                            style={adminStyles.editInput}
                            value={editingManual.name}
                            onChangeText={(text) => setEditingManual(prev => ({ ...prev, name: text }))}
                            placeholder="Manual Name"
                            placeholderTextColor={reportSearchPlaceholderColor}
                          />
                          <TextInput
                            style={adminStyles.editInput}
                            value={editingManual.url}
                            onChangeText={(text) => setEditingManual(prev => ({ ...prev, url: text }))}
                            placeholder="Manual URL"
                            placeholderTextColor={reportSearchPlaceholderColor}
                          />
                          {editingManual.software && (
                            <View>
                              <TextInput
                                style={adminStyles.editInput}
                                value={editingManual.software.name}
                                onChangeText={(text) => setEditingManual(prev => ({ 
                                  ...prev, 
                                  software: { ...prev.software, name: text }
                                }))}
                                placeholder="Software Name (optional)"
                                placeholderTextColor={reportSearchPlaceholderColor}
                              />
                              <TextInput
                                style={adminStyles.editInput}
                                value={editingManual.software.url}
                                onChangeText={(text) => setEditingManual(prev => ({ 
                                  ...prev, 
                                  software: { ...prev.software, url: text }
                                }))}
                                placeholder="Software URL (optional)"
                                placeholderTextColor={reportSearchPlaceholderColor}
                              />
                            </View>
                          )}
                          <View style={adminStyles.editButtons}>
                            <TouchableOpacity
                              style={[adminStyles.editButton, { backgroundColor: '#e74c3c' }]}
                              onPress={() => setEditingManual(null)}
                            >
                              <Text style={adminStyles.buttonText}>Cancel</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={[adminStyles.editButton, { backgroundColor: '#2ecc71' }]}
                              onPress={handleSaveManualEdit}
                            >
                              <Text style={adminStyles.buttonText}>Save</Text>
                            </TouchableOpacity>
                          </View>
                        </View>
                      ) : (
                        // View Mode
                        <View style={adminStyles.manualView}>
                          <View style={adminStyles.manualInfo}>
                            <Text style={adminStyles.manualKeyword}>{manual.searchWord}</Text>
                            <Text style={adminStyles.manualName}>{manual.name}</Text>
                            <Text style={adminStyles.manualUrl}>{manual.url}</Text>
                            {manual.software && (
                              <View style={adminStyles.softwareInfo}>
                                <Text style={adminStyles.softwareName}>Software: {manual.software.name}</Text>
                                <Text style={adminStyles.softwareUrl}>{manual.software.url}</Text>
                              </View>
                            )}
                          </View>
                          <View style={adminStyles.manualActions}>
                            <TouchableOpacity
                              style={[adminStyles.manualActionButton, { backgroundColor: '#3498db' }]}
                              onPress={() => handleEditManual(keyword, manual)}
                            >
                              <Text style={adminStyles.manualActionText}>Edit</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={[adminStyles.manualActionButton, { backgroundColor: '#e74c3c' }]}
                              onPress={() => handleDeleteManual(keyword)}
                            >
                              <Text style={adminStyles.manualActionText}>Delete</Text>
                            </TouchableOpacity>
                          </View>
                        </View>
                      )}
                    </View>
                  ))}
                </View>
              </View>
            </View>
          </View>
        )}

        {/* Add other tabs as needed */}
        {includes('database') && (
          <View>
            {sectionHeader('database')}
            <View style={adminStyles.devModeSection}>
              <Text style={adminStyles.cardTitle}>Database Management</Text>
              <View style={adminStyles.devModeContent}>
                <Text style={adminStyles.devModeText}>
                  Tools for managing the database and user data.
                </Text>
                <View style={adminStyles.actionButtonsContainer}>
                  <TouchableOpacity
                    style={[adminStyles.actionButton, { backgroundColor: '#3498db' }]}
                    onPress={handleMigrateUserIds}
                  >
                    <Text style={adminStyles.buttonText}>Fix User ID Mismatches</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>

            {/* CSV Uploader Section */}
            <View style={adminStyles.devModeSection}>
              <Text style={adminStyles.cardTitle}>CSV Venue Import</Text>
              <View style={adminStyles.devModeContent}>
                <Text style={adminStyles.devModeText}>
                  Import venues from CSV files to update the database. Google Maps embed links in the MAP Embed Link column are resolved to coordinates and saved on each venue.
                </Text>
                
                <View style={adminStyles.devModeControls}>
                  {/* File upload input */}
                  <View style={adminStyles.fileUploadContainer}>
                    <Text style={adminStyles.fileUploadLabel}>Select CSV file:</Text>
                    <View style={{ marginBottom: 10 }}>
                      <input
                        id="venue-csv-upload"
                        type="file"
                        accept=".csv,text/csv,text/plain,application/vnd.ms-excel"
                        style={{ 
                          color: adminColors.inputText,
                          backgroundColor: adminColors.inputBg,
                          padding: '10px',
                          border: `2px dashed ${adminColors.cardBorder}`,
                          borderRadius: '5px',
                          width: '100%',
                          marginBottom: '10px'
                        }}
                        onChange={(e) => {
                          const fileInput = e.target;
                          if (fileInput?.files?.length > 0) {
                            const file = fileInput.files[0];
                            console.log('File selected:', {
                              name: file.name,
                              size: file.size,
                              type: file.type
                            });
                            setImportStatus(
                              `Selected file: ${file.name}\n` +
                              `Size: ${(file.size / 1024 / 1024).toFixed(2)} MB\n` +
                              `Type: ${file.type || 'text/csv'}`
                            );
                          } else {
                            setImportStatus('No file selected');
                          }
                        }}
                      />
                    </View>
                    <Text style={{ color: '#aaa', fontSize: 12, marginBottom: 10 }}>
                      Supported format: CSV files with venue name and state columns
                    </Text>
                  </View>

                  {/* Add import mode selection */}
                  <View style={{ marginBottom: 15 }}>
                    <Text style={adminStyles.fileUploadLabel}>Import Mode:</Text>
                    <View style={{ flexDirection: 'row', marginTop: 8 }}>
                <TouchableOpacity
                  style={[
                          adminStyles.modeButton,
                          importMode === 'add' && adminStyles.modeButtonActive
                  ]}
                        onPress={() => setImportMode('add')}
                >
                        <Text style={adminStyles.modeButtonText}>Add Venues</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                          adminStyles.modeButton,
                          importMode === 'overwrite' && adminStyles.modeButtonActive,
                          { backgroundColor: importMode === 'overwrite' ? '#e74c3c' : 'rgba(231, 76, 60, 0.6)' }
                  ]}
                        onPress={() => setImportMode('overwrite')}
                >
                        <Text style={adminStyles.modeButtonText}>Overwrite All</Text>
                </TouchableOpacity>
                    </View>
                    <Text style={[adminStyles.importModeDescription, { fontSize: 12, marginTop: 4, opacity: 0.8 }]}>
                      {importMode === 'add' 
                        ? 'Add mode: Import new venues and update existing ones' 
                        : 'Overwrite mode: CAUTION - Will delete all existing venues and replace with CSV data'}
                    </Text>
                  </View>

                <TouchableOpacity
                    style={[adminStyles.actionButton, { backgroundColor: '#3498db', marginTop: 10 }]}
                    onPress={handleImportCSV}
                    disabled={isImporting}
                  >
                    <Text style={adminStyles.buttonText}>
                      {isImporting ? 'Importing...' : `Import Venues (${importMode === 'add' ? 'Add' : 'Overwrite'})`}
                    </Text>
                  </TouchableOpacity>
                </View>
                
                {/* Progress bar with status message */}
                <View style={[adminStyles.progressContainer, { marginTop: 20, marginBottom: 20 }]}>
                  <Text style={[adminStyles.progressText, { marginBottom: 10, fontWeight: 'bold' }]}>
                    {importStatus || 'Ready to import'}
                  </Text>
                  {isImporting && (
                    <View style={adminStyles.progressBar}>
                      <View 
                  style={[
                          adminStyles.progressFill, 
                          { width: `${importProgress * 100}%` }
                        ]} 
                      />
                    </View>
                  )}
                </View>
              
                {/* Additional venue management tools */}
                <View style={{ marginTop: 20 }}>
                  <Text style={adminStyles.fileUploadLabel}>Additional Tools:</Text>
                  <TouchableOpacity
                    style={[adminStyles.actionButton, { backgroundColor: '#3D7FE6', marginTop: 10 }]}
                    onPress={handleExportCSV}
                  >
                    <Text style={adminStyles.buttonText}>Download Venues CSV</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[adminStyles.actionButton, { backgroundColor: '#e74c3c', marginTop: 10 }]}
                    onPress={cleanupDuplicateVenues}
                  >
                    <Text style={adminStyles.buttonText}>Clean Up Duplicates</Text>
                </TouchableOpacity>
              </View>
                
                {/* Info section */}
                <View style={adminStyles.infoContainer}>
                  <Text style={adminStyles.infoText}>
                    This section allows you to:
                  </Text>
                  <View style={adminStyles.infoList}>
                    <Text style={adminStyles.infoListItem}>• Import venues in Add mode (merge with existing) or Overwrite mode (replace all)</Text>
                    <Text style={adminStyles.infoListItem}>• Update existing venues with new information</Text>
                    <Text style={adminStyles.infoListItem}>• Add new venues to the database</Text>
                    <Text style={adminStyles.infoListItem}>• Resolve Google Maps embed links to latitude/longitude during import</Text>
                    <Text style={adminStyles.infoListItem}>• Clean up and merge duplicate venues</Text>
            </View>
                </View>
              </View>
            </View>

            <View style={adminStyles.devModeSection}>
              <Text style={adminStyles.cardTitle}>CSV Merch Import</Text>
              <View style={adminStyles.devModeContent}>
                <Text style={adminStyles.devModeText}>
                  Import merch makers / screenprinters from a CSV. Google Maps embed links in ADDRESS are resolved to coordinates and saved on the shop.
                </Text>
                <View style={adminStyles.devModeControls}>
                  <View style={adminStyles.fileUploadContainer}>
                    <Text style={adminStyles.fileUploadLabel}>Select merch CSV file:</Text>
                    <View style={{ marginBottom: 10 }}>
                      <input
                        id="merch-csv-upload"
                        type="file"
                        accept=".csv"
                        style={{
                          color: adminColors.inputText,
                          backgroundColor: adminColors.inputBg,
                          padding: '10px',
                          border: `2px dashed ${adminColors.cardBorder}`,
                          borderRadius: '5px',
                          width: '100%',
                          marginBottom: '10px'
                        }}
                        onChange={(e) => {
                          const selected = e.target?.files?.[0];
                          setMerchImportProgress(0);
                          setMerchImportStatus(
                            selected
                              ? `Selected file: ${selected.name} (${(selected.size / 1024).toFixed(1)} KB)`
                              : 'No file selected'
                          );
                        }}
                      />
                    </View>
                    <Text style={{ color: '#aaa', fontSize: 12, marginBottom: 10 }}>
                      Columns: COMPANY, ADDRESS (Google embed), TYPE, WEBSITE, ECO, PHONE, EMAIL
                    </Text>
                  </View>
                  <TouchableOpacity
                    style={[adminStyles.actionButton, { backgroundColor: '#f39c12', marginTop: 10 }]}
                    onPress={handleImportMerchCSV}
                    disabled={isImportingMerch}
                  >
                    <Text style={adminStyles.buttonText}>
                      {isImportingMerch ? 'Importing merch…' : 'Import Merch CSV'}
                    </Text>
                  </TouchableOpacity>
                </View>
                <View style={[adminStyles.progressContainer, { marginTop: 20, marginBottom: 8 }]}>
                  <Text style={[adminStyles.progressText, { marginBottom: 10, fontWeight: 'bold' }]}>
                    {merchImportStatus || 'Ready to import merch'}
                  </Text>
                  {isImportingMerch && (
                    <View style={adminStyles.progressBar}>
                      <View
                        style={[
                          adminStyles.progressFill,
                          { width: `${merchImportProgress * 100}%` }
                        ]}
                      />
                    </View>
                  )}
                </View>
              </View>
            </View>
          </View>
        )}




        {includes('dev-tools') && (
          <View>
            {sectionHeader('dev-tools')}
            {isDev && (
              <View style={adminStyles.devModeSection}>
                <Text style={adminStyles.cardTitle}>Development Tools</Text>
                <View style={adminStyles.devModeContent}>
                  <Text style={adminStyles.devModeText}>
                    Tools for development and testing (only visible in development mode).
                  </Text>
                  <View style={adminStyles.devModeControls}>
                    <View style={adminStyles.selectContainer}>
                      <select
                        className="dev-mode-select"
                        style={domSelectStyle}
                        value={devModeEmail}
                        onChange={(e) => handleDevModeUserChange(e.target.value)}
                      >
                        <option value="">Select Test User</option>
                        <option value={Constants.expoConfig.extra.devAdminEmail}>Admin User</option>
                        <option value={Constants.expoConfig.extra.devUserEmail}>Regular User</option>
                      </select>
                    </View>
                  </View>
                </View>
              </View>
            )}
          </View>
        )}
      </View>
    );
  };

  // Add this function before renderContent
  // Update the handleApproveVerification function
  const handleApproveVerification = async (user) => {
    try {
      const db = getFirestore();
      const functions = getFunctions();
      const auth = getAuth();
      const userRef = doc(db, 'users', user.id);

      // Check for existing verification request
      const userReportsRef = collection(db, 'user_reports');
      const q = query(
        userReportsRef,
        where('userId', '==', user.id),
        where('type', '==', 'verification'),
        where('status', '==', 'pending')
      );
      const existingReports = await getDocs(q);

      // If there's a pending request, update it
      if (!existingReports.empty) {
        const reportRef = doc(db, 'user_reports', existingReports.docs[0].id);
        await updateDoc(reportRef, {
          status: 'approved',
          approvedAt: new Date(),
          approvedBy: auth.currentUser?.uid
        });
      }

      // Update user document
      await updateDoc(userRef, {
        verificationStatus: 'approved',
        verifiedAt: new Date(),
        verifiedBy: auth.currentUser?.uid
      });

      // Try to send verification approval email (non-critical)
      try {
        const handleVerificationStatus = httpsCallable(functions, 'handleVerificationStatus');
        await handleVerificationStatus({
          userData: { ...user, uid: user.id },
          status: 'approved'
        });
      } catch (emailError) {
        console.warn('Email sending failed, but verification succeeded:', emailError);
        // Don't throw error - verification was successful
      }

      fetchUsers();
      alert('User verified successfully');
    } catch (error) {
      console.error('Error verifying user:', error);
      alert('Error verifying user: ' + error.message);
    }
  };

  // Add column selector component
  // Add this effect to fetch verifier names
  useEffect(() => {
    const fetchVerifierNames = async () => {
      const db = getFirestore();
      const verifiedUsers = users.filter(user => user.verifiedBy);
      const names = {};
      
      for (const user of verifiedUsers) {
        if (user.verifiedBy && !verifierNames[user.verifiedBy]) {
          try {
            const verifierDoc = await getDoc(doc(db, 'users', user.verifiedBy));
            if (verifierDoc.exists()) {
              const verifier = verifierDoc.data();
              names[user.verifiedBy] = `${verifier.firstName} ${verifier.lastName}`;
            }
          } catch (error) {
            console.error('Error fetching verifier name:', error);
          }
        }
      }
      
      setVerifierNames(prev => ({ ...prev, ...names }));
    };

    if (users.length > 0) {
      fetchVerifierNames();
    }
  }, [users]);




  // Add function to load new venue submissions
  const loadNewVenueSubmissions = async () => {
    try {
      const db = getFirestore();
      const submissionsRef = collection(db, 'new_venues');
      let q;
      
      // Only filter by status if not showing 'all'
      if (reportStatus === 'all') {
        q = query(submissionsRef);
      } else {
        q = query(submissionsRef, where('status', '==', reportStatus));
      }

      const querySnapshot = await getDocs(q);
      const submissions = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        submissions.push({
          id: doc.id,
          ...data,
          // Map the fields to match the expected structure
          venueName: data.name || 'Unnamed Venue',
          userEmail: data.submittedBy?.email,
          submittedBy: `${data.submittedBy?.firstName || ''} ${data.submittedBy?.lastName || ''}`.trim(),
          createdAt: data.createdAt,
          status: data.status || 'pending'
        });
      });
      
      console.log('Loaded new venue submissions:', submissions);
      setNewVenueSubmissions(submissions);
    } catch (error) {
      console.error('Error loading new venue submissions:', error);
    }
  };

  // Add loadNewVenueSubmissions to useEffect dependencies
  useEffect(() => {
    fetchUsers();
    calculateVenueStats();
    loadNewVenueSubmissions(); // Add this call
  }, [venues]);

  // Add another useEffect specifically for new submissions
  useEffect(() => {
    const db = getFirestore();
    const submissionsRef = collection(db, 'new_venues');
    
    // Set up real-time listener for new submissions
    const unsubscribe = onSnapshot(submissionsRef, (snapshot) => {
      const submissions = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        submissions.push({
          id: doc.id,
          ...data,
          venueName: data.name || 'Unnamed Venue',
          userEmail: data.submittedBy?.email,
          submittedBy: `${data.submittedBy?.firstName || ''} ${data.submittedBy?.lastName || ''}`.trim(),
          createdAt: data.createdAt,
          status: data.status || 'pending'
        });
      });
      console.log('Real-time update - new venue submissions:', submissions);
      setNewVenueSubmissions(submissions);
    });

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, []);

  // Add effect to reload reports when status changes
  useEffect(() => {
    if (reportType === 'venue_submission' || reportType === 'resource_submission' || reportType === 'venue_document_submission' || reportType === 'venue_archive_request' || reportType === 'spec_flag' || reportType === 'venue_interest_tag') {
      loadReports();
    }
  }, [reportType, reportStatus]);

  // Add effect to update column view on window resize
  const tabs = [
    { id: 'venues', label: 'VENUES' },
    { id: 'new_venues', label: 'NEW VENUES' },
    { id: 'venue_reports', label: 'VENUE REPORTS' },
    { id: 'resource_submissions', label: 'RESOURCES' },
    { id: 'users', label: 'USERS' },
    { id: 'site', label: 'SITE MANAGEMENT' },
    { id: 'community', label: 'COMMUNITY' },
    { id: 'apis', label: 'APIs' }
  ];

  // Add new function to handle the venue scraping
  const handleScrapeVenues = () => {
    navigation.navigate('VenueScraper');
  };

  // Add the map embeds update function inside the Admin component
  const handleUpdateMapEmbedsFromCSV = async (event) => {
    try {
      const fileInput = document.getElementById('map-embeds-csv-upload');
      const file = fileInput?.files?.[0];
      
      console.log('Starting map embeds CSV import process');
      
      if (!file) {
        alert('Error: No file selected. Please choose a CSV file.');
        return;
      }

      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        alert(`Error: Invalid file type. Got ${file.type}, expected CSV file.`);
        return;
      }

      if (!confirm('This will update ONLY the map embed links for matching venues. Continue?')) {
        return;
      }

      setIsImporting(true);
      setImportStatus('Reading map embeds CSV file...');
      
      // Read the file content first
      const fileContent = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => {
          console.error('Error reading file:', e);
          alert('Error reading file. Please try again.');
          setIsImporting(false);
        };
        reader.readAsText(file);
      });
      
      if (!fileContent) {
        alert('Error: File appears to be empty');
        setIsImporting(false);
        return;
      }

      console.log('File content preview:', fileContent.substring(0, 200));
      setImportStatus('Parsing map embeds CSV data...');
      
      const results = await new Promise((resolve) => {
        Papa.parse(fileContent, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header) => {
            console.log('Original header:', header);
            header = header.replace(/^\uFEFF/, ''); // Remove BOM
            header = header.trim();
            console.log('Transformed header:', header);
            return header;
          },
          transform: (value, column) => {
            if (typeof value === 'string') {
              value = value.replace(/^\uFEFF/, ''); // Remove BOM
              
              // Preserve HTML in map/embed columns
              if (column.toLowerCase().includes('map') || column.toLowerCase().includes('embed')) {
                return value;
              }
            }
            return value;
          },
          complete: (results) => {
            console.log('CSV parsing results:', {
              totalRows: results.data.length,
              headers: results.meta.fields,
              firstRow: results.data[0]
            });
            resolve(results);
          },
          error: (error) => {
            console.error('CSV parsing error:', error);
            resolve({ data: [], errors: [error] });
          }
        });
      });
      
      if (results.errors && results.errors.length > 0) {
        console.error('CSV parsing errors:', results.errors);
        alert(`Error parsing CSV: ${results.errors[0].message}`);
        setIsImporting(false);
        return;
      }

      if (!results.data || results.data.length === 0) {
        alert('Error: No valid data found in CSV file');
        setIsImporting(false);
        return;
      }

      console.log('CSV headers found:', results.meta.fields);
      setImportStatus(`Found ${results.data.length} rows in CSV. Mapping data...`);
      
      // Map the CSV columns to our expected property names with detailed logging
      const mapUpdates = results.data.map((row) => {
        // Helper function to find matching column value
        const findValue = (keys) => {
          for (const key of keys) {
            const value = row[key] || row[key.toLowerCase()] || row[key.toUpperCase()];
            if (value) {
              // Special handling for mapEmbedLink to normalize and validate
              if (key.toLowerCase().includes('map') || key.toLowerCase().includes('embed')) {
                console.log(`Found potential map embed for ${key}:`, value);
                const normalizedEmbed = normalizeMapEmbed(value);
                if (normalizedEmbed) {
                  console.log(`Normalized map embed:`, normalizedEmbed);
                  return normalizedEmbed;
                }
              }
              return value;
            }
          }
          return '';
        };

        return {
          name: findValue(['VENUE', 'Venue', 'venue', 'NAME', 'Name', 'name', 'VENUE NAME', 'Venue Name', 'venue_name']),
          mapEmbedLink: findValue(['MAP EMBED LINK', 'Map Embed Link', 'map_embed_link', 'MAP', 'Map', 'map', 'EMBED', 'Embed', 'embed']),
        };
      });
      
      // Filter out entries without name or valid mapEmbedLink
      const validUpdates = mapUpdates.filter(update => {
        const isValid = update.name && update.name.trim() !== '' && 
                       update.mapEmbedLink && isValidMapEmbed(update.mapEmbedLink);
        
        if (!isValid && update.name) {
          console.log(`Invalid map embed for venue "${update.name}":`, update.mapEmbedLink);
        }
        
        return isValid;
      });
      
      if (validUpdates.length === 0) {
        alert('Error: No valid venue map embed data found in CSV');
        setIsImporting(false);
        return;
      }
      
      console.log(`Found ${validUpdates.length} valid venue map embeds to update`);
      setImportStatus(`Updating map embeds for ${validUpdates.length} venues...`);
      
      // Update venues in database
      const db = getFirestore();
      const venuesRef = collection(db, 'venues');
      const venuesSnapshot = await getDocs(venuesRef);
      const allVenues = venuesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      let updateCount = 0;
      let notFoundCount = 0;
      let errorCount = 0;
      
      for (let i = 0; i < validUpdates.length; i++) {
        const update = validUpdates[i];
        setImportProgress((i + 1) / validUpdates.length);
        
        try {
          // Find matching venue by name
          const matchingVenues = allVenues.filter(venue => 
            venue.name && venue.name.toLowerCase() === update.name.toLowerCase()
          );
          
          if (matchingVenues.length === 0) {
            console.log(`No matching venue found for: ${update.name}`);
            notFoundCount++;
            continue;
          }
          
          // Update each matching venue (in case of duplicates)
          for (const venue of matchingVenues) {
            const venueRef = doc(db, 'venues', venue.id);
            await updateDoc(venueRef, {
              mapEmbedLink: update.mapEmbedLink,
              updatedAt: serverTimestamp()
            });
            
            console.log(`Updated map embed for venue: ${venue.name} (${venue.id})`);
            updateCount++;
          }
        } catch (error) {
          console.error(`Error updating venue "${update.name}":`, error);
          errorCount++;
        }
      }
      
      const summary = [
        `Successfully updated map embeds for ${updateCount} venues.`,
        notFoundCount > 0 ? `${notFoundCount} venues were not found.` : null,
        errorCount > 0 ? `${errorCount} errors occurred during update.` : null
      ].filter(Boolean).join('\n');
      
      console.log('Map embed update complete:', summary);
      alert(summary);
      
      setImportStatus('Map embed update complete!');
      refreshVenues();
    } catch (error) {
      console.error('Error updating map embeds:', error);
      alert(`Error updating map embeds: ${error.message}`);
    } finally {
      setIsImporting(false);
      setImportProgress(0);
    }
  };

  // POI admin: refresh import cache stats
  const loadPoiCoverageStats = async () => {
    try {
      const snap = await getDocs(collection(db, 'poiImportCache'));
      // Only count the "regular sync" cache (version 2). Preliminary seeding writes version 3.
      const docs = snap.docs
        .map((d) => d.data() || {})
        .filter((d) => Number(d.version) === 2);
      const nowMs = Date.now();
      const totalRegions = docs.length;
      const freshRegions = docs.filter((d) => Number(d.expiresAtMs) > nowMs).length;
      const lastUpdatedMs = docs.reduce((max, d) => {
        const v = Number(d.importedAtMs);
        if (!Number.isFinite(v)) return max;
        return Math.max(max, v);
      }, 0);

      setPoiCoverageStats({
        totalRegions,
        freshRegions,
        lastUpdated: lastUpdatedMs ? new Date(lastUpdatedMs) : null,
      });
    } catch (err) {
      console.error('loadPoiCoverageStats error:', err);
    }
  };

  const loadPoisCollectionTotalCount = async () => {
    setPoisCollectionCountError(false);
    try {
      const snap = await getCountFromServer(collection(db, 'pois'));
      setPoisCollectionTotalCount(snap.data().count);
    } catch (err) {
      console.error('loadPoisCollectionTotalCount error:', err);
      setPoisCollectionCountError(true);
    }
  };

  const loadPoiSeedCoverageStats = async () => {
    try {
      const snap = await getDocs(collection(db, 'poiSeedCoverage'));
      const byCategory = {};
      const rawDocs = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() || {}),
      }));
      const usaDocs = rawDocs.filter(isUsaV1CoverageDoc);
      setPoiSeedCoverageDocs(usaDocs);

      usaDocs.forEach((d) => {
        const cat = d.category;
        if (!cat || typeof cat !== 'string') return;
        if (!byCategory[cat]) {
          byCategory[cat] = { tileDocs: 0, totalPois: 0, lastImportedAtMs: 0 };
        }
        byCategory[cat].tileDocs += 1;
        byCategory[cat].totalPois += Number(d.poiCount) || 0;
        const t = Number(d.importedAtMs);
        if (Number.isFinite(t) && t > byCategory[cat].lastImportedAtMs) {
          byCategory[cat].lastImportedAtMs = t;
        }
      });
      setPoiSeedCoverageByCategory(byCategory);
    } catch (err) {
      console.error('loadPoiSeedCoverageStats error:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'pois') {
      loadPoiCoverageStats();
      loadPoiSeedCoverageStats();
      loadPoisCollectionTotalCount();
    }
  }, [activeTab]);

  const togglePoiTypeKey = (typeKey) => {
    setPoiSelectedTypeKeys((prev) => {
      if (prev.includes(typeKey)) return prev.filter((k) => k !== typeKey);
      return [...prev, typeKey];
    });
  };

  const handlePoiBatchImport = async () => {
    const category = poiBatchCategoryKey;
    const isAllIncompleteMode = category === POI_BATCH_ALL_INCOMPLETE_KEY;
    if (!category || (!isAllIncompleteMode && !ALL_POI_CATEGORY_KEYS.includes(category))) {
      alert('Choose a POI category (or All incomplete) for this import run.');
      return;
    }

    let sleepMs = Math.max(500, Math.min(120000, Math.floor(Number(poiBatchSleepMs) || 2500)));
    if (isAllIncompleteMode) {
      sleepMs = Math.max(5000, sleepMs);
    }
    const functions = getFunctions();
    const fetchAndStorePOIs = httpsCallable(functions, 'fetchAndStorePOIs');

    const skipResume = poiBatchForceRerun || !poiBatchSkipCompleted;

    /** @type {{ category: string, tile: (typeof USA_POI_TILES)[number] }[]} */
    let jobsToRun = [];

    if (isAllIncompleteMode) {
      if (skipResume) {
        for (const cat of ALL_POI_CATEGORY_KEYS) {
          USA_POI_TILES.forEach((t) => jobsToRun.push({ category: cat, tile: t }));
        }
      } else {
        const pairList = [];
        for (const cat of ALL_POI_CATEGORY_KEYS) {
          for (const t of USA_POI_TILES) {
            pairList.push({ category: cat, tile: t });
          }
        }
        const results = await Promise.all(
          pairList.map(async ({ category: cat, tile: t }) => {
            const ref = doc(db, 'poiSeedCoverage', `${t.id}__${cat}`);
            const snap = await getDoc(ref);
            return { category: cat, tile: t, done: isPoiSeedCoverageComplete(snap) };
          })
        );
        results.forEach(({ category: cat, tile: t, done }) => {
          if (!done) jobsToRun.push({ category: cat, tile: t });
        });
      }
    } else {
      const tilesToRun = [];
      if (skipResume) {
        USA_POI_TILES.forEach((t) => tilesToRun.push(t));
      } else {
        const checks = await Promise.all(
          USA_POI_TILES.map(async (t) => {
            const ref = doc(db, 'poiSeedCoverage', `${t.id}__${category}`);
            const snap = await getDoc(ref);
            const done = isPoiSeedCoverageComplete(snap);
            return { tile: t, done };
          })
        );
        checks.forEach(({ tile, done }) => {
          if (!done) tilesToRun.push(tile);
        });
      }
      tilesToRun.forEach((t) => jobsToRun.push({ category, tile: t }));
    }

    const maxJobs = isAllIncompleteMode
      ? ALL_POI_CATEGORY_KEYS.length * USA_POI_TILE_COUNT
      : USA_POI_TILE_COUNT;
    const skippedCount = maxJobs - jobsToRun.length;

    if (jobsToRun.length === 0) {
      setPoiImportStatusText(
        isAllIncompleteMode
          ? `Nothing to import: every category × tile already has coverage. Turn off “Skip completed” or enable “Force re-run” for a full sweep.`
          : `Nothing to import: all ${USA_POI_TILE_COUNT} USA tiles already have coverage for “${category}”. Turn off “Skip completed” or enable “Force re-run” to refresh.`
      );
      await loadPoiSeedCoverageStats();
      await loadPoisCollectionTotalCount();
      return;
    }

    poiBatchAbortRef.current = false;
    setPoiImporting(true);
    let totalImported = 0;
    let totalSkippedNames = 0;
    let cachedTiles = 0;
    /** job keys that threw — re-run with Skip completed retries missing pairs */
    const failedJobKeys = [];

    try {
      setPoiImportStatusText(
        isAllIncompleteMode
          ? `Starting ${jobsToRun.length} job(s) — all categories × tiles (${skippedCount} skipped). ${sleepMs}ms between calls.`
          : `Starting ${jobsToRun.length} tile(s) for “${category}” (${skippedCount} skipped as already done).`
      );

      for (let i = 0; i < jobsToRun.length; i += 1) {
        if (poiBatchAbortRef.current) {
          setPoiImportStatusText(`Cancelled after ${i}/${jobsToRun.length} remaining jobs.`);
          break;
        }
        const job = jobsToRun[i];
        const { tile, category: jobCat } = job;
        const jobKey = `${jobCat}::${tile.id}`;
        setPoiBatchProgress({ current: i + 1, total: jobsToRun.length, label: jobKey });
        setPoiImportStatusText(
          `Job ${i + 1}/${jobsToRun.length} · ${jobCat} · ${tile.id}${isAllIncompleteMode ? ` · next gap ${sleepMs}ms` : ''}…`
        );

        try {
          const result = await fetchAndStorePOIs({
            bbox: tile.bbox,
            categories: [jobCat],
            tileKey: tile.id,
            regionId: 'usa',
            tileSetId: USA_POI_TILE_SET_ID,
          });
          const d = result?.data;
          if (d?.cached) cachedTiles += 1;
          else {
            totalImported += Number(d?.importedCount) || 0;
            totalSkippedNames += Number(d?.skippedNoNameCount) || 0;
          }
        } catch (err) {
          console.error('handlePoiBatchImport job error:', jobKey, err);
          failedJobKeys.push(jobKey);
          setPoiImportStatusText(
            `Error on ${jobKey} — continuing (${failedJobKeys.length} failed, ${jobsToRun.length - i - 1} left). Re-run with Skip completed to retry missing pairs.`
          );
        }

        if (i < jobsToRun.length - 1 && !poiBatchAbortRef.current) {
          await waitMs(sleepMs);
        }
      }

      if (!poiBatchAbortRef.current) {
        if (failedJobKeys.length === 0) {
          setPoiImportStatusText(
            isAllIncompleteMode
              ? `All-incomplete sweep done. ~${totalImported} named POIs (${cachedTiles} jobs used Overpass cache). Unnamed skipped: ${totalSkippedNames}.`
              : `Done. ~${totalImported} named POIs for “${category}” (${cachedTiles} tiles used Overpass cache). Unnamed skipped: ${totalSkippedNames}.`
          );
        } else {
          const preview = failedJobKeys.slice(0, 5).join(', ');
          const more = failedJobKeys.length > 5 ? ` (+${failedJobKeys.length - 5} more)` : '';
          setPoiImportStatusText(
            `Finished with ${failedJobKeys.length} error(s): ${preview}${more}. Imported ~${totalImported} POIs (${cachedTiles} cached). Unnamed skipped: ${totalSkippedNames}. Re-run with Skip completed to retry failed pairs only.`
          );
        }
      }
    } catch (err) {
      console.error('handlePoiBatchImport error:', err);
      setPoiImportStatusText('Import failed. Check logs.');
    } finally {
      setPoiImporting(false);
      setPoiBatchProgress({ current: 0, total: 0, label: '' });
      await loadPoiCoverageStats();
      await loadPoiSeedCoverageStats();
      await loadPoisCollectionTotalCount();
    }
  };

  const handlePoiBatchCancel = () => {
    poiBatchAbortRef.current = true;
  };

  const handlePrelimSeedUSStates = async () => {
    const functions = getFunctions();
    const seedPOIsPreliminary = httpsCallable(functions, 'seedPOIsPreliminary');

    try {
      setPoiPrelimImporting(true);
      setPoiPrelimStatusText('Preliminary seeding US states started…');

      const categoriesToSeed =
        poiSelectedTypeKeys.length > 0 ? poiSelectedTypeKeys : ALL_POI_CATEGORY_KEYS;

      const result = await seedPOIsPreliminary({
        mode: 'us_states',
        regionIds: null,
        categories: categoriesToSeed, // 1b: cap per selected type within each state
        capPerCategory: 5,
        maxRegionsPerCall: poiPrelimMaxRegions,
      });

      const d = result?.data || {};
      setPoiPrelimStatusText(
        `US seed done. Imported ${d.importedCount ?? 0} POIs across ${d.processedRegions ?? 0} states (cap ${d.regionCap ?? 5}/type).`
      );
      await loadPoiCoverageStats();
      await loadPoisCollectionTotalCount();
    } catch (err) {
      console.error('handlePrelimSeedUSStates error:', err);
      setPoiPrelimStatusText('Preliminary US seed failed. Check logs.');
    } finally {
      setPoiPrelimImporting(false);
    }
  };

  const handlePrelimSeedMajorCountries = async () => {
    const functions = getFunctions();
    const seedPOIsPreliminary = httpsCallable(functions, 'seedPOIsPreliminary');

    try {
      setPoiPrelimImporting(true);
      setPoiPrelimStatusText('Preliminary seeding major countries started…');

      const categoriesToSeed =
        poiSelectedTypeKeys.length > 0 ? poiSelectedTypeKeys : ALL_POI_CATEGORY_KEYS;

      const result = await seedPOIsPreliminary({
        mode: 'major_countries',
        regionIds: null,
        categories: categoriesToSeed, // 2b: cap per selected type within each country
        capPerCategory: 1,
        maxRegionsPerCall: poiPrelimMaxRegions,
      });

      const d = result?.data || {};
      setPoiPrelimStatusText(
        `Major-country seed done. Imported ${d.importedCount ?? 0} POIs across ${d.processedRegions ?? 0} countries (cap ${d.regionCap ?? 1}/type).`
      );
      await loadPoiCoverageStats();
      await loadPoisCollectionTotalCount();
    } catch (err) {
      console.error('handlePrelimSeedMajorCountries error:', err);
      setPoiPrelimStatusText('Preliminary major-country seed failed. Check logs.');
    } finally {
      setPoiPrelimImporting(false);
    }
  };

  const handleBakePois = async () => {
    const functions = getFunctions();
    const bakePoisGeoJSON = httpsCallable(functions, 'bakePoisGeoJSON', { timeout: 540000 });
    const maxPerCategory = Math.max(parseInt(poiBakeMaxPerCategory, 10) || 15000, 100);

    try {
      setPoiBaking(true);
      setPoiBakeStatusText(
        `Baking POIs (per category, up to ${maxPerCategory.toLocaleString()} each)… this can take a minute.`
      );

      const result = await bakePoisGeoJSON({ maxPerCategory });
      const d = result?.data || {};
      const categoryCount = Object.keys(d.categories || {}).length;
      setPoiBakeStatusText(
        `Done. Baked ${Number(d.sampledTotal ?? 0).toLocaleString()} POIs across ${categoryCount} category file(s) (cap ${Number(d.maxPerCategory ?? maxPerCategory).toLocaleString()}/category).`
      );
    } catch (err) {
      console.error('handleBakePois error:', err);
      setPoiBakeStatusText('Bake failed. Check logs.');
    } finally {
      setPoiBaking(false);
    }
  };

  const handleDeleteBakedPois = async () => {
    const functions = getFunctions();
    const deleteBakedPois = httpsCallable(functions, 'deleteBakedPois', { timeout: 120000 });

    try {
      setPoiBakeDeleting(true);
      setPoiBakeStatusText('Deleting current baked POI files…');

      const result = await deleteBakedPois({});
      const d = result?.data || {};
      setPoiBakeStatusText(`Deleted ${Number(d.deleted ?? 0).toLocaleString()} baked file(s). Re-bake to regenerate.`);
    } catch (err) {
      console.error('handleDeleteBakedPois error:', err);
      setPoiBakeStatusText('Delete failed. Check logs.');
    } finally {
      setPoiBakeDeleting(false);
    }
  };

  const renderPOIsContent = () => {
    const displayedTypeKeys = POI_TYPE_GROUPS[poiTypeModeTab] || [];
    const poiPanel = {
      backgroundColor: adminColors.cardBg,
      borderWidth: 1,
      borderColor: adminColors.cardBorder,
      borderRadius: 12,
    };
    const poiTextPrimary = { color: adminColors.textPrimary };
    const poiTextSecondary = { color: adminColors.textSecondary };
    const poiTextMuted = { color: adminColors.textMuted };
    const poiInput = {
      backgroundColor: adminColors.inputBg,
      borderColor: adminColors.inputBorder,
      color: adminColors.inputText,
      borderWidth: 1,
      borderRadius: 8,
    };
    const batchCategoryMeta =
      poiBatchCategoryKey === POI_BATCH_ALL_INCOMPLETE_KEY
        ? { label: 'All incomplete (all categories × tiles)' }
        : POI_TYPE_META_BY_KEY[poiBatchCategoryKey] || { label: poiBatchCategoryKey };
    const batchIsAllIncomplete = poiBatchCategoryKey === POI_BATCH_ALL_INCOMPLETE_KEY;

    return (
      <View style={adminStyles.sectionContainer}>
        <View
          style={{
            marginTop: 12,
            paddingVertical: 14,
            paddingHorizontal: 16,
            borderRadius: 12,
            backgroundColor: 'rgba(255,255,255,0.08)',
            borderWidth: 1,
            borderColor: 'rgba(255,255,255,0.12)',
          }}
        >
          <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, marginBottom: 4 }}>
            Total POIs in Firestore (<Text style={{ fontWeight: '800' }}>pois</Text> collection)
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
            {poisCollectionCountError ? (
              <Text style={{ color: '#e67e22', fontSize: 13, fontWeight: '700' }}>
                Couldn’t load count (permissions / network). Try re-opening this tab.
              </Text>
            ) : poisCollectionTotalCount === null ? (
              <>
                <ActivityIndicator size="small" color="#e67e22" />
                <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, marginLeft: 10 }}>Loading count…</Text>
              </>
            ) : (
              <Text style={{ color: 'white', fontWeight: '800', fontSize: 22 }}>
                {poisCollectionTotalCount.toLocaleString()}
              </Text>
            )}
          </View>
          <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10, marginTop: 6 }}>
            Worldwide total (OSM imports). Open this tab to refresh; updates after tile import and preliminary seed.
          </Text>
        </View>

        <View
          style={{
            marginTop: 16,
            paddingVertical: 14,
            paddingHorizontal: 16,
            borderRadius: 12,
            backgroundColor: 'rgba(45,120,255,0.10)',
            borderWidth: 1,
            borderColor: 'rgba(80,140,255,0.30)',
          }}
        >
          <Text style={{ color: 'white', fontWeight: '800', fontSize: 14 }}>Bake map overview</Text>
          <Text style={{ color: 'rgba(255,255,255,0.7)', marginTop: 6, fontSize: 12 }}>
            Exports the <Text style={{ fontWeight: '800' }}>pois</Text> collection into slim, gzipped GeoJSON files —
            <Text style={{ fontWeight: '800' }}> one per category</Text>. Each file is a representative sample
            (uniform across the whole dataset, so “pins from everywhere”) capped at the limit below. The tour planner
            loads only the categories it shows, clustered when zoomed out. Re-run after importing or seeding new POIs.
          </Text>

          <Text style={{ color: 'white', fontWeight: '800', fontSize: 12, marginTop: 12 }}>Max pins per category</Text>
          <TextInput
            value={poiBakeMaxPerCategory}
            onChangeText={(t) => setPoiBakeMaxPerCategory(t.replace(/[^0-9]/g, ''))}
            keyboardType="number-pad"
            placeholder="15000"
            placeholderTextColor="rgba(255,255,255,0.4)"
            editable={!poiBaking && !poiBakeDeleting}
            style={{
              marginTop: 6,
              width: 160,
              backgroundColor: 'rgba(255,255,255,0.08)',
              borderColor: 'rgba(255,255,255,0.16)',
              borderWidth: 1,
              borderRadius: 8,
              color: 'white',
              paddingVertical: 9,
              paddingHorizontal: 12,
              fontSize: 13,
            }}
          />

          <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginTop: 14 }}>
            <TouchableOpacity
              onPress={handleBakePois}
              disabled={poiBaking || poiBakeDeleting}
              style={{
                backgroundColor: poiBaking || poiBakeDeleting ? 'rgba(80,140,255,0.4)' : '#2d78ff',
                borderRadius: 10,
                paddingVertical: 11,
                paddingHorizontal: 18,
                flexDirection: 'row',
                alignItems: 'center',
                marginRight: 10,
              }}
            >
              {poiBaking ? (
                <ActivityIndicator size="small" color="white" style={{ marginRight: 8 }} />
              ) : null}
              <Text style={{ color: 'white', fontWeight: '800', fontSize: 13 }}>
                {poiBaking ? 'Baking…' : 'Bake POIs now'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={handleDeleteBakedPois}
              disabled={poiBaking || poiBakeDeleting}
              style={{
                backgroundColor: 'transparent',
                borderRadius: 10,
                borderWidth: 1,
                borderColor: poiBaking || poiBakeDeleting ? 'rgba(231,76,60,0.4)' : '#e74c3c',
                paddingVertical: 11,
                paddingHorizontal: 18,
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {poiBakeDeleting ? (
                <ActivityIndicator size="small" color="#e74c3c" style={{ marginRight: 8 }} />
              ) : null}
              <Text style={{ color: '#e74c3c', fontWeight: '800', fontSize: 13 }}>
                {poiBakeDeleting ? 'Deleting…' : 'Delete baked POIs'}
              </Text>
            </TouchableOpacity>
          </View>
          {poiBakeStatusText ? (
            <Text style={{ color: 'white', marginTop: 12, fontSize: 13 }}>{poiBakeStatusText}</Text>
          ) : null}
        </View>

        <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: '#ddd', paddingTop: 20 }}>
          <Text style={adminStyles.label}>USA tile import (one category or full sweep)</Text>
          <Text style={{ color: 'rgba(255,255,255,0.75)', marginTop: 6, fontSize: 12 }}>
            Fixed map: <Text style={{ fontWeight: '800' }}>{USA_POI_TILE_COUNT} tiles</Text> ({USA_POI_TILE_SET_ID}) — lower
            48 grid + Alaska (2) + Hawaii. Pick <Text style={{ fontWeight: '800' }}>one category</Text> (one Overpass shape
            per tile) or <Text style={{ fontWeight: '800' }}>All incomplete</Text> to walk every category × tile in order
            (min. 5s gap between calls in that mode). Delay between jobs reduces 429 rate limits. Coverage:{' '}
            <Text style={{ fontWeight: '800' }}>{'{tileId}__{category}'}</Text> in{' '}
            <Text style={{ fontWeight: '800' }}>poiSeedCoverage</Text>; re-run to resume after a failure.
          </Text>

          <Text style={{ color: 'white', fontWeight: '800', fontSize: 12, marginTop: 14 }}>Category for this run</Text>
          <TouchableOpacity
            onPress={() => setPoiBatchCategoryDropdownOpen(true)}
            style={{
              width: 280,
              marginTop: 8,
              backgroundColor: 'rgba(255,255,255,0.08)',
              borderRadius: 10,
              paddingVertical: 10,
              paddingHorizontal: 12,
              borderWidth: 1,
              borderColor: 'rgba(255,255,255,0.16)',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Text style={{ color: 'white', fontWeight: '800', fontSize: 12 }}>{batchCategoryMeta.label}</Text>
            <Text style={{ color: 'white', fontWeight: '800' }}>▾</Text>
          </TouchableOpacity>

          <Modal
            visible={poiBatchCategoryDropdownOpen}
            transparent
            animationType="fade"
            onRequestClose={() => setPoiBatchCategoryDropdownOpen(false)}
          >
            <TouchableOpacity
              style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.6)' }}
              activeOpacity={1}
              onPress={() => setPoiBatchCategoryDropdownOpen(false)}
            >
              <View
                style={{
                  position: 'absolute',
                  top: 120,
                  left: '50%',
                  transform: [{ translateX: -140 }],
                  width: 280,
                  backgroundColor: 'rgba(20,20,30,0.98)',
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: 'rgba(255,255,255,0.14)',
                  paddingVertical: 8,
                  maxHeight: 360,
                }}
              >
                <ScrollView style={{ maxHeight: 320 }}>
                  {POI_BATCH_CATEGORY_DROPDOWN_OPTIONS.map((opt) => {
                    const active = opt.key === poiBatchCategoryKey;
                    return (
                      <TouchableOpacity
                        key={opt.key}
                        onPress={() => {
                          setPoiBatchCategoryKey(opt.key);
                          setPoiBatchCategoryDropdownOpen(false);
                        }}
                        style={{
                          paddingVertical: 10,
                          paddingHorizontal: 12,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: active ? 'rgba(230,126,34,0.22)' : 'transparent',
                        }}
                      >
                        <Text style={{ color: 'white', fontWeight: '700', fontSize: 12 }}>{opt.label}</Text>
                        {active ? <Text style={{ color: '#e67e22', fontWeight: '900' }}>✓</Text> : <View />}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              </View>
            </TouchableOpacity>
          </Modal>

          <View style={{ marginTop: 14, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
            <TouchableOpacity
              onPress={() => setPoiBatchSkipCompleted((v) => !v)}
              style={{ flexDirection: 'row', alignItems: 'center' }}
            >
              <Text style={{ color: poiBatchSkipCompleted ? '#e67e22' : 'rgba(255,255,255,0.5)', fontSize: 20 }}>
                {poiBatchSkipCompleted ? '☑' : '☐'}
              </Text>
              <Text style={{ color: 'white', fontSize: 12, marginLeft: 8, maxWidth: 280 }}>
                {batchIsAllIncomplete
                  ? 'Skip tile×category pairs already in coverage (resume)'
                  : 'Skip tiles already completed for this category (resume)'}
              </Text>
            </TouchableOpacity>
          </View>
          <View style={{ marginTop: 10, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
            <TouchableOpacity
              onPress={() => setPoiBatchForceRerun((v) => !v)}
              style={{ flexDirection: 'row', alignItems: 'center' }}
            >
              <Text style={{ color: poiBatchForceRerun ? '#e67e22' : 'rgba(255,255,255,0.5)', fontSize: 20 }}>
                {poiBatchForceRerun ? '☑' : '☐'}
              </Text>
              <Text style={{ color: 'white', fontSize: 12, marginLeft: 8, maxWidth: 280 }}>
                {batchIsAllIncomplete
                  ? 'Force re-run every category × every tile (full matrix; slow)'
                  : 'Force re-run all tiles (ignores coverage; still one category)'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: '#ddd', paddingTop: 20 }}>
          <Text style={adminStyles.label}>POI types (preliminary seed only)</Text>

          {/* Mode segmented controls */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 10 }}>
            {[
              { id: 'travel', label: 'Travel' },
              { id: 'health', label: 'Health' },
              { id: 'parks', label: 'Parks' },
            ].map((tab) => {
              const active = poiTypeModeTab === tab.id;
              return (
                <TouchableOpacity
                  key={tab.id}
                  onPress={() => setPoiTypeModeTab(tab.id)}
                  style={{
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    borderRadius: 10,
                    backgroundColor: active ? '#e67e22' : 'rgba(255,255,255,0.08)',
                    marginRight: 8,
                    marginBottom: 8,
                    borderWidth: 1,
                    borderColor: active ? '#e67e22' : 'rgba(255,255,255,0.18)',
                  }}
                >
                  <Text style={{ color: 'white', fontWeight: '800', fontSize: 12 }}>{tab.label}</Text>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Compact flex-wrap toggle buttons */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 6 }}>
            {displayedTypeKeys.map((typeKey) => {
              const opt = POI_TYPE_META_BY_KEY[typeKey] || { key: typeKey, label: typeKey };
              const enabled = poiSelectedTypeKeys.includes(typeKey);
              return (
                <TouchableOpacity
                  key={typeKey}
                  onPress={() => togglePoiTypeKey(typeKey)}
                  style={{
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    borderRadius: 999,
                    backgroundColor: enabled ? '#e67e22' : 'rgba(255,255,255,0.08)',
                    marginRight: 8,
                    marginBottom: 8,
                    borderWidth: 1,
                    borderColor: enabled ? '#e67e22' : 'rgba(255,255,255,0.18)',
                  }}
                >
                  <Text style={{ color: 'white', fontWeight: '700', fontSize: 11 }}>
                    {enabled ? '✓ ' : ''}{opt.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: '#ddd', paddingTop: 20 }}>
          <Text style={adminStyles.label}>Run USA tile batch</Text>
          <Text style={{ color: 'rgba(255,255,255,0.7)', marginTop: 6, fontSize: 11 }}>
            Only named POIs are stored. Delay between jobs helps avoid Overpass rate limits.
            {batchIsAllIncomplete ? ' All incomplete mode uses at least 5000ms between jobs.' : ''}
          </Text>

          <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 12, alignItems: 'center', gap: 10 }}>
            <Text style={{ color: 'white', fontWeight: '700', fontSize: 12 }}>Delay (ms)</Text>
            <TextInput
              value={String(poiBatchSleepMs)}
              onChangeText={(t) => {
                const n = Number(t);
                if (!Number.isFinite(n)) return;
                setPoiBatchSleepMs(Math.max(500, Math.min(120000, Math.floor(n))));
              }}
              keyboardType="numeric"
              style={{
                width: 88,
                backgroundColor: 'rgba(255,255,255,0.06)',
                borderRadius: 8,
                borderWidth: 1,
                borderColor: 'rgba(255,255,255,0.16)',
                color: 'white',
                paddingVertical: 8,
                paddingHorizontal: 8,
                textAlign: 'center',
              }}
            />
          </View>

          <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 12 }}>
            <TouchableOpacity
              style={adminStyles.button}
              onPress={handlePoiBatchImport}
              disabled={poiImporting}
            >
              <Text style={adminStyles.buttonText}>
                {poiImporting ? 'Importing tiles…' : `Run import (${batchCategoryMeta.label})`}
              </Text>
            </TouchableOpacity>
            {poiImporting ? (
              <TouchableOpacity
                style={[adminStyles.button, { marginLeft: 10, backgroundColor: '#8b4513' }]}
                onPress={handlePoiBatchCancel}
              >
                <Text style={adminStyles.buttonText}>Cancel after this tile</Text>
              </TouchableOpacity>
            ) : null}
          </View>

          {poiBatchProgress.total > 0 ? (
            <Text style={{ color: 'rgba(255,255,255,0.85)', marginTop: 8, fontSize: 12 }}>
              Progress: {poiBatchProgress.current}/{poiBatchProgress.total} {poiBatchProgress.label ? `(${poiBatchProgress.label})` : ''}
            </Text>
          ) : null}

          {poiImportStatusText ? (
            <Text style={{ color: 'white', marginTop: 12, fontSize: 13 }}>{poiImportStatusText}</Text>
          ) : null}
        </View>

        <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: '#ddd', paddingTop: 20 }}>
          <Text style={adminStyles.label}>Preliminary POI Seed (beta caps)</Text>
          <Text style={{ color: 'rgba(255,255,255,0.75)', marginTop: 6, fontSize: 12 }}>
            Runs capped sampling to quickly populate POIs. For full USA coverage use “USA tile import” above (one
            category at a time).
          </Text>

          <View style={{ marginTop: 12, flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <Text style={{ color: 'white', fontWeight: '800', fontSize: 12 }}>Max regions per call</Text>
            <TextInput
              value={String(poiPrelimMaxRegions)}
              onChangeText={(t) => {
                const n = Number(t);
                if (!Number.isFinite(n)) return;
                setPoiPrelimMaxRegions(Math.max(1, Math.min(25, Math.floor(n))));
              }}
              keyboardType="numeric"
              style={{
                flex: 0,
                width: 90,
                backgroundColor: 'rgba(255,255,255,0.06)',
                borderRadius: 10,
                borderWidth: 1,
                borderColor: 'rgba(255,255,255,0.16)',
                color: 'white',
                paddingVertical: 8,
                paddingHorizontal: 10,
                textAlign: 'center',
                fontWeight: '800',
              }}
            />
          </View>

          <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 12 }}>
            <TouchableOpacity
              style={adminStyles.button}
              onPress={handlePrelimSeedUSStates}
              disabled={poiImporting || poiPrelimImporting}
            >
              <Text style={adminStyles.buttonText}>
                {poiPrelimImporting ? 'Seeding…' : 'Seed US States (1b)'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[adminStyles.button, { marginLeft: 10, flex: 1, minWidth: 200 }]}
              onPress={handlePrelimSeedMajorCountries}
              disabled={poiImporting || poiPrelimImporting}
            >
              <Text style={adminStyles.buttonText}>
                {poiPrelimImporting ? 'Seeding…' : 'Seed Major Countries (2b)'}
              </Text>
            </TouchableOpacity>
          </View>

          {poiPrelimStatusText ? (
            <Text style={{ color: 'white', marginTop: 12, fontSize: 13 }}>{poiPrelimStatusText}</Text>
          ) : null}
        </View>

        <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: '#ddd', paddingTop: 20 }}>
          <Text style={adminStyles.label}>Overpass cache (poiImportCache)</Text>
          <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, marginBottom: 8 }}>
            Used to skip redundant Overpass fetches for the same geohash area (sync runs).
          </Text>
          <View style={{ flexDirection: 'row', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
            <View style={{ padding: 12, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.06)' }}>
              <Text style={adminStyles.statNumber}>{poiCoverageStats.totalRegions}</Text>
              <Text style={adminStyles.statLabel}>Cached Regions</Text>
            </View>
            <View style={{ padding: 12, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.06)' }}>
              <Text style={adminStyles.statNumber}>{poiCoverageStats.freshRegions}</Text>
              <Text style={adminStyles.statLabel}>Fresh (not expired)</Text>
            </View>
            <View style={{ padding: 12, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.06)' }}>
              <Text style={adminStyles.statLabel}>Last import</Text>
              <Text style={{ color: 'white', fontWeight: '700', fontSize: 12 }}>
                {poiCoverageStats.lastUpdated ? poiCoverageStats.lastUpdated.toLocaleString() : '—'}
              </Text>
            </View>
          </View>
        </View>

        <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: '#ddd', paddingTop: 20 }}>
          <Text style={adminStyles.label}>Seed coverage by category (poiSeedCoverage)</Text>
          <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, marginBottom: 8 }}>
            One doc per USA tile × category. <Text style={{ fontWeight: '800' }}>importStatus</Text>: ok (fresh import) or
            cached (Overpass cache hit). Re-run the same category to fill missing tiles after errors.
          </Text>
          <ScrollView style={{ maxHeight: 220 }} nestedScrollEnabled>
            {ALL_POI_CATEGORY_KEYS.map((key) => {
              const meta = POI_TYPE_META_BY_KEY[key] || { label: key };
              const row = poiSeedCoverageByCategory[key];
              const tiles = row?.tileDocs ?? 0;
              const pois = row?.totalPois ?? 0;
              const last = row?.lastImportedAtMs
                ? new Date(row.lastImportedAtMs).toLocaleString()
                : '—';
              return (
                <View
                  key={key}
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    paddingVertical: 6,
                    borderBottomWidth: 1,
                    borderBottomColor: 'rgba(255,255,255,0.08)',
                  }}
                >
                  <Text style={{ color: 'white', fontWeight: '700', fontSize: 12, flex: 1 }}>{meta.label}</Text>
                  <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, width: 56, textAlign: 'right' }}>
                    {tiles} tiles
                  </Text>
                  <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, width: 64, textAlign: 'right' }}>
                    {pois} POIs
                  </Text>
                  <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 10, width: 120, textAlign: 'right' }}>
                    {last}
                  </Text>
                </View>
              );
            })}
          </ScrollView>
        </View>

        <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: '#ddd', paddingTop: 20 }}>
          <Text style={adminStyles.label}>Seed coverage map</Text>
          <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, marginBottom: 4 }}>
            Faint outlines show all {USA_POI_TILE_COUNT} permanent {USA_POI_TILE_SET_ID} cells; colored fills are coverage
            from Firestore (same tile set only). Colors are per category when “All” is selected.
          </Text>
          <AdminPoiCoverageMap
            docs={poiSeedCoverageDocs}
            categoryFilter={poiCoverageMapCategory}
            onCategoryChange={setPoiCoverageMapCategory}
            categoryOptions={POI_TYPE_OPTIONS}
          />
        </View>

        <Text style={{ color: 'rgba(255,255,255,0.65)', marginTop: 12, fontSize: 11 }}>
          Tile set: {USA_POI_TILE_SET_ID} · {USA_POI_TILE_COUNT} bboxes (constant; other countries can add new sets later).
        </Text>
      </View>
    );
  };

  // Update the renderAPIsContent function
  const renderAPIsContent = () => {
    return (
      <View style={adminStyles.sectionContainer}>
        <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: '#ddd', paddingTop: 20 }}>
          <Text style={adminStyles.label}>Venue Photos</Text>
                  <TouchableOpacity
            style={adminStyles.button}
            onPress={handleFetchVenuePhotos}
                  >
            <Text style={adminStyles.buttonText}>Fetch All Venue Photos</Text>
                  </TouchableOpacity>
          {venuePhotos.map((venue) => (
            <View key={venue.id} style={{ marginTop: 10 }}>
              <Text>{venue.name}: {venue.photoCount} photos</Text>
                </View>
          ))}
            </View>

        <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: '#ddd', paddingTop: 20 }}>
          <Text style={adminStyles.label}>Update IDs</Text>
              <TouchableOpacity
            style={adminStyles.button}
                onPress={handleMigrateUserIds}
              >
                <Text style={adminStyles.buttonText}>Migrate User IDs</Text>
              </TouchableOpacity>
            </View>

        <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: '#ddd', paddingTop: 20 }}>
          <Text style={adminStyles.label}>Development Mode</Text>
          <TouchableOpacity
            style={adminStyles.button}
            onPress={() => handleDevModeUserChange('dsignablellc@gmail.com')}
          >
            <Text style={adminStyles.buttonText}>Add Admin to Dev Mode</Text>
          </TouchableOpacity>
            </View>

        <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: '#ddd', paddingTop: 20 }}>
          <Text style={adminStyles.label}>Venue Data Scraper</Text>
              <TouchableOpacity
            style={adminStyles.button}
                onPress={handleScrapeVenues}
              >
            <Text style={adminStyles.buttonText}>Run Venue Scraper</Text>
              </TouchableOpacity>
            </View>
              </View>
    );
  };

  // Add the renderUserReports function
  const renderUserReports = () => {
    return (
      <ScrollView style={adminStyles.reportsContainer}>
        <View style={adminStyles.reportFilters}>
          <Text style={adminStyles.sectionTitle}>User Reports</Text>
          
          <View style={adminStyles.filterButtonsContainer}>
            {renderStatusButtons()}
          </View>
        </View>
        
        {loading ? (
          <ActivityIndicator size="large" color="#ffffff" />
        ) : reports.length === 0 ? (
          <Text style={adminStyles.emptyStateText}>No user reports found</Text>
        ) : (
          <View style={adminStyles.reportsList}>
            {reports.map(report => renderUserReport(report))}
          </View>
        )}
          </ScrollView>
        );
  };

  const archiveUserDoc = async (userId, { archiveReason, archiveKeptUserId } = {}) => {
    const db = getFirestore();
    const auth = getAuth();
    const userRef = doc(db, 'users', userId);
    const userData = (await getDoc(userRef)).data();
    if (!userData) {
      throw new Error('User profile was already removed');
    }
    await setDoc(doc(db, 'archived_users', userId), {
      ...userData,
      archivedAt: new Date(),
      archivedBy: auth.currentUser?.uid,
      ...(archiveReason ? { archiveReason } : {}),
      ...(archiveKeptUserId ? { archiveKeptUserId } : {}),
    });
    await deleteDoc(userRef);
  };

  const handleArchiveUser = async (userId) => {
    const target = users.find((user) => user.id === userId);
    const shared = target ? usersSharingEmail(users, target) : [];
    const extraCount = Math.max(0, shared.length - 1);
    const warning = extraCount
      ? `\n\n${extraCount} other profile${extraCount === 1 ? '' : 's'} share ${target.email}. Archiving THIS profile only does not disable their email login. If this is the approved account they should keep, cancel.`
      : '';

    if (!confirm(`Archive this user profile? It moves to Archived Users. Their email sign-in is not disabled.${warning}`)) {
      return;
    }

    try {
      await archiveUserDoc(userId);
      fetchUsers();
      alert('User archived successfully');
    } catch (error) {
      console.error('Error archiving user:', error);
      alert('Error archiving user: ' + error.message);
    }
  };

  const handleArchiveEmailExtras = async (keepUser) => {
    const shared = usersSharingEmail(users, keepUser);
    const picked = pickKeeperAndExtras(shared, { keepId: keepUser.id });
    const check = canArchiveEmailExtras(picked);
    if (!check.ok) {
      const messages = {
        no_keeper: 'Could not find the profile to keep.',
        keeper_not_approved: 'Keep the approved profile. Do not archive extras from an unapproved one.',
        no_extras: 'No extra profiles share this email.',
        approved_extra: 'Another profile with this email is also approved. Nothing was archived.',
      };
      alert(messages[check.reason] || 'Could not archive extras.');
      return;
    }

    if (!confirm(`Archive ${check.extras.length} extra profile${check.extras.length === 1 ? '' : 's'} for ${keepUser.email}?\n\nKeep ${[keepUser.firstName, keepUser.lastName].filter(Boolean).join(' ') || keepUser.username || 'this approved account'}. Their email sign-in stays working. Auth is not disabled.`)) {
      return;
    }

    try {
      for (const extra of check.extras) {
        await archiveUserDoc(extra.id, {
          archiveReason: 'duplicate_email_keep_approved',
          archiveKeptUserId: keepUser.id,
        });
      }
      fetchUsers();
      alert(`Archived ${check.extras.length} extra profile${check.extras.length === 1 ? '' : 's'}. ${keepUser.email} can still sign in.`);
    } catch (error) {
      console.error('Error archiving extra profiles:', error);
      alert('Error archiving extras: ' + error.message);
    }
  };

  // Organization action handlers
  const handleOrganizationAction = async (orgId, action) => {
    const db = getFirestore();
    const auth = getAuth();
    const orgRef = doc(db, 'organizations', orgId);
    
    switch (action) {
      case 'makePublic':
        try {
          await updateDoc(orgRef, { isPublic: true, updatedAt: serverTimestamp() });
          fetchOrganizations();
          alert('Organization set to public');
        } catch (error) {
          console.error('Error making organization public:', error);
          alert('Error updating organization: ' + error.message);
        }
        break;
      case 'makePrivate':
        try {
          await updateDoc(orgRef, { isPublic: false, updatedAt: serverTimestamp() });
          fetchOrganizations();
          alert('Organization set to private');
        } catch (error) {
          console.error('Error making organization private:', error);
          alert('Error updating organization: ' + error.message);
        }
        break;
    }
  };




  // Add footer component at the bottom of the return statement
  const adminPageContainerStyle = Platform.OS === 'web'
    ? {
        height: '100vh',
        minHeight: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden',
      }
    : { flex: 1 };

  return (
    <AdminProvider addManualToMasterDatabase={addManualToMasterDatabase}>
      <AdminThemeProvider>
      <View
        nativeID="admin-root"
        style={[globalStyles.container, { backgroundColor: adminColors.pageBg }, adminPageContainerStyle]}
      >
        <View 
          style={{
            flex: 1,
            width: '100%',
            height: '100%',
            backgroundColor: adminColors.pageBg,
            overflow: 'hidden',
          }}
        >
          {loading ? (
            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
              <ActivityIndicator size="large" color={adminColors.textPrimary} />
            </View>
          ) : (
            <View style={{
              flex: 1,
              flexDirection: 'row',
              width: '100%',
              overflow: 'hidden',
              minHeight: 0,
            }}>
              <AdminSidebar
                activeTab={activeTab}
                onSelectTab={handleAdminTabSelect}
                collapsed={sidebarCollapsed}
                onCollapsedChange={setSidebarCollapsed}
              />
              <ScrollView 
                style={[
                  adminStyles.container,
                  {
                    flex: 1,
                    width: '100%',
                    backgroundColor: adminColors.pageBg,
                    ...(Platform.OS === 'web' ? { marginLeft: adminSidebarWidth } : {}),
                  },
                ]}
                contentContainerStyle={{ 
                  flexGrow: 1,
                  alignItems: 'stretch',
                  paddingBottom: 40,
                }}
              >
                {renderContent()}
              </ScrollView>
            </View>
          )}

          {/* Footer */}
          <Footer isAdminPage={true} />
        </View>
        {notification && (
          <NotificationPopup
            message={notification.message}
            type={notification.type}
            onHide={() => setNotification(null)}
          />
        )}

        {/* Edit Resource Submission Modal */}
        <Modal
          visible={Boolean(editingResourceSubmission)}
          transparent
          animationType="fade"
          onRequestClose={closeEditResourceSubmission}
        >
          <View style={{
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20,
          }}>
            <View style={{
              backgroundColor: adminColors.cardBg,
              borderRadius: 12,
              padding: 20,
              width: '100%',
              maxWidth: 520,
              borderWidth: 1,
              borderColor: adminColors.cardBorder,
            }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Text style={{
                  fontFamily: 'VastagoGrotesk',
                  fontSize: 18,
                  fontWeight: 'bold',
                  color: adminColors.textPrimary,
                }}>
                  Edit Resource
                </Text>
                <TouchableOpacity onPress={closeEditResourceSubmission}>
                  <Text style={{ color: adminColors.textPrimary, fontSize: 18, fontWeight: 'bold' }}>✕</Text>
                </TouchableOpacity>
              </View>

              {editingResourceSubmission ? (
                <Text style={{ color: adminColors.textSecondary, marginBottom: 14, fontFamily: 'VastagoGrotesk' }}>
                  {editingResourceSubmission.resourceName}
                </Text>
              ) : null}

              <Text style={[adminStyles.detailLabel, { marginBottom: 10 }]}>Category</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 10, marginBottom: 18 }}>
                {[...resourceCategoryCatalog, ...(editingResourceSubmission?.category && !resourceCategoryCatalog.some((category) => category.id === editingResourceSubmission.category)
                  ? [{
                    id: editingResourceSubmission.category,
                    label: editingResourceSubmission.categoryLabel || editingResourceSubmission.category,
                    iconName: 'construct-outline',
                  }]
                  : [])].map((category) => {
                  const selected = editResourceCategory === category.id;
                  const iconSize = getResourceCategoryIconSize(category, 40);
                  return (
                    <TouchableOpacity
                      key={category.id}
                      accessibilityLabel={category.label}
                      onPress={() => {
                        setEditResourceCategory(category.id);
                        setEditResourceNewCategory('');
                      }}
                      style={{
                        minWidth: 74,
                        minHeight: 74,
                        padding: 6,
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 10,
                        backgroundColor: selected ? 'rgba(52,152,219,0.25)' : adminColors.cardBgSubtle,
                        borderWidth: 2,
                        borderColor: selected ? '#3498db' : adminColors.cardBorder,
                      }}
                    >
                      {category.source ? (
                        <Image
                          source={category.source}
                          style={{
                            width: iconSize,
                            height: iconSize,
                            ...(category.invertInLightMode && !adminIsDarkMode && Platform.OS === 'web'
                              ? { filter: 'invert(1)' }
                              : null),
                          }}
                          resizeMode="contain"
                        />
                      ) : (
                        <Ionicons name={category.iconName || 'construct-outline'} size={28} color={adminColors.textPrimary} />
                      )}
                      <Text style={{ color: adminColors.textPrimary, fontSize: 10, textAlign: 'center', fontFamily: 'VastagoGrotesk' }}>
                        {category.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <Text style={[adminStyles.detailLabel, { marginBottom: 6 }]}>Or create a new category</Text>
              <TextInput
                value={editResourceNewCategory}
                onChangeText={(value) => {
                  setEditResourceNewCategory(value);
                  if (value.trim()) setEditResourceCategory('');
                }}
                placeholder="New category name"
                placeholderTextColor={adminColors.textSecondary}
                maxLength={48}
                style={{
                  backgroundColor: adminColors.cardBgSubtle,
                  borderWidth: 1,
                  borderColor: adminColors.cardBorder,
                  borderRadius: 8,
                  padding: 10,
                  color: adminColors.textPrimary,
                  marginBottom: 18,
                }}
              />

              <Text style={[adminStyles.detailLabel, { marginBottom: 10 }]}>Cover Photo</Text>
              {editResourcePhoto.preview && !editResourcePhoto.removed ? (
                <View style={{ marginBottom: 12 }}>
                  <View style={{
                    width: '100%',
                    aspectRatio: editResourcePhoto.width && editResourcePhoto.height
                      ? editResourcePhoto.width / editResourcePhoto.height
                      : 2.5,
                    maxHeight: 200,
                    borderRadius: 8,
                    overflow: 'hidden',
                    backgroundColor: adminIsDarkMode ? '#0f1a2e' : '#f5f5f0',
                    borderWidth: 1,
                    borderColor: adminColors.cardBorder,
                  }}>
                    <Image
                      source={{ uri: editResourcePhoto.preview }}
                      style={{ width: '100%', height: '100%' }}
                      resizeMode="contain"
                    />
                  </View>
                  <View style={{ flexDirection: 'row', gap: 8, marginTop: 10 }}>
                    <TouchableOpacity
                      style={[adminStyles.actionButton, { backgroundColor: '#3498db', flex: 1 }]}
                      onPress={handleEditResourcePhotoPick}
                    >
                      <Text style={adminStyles.actionButtonText}>Replace Photo</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[adminStyles.actionButton, { backgroundColor: '#7f8c8d', flex: 1 }]}
                      onPress={() => setEditResourcePhoto((prev) => ({ ...prev, file: null, preview: null, width: null, height: null, removed: true }))}
                    >
                      <Text style={adminStyles.actionButtonText}>Remove</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <TouchableOpacity
                  style={[adminStyles.actionButton, { backgroundColor: '#3498db', marginBottom: 12 }]}
                  onPress={handleEditResourcePhotoPick}
                >
                  <Text style={adminStyles.actionButtonText}>Add Cover Photo</Text>
                </TouchableOpacity>
              )}

              <View style={{ flexDirection: 'row', gap: 10, marginTop: 8 }}>
                <TouchableOpacity
                  style={[adminStyles.actionButton, { backgroundColor: '#95a5a6', flex: 1 }]}
                  onPress={closeEditResourceSubmission}
                  disabled={isSavingResourceEdit}
                >
                  <Text style={adminStyles.actionButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[adminStyles.actionButton, { backgroundColor: '#2ecc71', flex: 1, opacity: isSavingResourceEdit ? 0.6 : 1 }]}
                  onPress={handleSaveResourceSubmissionEdit}
                  disabled={isSavingResourceEdit}
                >
                  {isSavingResourceEdit ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={adminStyles.actionButtonText}>Save Changes</Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

        {/* Reject Report Modal */}
        <Modal
          visible={showRejectModal}
          transparent={true}
          animationType="fade"
          onRequestClose={() => {
            setShowRejectModal(false);
            setRejectingReport(null);
            setDenyReason('');
          }}
        >
          <View style={{
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20
          }}>
            <View style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 10,
              padding: 20,
              width: '100%',
              maxWidth: 500
            }}>
              <Text style={{
                fontFamily: 'VastagoGrotesk',
                fontSize: 18,
                fontWeight: 'bold',
                color: '#000000',
                marginBottom: 15
              }}>
                Reject Report
              </Text>
              
              <Text style={{
                fontFamily: 'VastagoGrotesk',
                fontSize: 14,
                color: '#000000',
                marginBottom: 10
              }}>
                Please provide a reason for rejecting this report (optional):
              </Text>
              
              <TextInput
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 8,
                  padding: 12,
                  fontFamily: 'VastagoGrotesk',
                  fontSize: 14,
                  color: '#000000',
                  borderWidth: 1,
                  borderColor: 'rgba(0, 0, 0, 0.2)',
                  minHeight: 100,
                  textAlignVertical: 'top',
                  marginBottom: 15
                }}
                multiline
                numberOfLines={4}
                value={denyReason}
                onChangeText={setDenyReason}
                placeholder="Enter rejection reason..."
                placeholderTextColor="rgba(0, 0, 0, 0.5)"
              />
              
              <View style={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                gap: 10
              }}>
                <TouchableOpacity
                  style={{
                    borderRadius: 8,
                    paddingVertical: 10,
                    paddingHorizontal: 20,
                    backgroundColor: '#95a5a6',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 100
                  }}
                  onPress={() => {
                    setShowRejectModal(false);
                    setRejectingReport(null);
                    setDenyReason('');
                  }}
                >
                  <Text style={{
                    color: '#ffffff',
                    fontFamily: 'VastagoGrotesk',
                    fontSize: 14,
                    fontWeight: '500'
                  }}>
                    Cancel
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    borderRadius: 8,
                    paddingVertical: 10,
                    paddingHorizontal: 20,
                    backgroundColor: '#e74c3c',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 100,
                    opacity: isRejectingReport ? 0.5 : 1
                  }}
                  onPress={confirmRejectReport}
                  disabled={isRejectingReport}
                >
                  {isRejectingReport ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={{
                      color: '#ffffff',
                      fontFamily: 'VastagoGrotesk',
                      fontSize: 14,
                      fontWeight: '500'
                    }}>
                      Confirm Reject
                    </Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

        {/* Upload Review Modal */}
        <Modal
          visible={showUploadReviewModal}
          transparent={true}
          animationType="fade"
          onRequestClose={() => {
            setShowUploadReviewModal(false);
            setPendingVenueUpdates([]);
          }}
        >
          <View style={{
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20
          }}>
            <View style={{
              backgroundColor: 'rgba(30, 30, 30, 0.98)',
              borderRadius: 10,
              padding: 20,
              width: '100%',
              maxWidth: 1400,
              maxHeight: '90%',
              overflow: 'hidden',
              borderWidth: 1,
              borderColor: 'rgba(255, 255, 255, 0.1)'
            }}>
              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
                borderBottomWidth: 1,
                borderBottomColor: 'rgba(255, 255, 255, 0.1)',
                paddingBottom: 15
              }}>
                <Text style={{
                  fontFamily: 'VastagoGrotesk',
                  fontSize: 20,
                  fontWeight: 'bold',
                  color: '#ffffff'
                }}>
                  Review Tech Specs Upload ({pendingVenueUpdates.length} venue{pendingVenueUpdates.length !== 1 ? 's' : ''})
                </Text>
                <TouchableOpacity
                  style={{
                    padding: 8,
                    borderRadius: 15,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }}
                  onPress={() => {
                    setShowUploadReviewModal(false);
                    setPendingVenueUpdates([]);
                  }}
                >
                  <Text style={{
                    fontSize: 16,
                    fontWeight: 'bold',
                    color: '#ffffff'
                  }}>✕</Text>
                </TouchableOpacity>
              </View>

              <ScrollView style={{ flex: 1 }}>
                {pendingVenueUpdates.map((venueUpdate, venueIndex) => (
                  <View key={venueUpdate.venueId} style={{
                    marginBottom: 30,
                    padding: 15,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: 'rgba(255, 255, 255, 0.1)'
                  }}>
                    <Text style={{
                      fontFamily: 'VastagoGrotesk',
                      fontSize: 18,
                      fontWeight: 'bold',
                      color: '#ffffff',
                      marginBottom: 10
                    }}>
                      {venueUpdate.venueName}
                    </Text>
                    <Text style={{
                      fontFamily: 'VastagoGrotesk',
                      fontSize: 12,
                      color: 'rgba(255, 255, 255, 0.6)',
                      marginBottom: 15
                    }}>
                      Report from: {venueUpdate.reportEmail}
                    </Text>

                    {Object.entries(venueUpdate.fields).map(([field, data]) => (
                      <View key={field} style={{
                        marginBottom: 15,
                        padding: 12,
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: 'rgba(255, 255, 255, 0.1)'
                      }}>
                        <Text style={{
                          fontFamily: 'VastagoGrotesk',
                          fontSize: 14,
                          fontWeight: 'bold',
                          color: '#ffffff',
                          marginBottom: 8,
                          textTransform: 'capitalize'
                        }}>
                          {field.replace(/([A-Z])/g, ' $1').trim()}
                        </Text>

                        <View style={{
                          flexDirection: 'row',
                          gap: 10,
                          marginBottom: 10
                        }}>
                          <View style={{ flex: 1 }}>
                            <Text style={{
                              fontFamily: 'VastagoGrotesk',
                              fontSize: 11,
                              color: 'rgba(255, 255, 255, 0.7)',
                              marginBottom: 4,
                              fontWeight: '500'
                            }}>Previous:</Text>
                            <View style={{
                              backgroundColor: 'rgba(231, 76, 60, 0.2)',
                              padding: 8,
                              borderRadius: 4,
                              minHeight: 40,
                              borderLeftWidth: 3,
                              borderLeftColor: '#e74c3c'
                            }}>
                              <Text style={{
                                fontFamily: 'VastagoGrotesk',
                                fontSize: 12,
                                color: '#ffffff'
                              }}>
                                {data.previous}
                              </Text>
                            </View>
                          </View>

                          <View style={{ flex: 1 }}>
                            <Text style={{
                              fontFamily: 'VastagoGrotesk',
                              fontSize: 11,
                              color: 'rgba(255, 255, 255, 0.7)',
                              marginBottom: 4,
                              fontWeight: '500'
                            }}>Updating:</Text>
                            <View style={{
                              backgroundColor: 'rgba(52, 152, 219, 0.2)',
                              padding: 8,
                              borderRadius: 4,
                              minHeight: 40,
                              borderLeftWidth: 3,
                              borderLeftColor: '#3498db'
                            }}>
                              <Text style={{
                                fontFamily: 'VastagoGrotesk',
                                fontSize: 12,
                                color: '#ffffff'
                              }}>
                                {data.updating}
                              </Text>
                            </View>
                          </View>

                          <View style={{ flex: 1 }}>
                            <Text style={{
                              fontFamily: 'VastagoGrotesk',
                              fontSize: 11,
                              color: 'rgba(255, 255, 255, 0.7)',
                              marginBottom: 4,
                              fontWeight: '500'
                            }}>Result:</Text>
                            <View style={{
                              backgroundColor: 'rgba(46, 204, 113, 0.2)',
                              padding: 8,
                              borderRadius: 4,
                              minHeight: 40,
                              borderLeftWidth: 3,
                              borderLeftColor: '#2ecc71'
                            }}>
                              <Text style={{
                                fontFamily: 'VastagoGrotesk',
                                fontSize: 12,
                                color: '#ffffff'
                              }}>
                                {data.result}
                              </Text>
                            </View>
                          </View>
                        </View>

                        <View>
                          <Text style={{
                            fontFamily: 'VastagoGrotesk',
                            fontSize: 11,
                            color: 'rgba(255, 255, 255, 0.7)',
                            marginBottom: 4,
                            fontWeight: '500'
                          }}>Final Value (Editable):</Text>
                          <TextInput
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              borderRadius: 8,
                              padding: 12,
                              fontFamily: 'VastagoGrotesk',
                              fontSize: 14,
                              color: '#ffffff',
                              borderWidth: 1,
                              borderColor: 'rgba(255, 255, 255, 0.2)',
                              minHeight: 60,
                              textAlignVertical: 'top'
                            }}
                            multiline
                            numberOfLines={3}
                            value={data.final}
                            onChangeText={(value) => handleUpdateFinalValue(venueIndex, field, value)}
                            placeholder="Edit final value..."
                            placeholderTextColor={reportSearchPlaceholderColor}
                          />
                        </View>
                      </View>
                    ))}
                  </View>
                ))}
              </ScrollView>

              <View style={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                gap: 10,
                marginTop: 20,
                paddingTop: 20,
                borderTopWidth: 1,
                borderTopColor: 'rgba(255, 255, 255, 0.1)'
              }}>
                <TouchableOpacity
                  style={{
                    borderRadius: 8,
                    paddingVertical: 12,
                    paddingHorizontal: 24,
                    backgroundColor: 'rgba(149, 165, 166, 0.3)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 120,
                    borderWidth: 1,
                    borderColor: 'rgba(149, 165, 166, 0.5)'
                  }}
                  onPress={() => {
                    setShowUploadReviewModal(false);
                    setPendingVenueUpdates([]);
                  }}
                >
                  <Text style={{
                    color: '#ffffff',
                    fontFamily: 'VastagoGrotesk',
                    fontSize: 14,
                    fontWeight: '500'
                  }}>
                    Cancel
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    borderRadius: 8,
                    paddingVertical: 12,
                    paddingHorizontal: 24,
                    backgroundColor: '#2ecc71',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 120,
                    opacity: isFinishingUpload ? 0.5 : 1
                  }}
                  onPress={handleFinishUpload}
                  disabled={isFinishingUpload}
                >
                  {isFinishingUpload ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={{
                      color: '#ffffff',
                      fontFamily: 'VastagoGrotesk',
                      fontSize: 14,
                      fontWeight: '500'
                    }}>
                      Finish Upload
                    </Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
    </View>
      </AdminThemeProvider>
    </AdminProvider>
  );
} 

// Add this helper function before handleImportCSV
const normalizeCountry = (value) => {
  if (!value) return '';
  
  const countryMappings = {
    // Common variations for UK
    'uk': 'United Kingdom',
    'u.k.': 'United Kingdom',
    'united kingdom': 'United Kingdom',
    'great britain': 'United Kingdom',
    'england': 'United Kingdom',
    'scotland': 'United Kingdom',
    'wales': 'United Kingdom',
    'northern ireland': 'United Kingdom',
    'britain': 'United Kingdom',
    
    // Common variations for USA
    'usa': 'United States',
    'u.s.a.': 'United States',
    'u.s.': 'United States',
    'united states': 'United States',
    'united states of america': 'United States',
    'america': 'United States',
    'us': 'United States',
    'unknown': 'United States', // Default "Unknown" to United States
    
    // European countries
    'republic of ireland': 'Ireland',
    'eire': 'Ireland',
    'deutschland': 'Germany',
    'bundesrepublik': 'Germany',
    'holland': 'Netherlands',
    'the netherlands': 'Netherlands',
    'nederland': 'Netherlands',
    'belgië': 'Belgium',
    'belgique': 'Belgium',
    'schweiz': 'Switzerland',
    'suisse': 'Switzerland',
    'svizzera': 'Switzerland',
    'españa': 'Spain',
    'italia': 'Italy',
    'sverige': 'Sweden',
    'norge': 'Norway',
    'danmark': 'Denmark',
    'suomi': 'Finland',
    'österreich': 'Austria',
    'polska': 'Poland',
    'česká republika': 'Czech Republic',
    'ceska republika': 'Czech Republic',
    'magyarország': 'Hungary',
    'magyarorszag': 'Hungary',
    
    // North America
    'can': 'Canada',
    'méxico': 'Mexico',
    'mejico': 'Mexico',
    
    // Asia
    '中国': 'China',
    'zhongguo': 'China',
    '日本': 'Japan',
    'nippon': 'Japan',
    '한국': 'South Korea',
    'hanguk': 'South Korea',
    'republic of korea': 'South Korea',
    
    // Oceania
    'aus': 'Australia',
    'nz': 'New Zealand',
    'aotearoa': 'New Zealand'
  };
  
  const normalized = value.toLowerCase().trim();
  return countryMappings[normalized] || value;
};

// Modify the findValue function in handleImportCSV to use normalizeCountry
const findValue = (keys) => {
  for (const key of keys) {
    const value = row[key] || row[key.toLowerCase()] || row[key.toUpperCase()];
    if (value) {
      // Special handling for mapEmbedLink to preserve full HTML iframe code
      if (key.toLowerCase().includes('map') || key.toLowerCase().includes('embed')) {
        console.log(`Found potential map embed for ${key}:`, value);
        return value;
      }
      
      // Normalize country names in state/location fields
      if (key.toLowerCase().includes('state') || 
          key.toLowerCase().includes('country') || 
          key.toLowerCase().includes('location')) {
        const normalizedValue = normalizeCountry(value);
        console.log(`Found and normalized value for ${key}:`, {
          original: value,
          normalized: normalizedValue
        });
        return normalizedValue;
      }
      console.log(`Found value for ${key}:`, value);
      return value;
    }
  }
  
  // Special handling for STATE/COUNTRY column
  if (keys.some(k => k.includes('STATE') || k.includes('COUNTRY')) && 
      row['STATE/COUNTRY'] !== undefined) {
    const value = row['STATE/COUNTRY'];
    const normalizedValue = normalizeCountry(value);
    console.log(`Found value for STATE/COUNTRY:`, {
      original: value,
      normalized: normalizedValue
    });
    return normalizedValue;
  }
  
  return '';
};

// Add this helper function for US location detection
const isLikelyUSLocation = (city) => {
  if (!city) return false;
  
  // Common major US cities
  const commonUSCities = [
    'new york', 'los angeles', 'chicago', 'houston', 'phoenix', 'philadelphia',
    'san antonio', 'san diego', 'dallas', 'san jose', 'austin', 'nashville',
    'denver', 'boston', 'seattle', 'atlanta', 'portland', 'miami', 'minneapolis',
    'las vegas', 'st louis', 'st. louis', 'san francisco', 'charlotte', 'pittsburgh',
    'new orleans', 'orlando', 'detroit', 'cleveland', 'washington dc', 'raleigh',
    'cincinnati', 'indianapolis', 'kansas city', 'columbus', 'memphis', 'tampa',
    'louisville', 'sacramento', 'baltimore', 'milwaukee', 'oklahoma city', 'tucson',
    'fresno', 'albuquerque'
  ];
  
  // Check if ends with a US state abbreviation
  const endsWithStateCode = /\b(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY)\b/i.test(city);
  
  // Check if the city name contains a common US city
  const containsUSCity = commonUSCities.some(usCity => 
    city.toLowerCase().includes(usCity)
  );
  
  return endsWithStateCode || containsUSCity;
};
